/**
 * ShowroomModal — Cyber Hangar & Arsenal Showroom Interface.
 * 
 * Features (Phase 5 Redesign — Consolidated Holo-Armory & Live-Sprite Testbed):
 * 1. Consolidated Hangar Stage: A single unified 60 FPS Canvas testing bay
 *    combining 3D holographic pedestal, live procedural unit sprite, ground aura,
 *    muzzle flash, damped spring recoil, and interactive hover target drone.
 * 2. Hybrid Showcase (Option 1A): Interactive live pixel-sprite in front with
 *    subtle holographic card projection floating in depth behind it + view focus toggle.
 * 3. Bottom Carousel (Option 2B): Full-width hero stage with high-end horizontal
 *    unit selection carousel at the bottom, race tabs (ALL, HUMAN, SUL), and role filters.
 * 4. Instant Feedback & Action Bar (Option 3A): Swatch click immediately equips AND
 *    fires the weapon with authentic audio/projectile physics; Action bar for Idle/Walk/Fire;
 *    click on sprite directly triggers attack.
 * 5. Player Cosmetics: Seamless Commander-Suite for Card Frames, HP Bars, and Avatar Borders.
 */

import { CARDS_DATA, getGoldCardImage, getProfileArtworkCards } from '../data/cards.js';
import { getCardThumbUrl } from '../data/cardThumbUrl.js';
import { REEL_3_STYLES } from '../engine/slotMachine.js';
import { inventory, isBulletColorExcluded, isRocketTrailUnit } from '../engine/inventory.js';
import { DECKBUILDER_THEMES, getDeckbuilderThemeById } from '../data/deckbuilderThemes.js';
import { CARD_THEMES, getCardThemeById } from '../data/cardThemes.js';
import { DECK_ICON_PRESETS } from '../data/customDecks.js';
import { getDeckIconMarkup } from './factionCrestMarkup.js';
import { TAUNT_DEFINITIONS, MAX_EQUIPPED_TAUNTS } from '../data/taunts.js';
import { hubNavigator } from './hubNavigator.js';
import { buildSpriteBitmap, buildAuraBitmap, dampedSpringRecoil } from '../engine/render/unitSprites.js';
import { isPinkBeretV2Enabled } from '../engine/render/hdSprites.js';
import {
  VULTURE_POD_SPINE_OFFSETS,
  VULTURE_RENDER_POD_ORDER,
  PROCESSING_ARM_ANCHOR_OFFSETS,
  PROCESSING_FEELER_ROOTS,
  drawProcessingPillarRig,
  getSulProcessingPillarOcularGlowCanvas,
  getSulProcessingPillarArmTipGlowCanvas,
  getSentinalLockHeadGlowCanvas,
  drawHyperDevilPatrolAmmoBelt,
  drawMonitorLockCrownSpines,
  HOTHAND_SPRINT_FRAMES,
  HOTHAND_GOLD_SPRINT_FRAMES,
  PINKBERET_WALK_FRAMES,
  PINKBERET_GOLD_WALK_FRAMES,
  PINKBERET_CLASSIC_WALK_FRAMES,
  PINKBERET_GOLD_CLASSIC_WALK_FRAMES,
  PINKBERET_V2_WALK_FRAMES,
  PINKBERET_GOLD_V2_WALK_FRAMES,
  STICKMAN_WALK_FRAMES,
  PULP_WALK_FRAMES,
  PRESERVER_LOCK_WALK_FRAMES,
  PRESERVER_LOCK_BODY_FRAMES,
  BASTION_LOCK_WALK_FRAMES,
  ANTI_SHADE_WALK_FRAMES,
  drawAntiShadeTatterPoly,
  drawHarvestingPillarRig,
  getPreserverLockCoronaCanvas,
  getSulClaimingPillarFloorGlowCanvas
} from '../engine/render/units.js';
// Aliased: drawStageFrame() below uses a local `progress` (0..1 projectile
// flight fraction) throughout, which would otherwise shadow this import.
import { progress as gameProgress } from '../engine/progress.js';
import { getAvatarCatalog } from '../engine/avatarCatalog.js';
import { resolveAvatarImageUrl } from './avatarImage.js';
import { ACHIEVEMENTS, ACHIEVEMENT_THEMES, getNextGoals } from '../data/achievements.js';
import { buildAchievementFacts } from '../engine/achievementFacts.js';
import { buildAchievementIconBitmap } from '../engine/render/achievementIcons.js';
import { gradeFromComposite } from '../engine/gamerank.js';
import { getAuth, getDisplayName, getMMR } from '../net/playerIdentity.js';
import { getRankTier } from './leaderboardModal.js';
import { ensureSlotIconDefs, getSlotCategorySvg } from './slotIcons.js';
import { getIconSvg } from './icons.js';

function escapeHtml(str) {
  return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Master tab category order - drives both the old bottom selector's data-
// misc-cat values and selectRelativeMiscCategory()'s arrow/swipe stepping,
// so "next"/"previous" always cycles Frames -> Team -> Avatar Border ->
// Card Tray Glow -> Commander Taunts -> (wraps to Frames).
const MISC_CATEGORIES = Object.freeze(['frames', 'hp_bar_color', 'profile', 'avatar', 'avatar_border', 'tray_color', 'deck_color', 'deck_icon', 'taunts', 'deckbuilder', 'cards']);
const PROGRESS_VIEWS = Object.freeze(['overview', 'achievements', 'stats', 'levelpath', 'ladder']);

// Sul Procedural Tentacle Physics Constants & Zero-Allocation Scratch Buffers (Arsenal Showroom)
const REJECTED_LOAF_STRAND_FREQ = Object.freeze([0.82, 1.12, 0.94, 1.22, 0.86]);
const REJECTED_LOAF_STRAND_PHASE = Object.freeze([0.0, 2.15, 4.30, 1.45, 3.75]);
const REJECTED_LOAF_STRAND_SPATIAL = Object.freeze([4.2, 5.4, 4.6, 5.8, 4.4]);
const REJECTED_LOAF_STRAND_LEN = Object.freeze([0.48, 0.62, 0.74, 0.58, 0.50]);
const REJECTED_LOAF_STRAND_AMP = Object.freeze([1.10, 0.88, 1.05, 0.90, 1.15]);
const REJECTED_LOAF_SEG_COLORS = Object.freeze(['#581c87', '#6b21a8', '#7e22ce', '#9333ea', '#a855f7', '#c084fc']);
const REJECTED_LOAF_SEG_WIDTHS = Object.freeze([2.6, 2.2, 1.9, 1.6, 1.3, 1.0]);

const DEFECTIVE_LOAF_ROOT_FREQ = Object.freeze([0.78, 1.05, 0.88, 1.18, 0.92]);
const DEFECTIVE_LOAF_ROOT_PHASE = Object.freeze([0.0, 1.95, 3.80, 5.20, 2.60]);
const DEFECTIVE_LOAF_ROOT_SPATIAL = Object.freeze([3.8, 4.9, 4.2, 5.2, 3.9]);
const DEFECTIVE_LOAF_ROOT_LEN = Object.freeze([0.40, 0.52, 0.60, 0.48, 0.36]);
const DEFECTIVE_LOAF_ROOT_AMP = Object.freeze([1.05, 0.92, 1.15, 0.85, 1.00]);
const DEFECTIVE_LOAF_ROOT_COLORS = Object.freeze(['#991b1b', '#b45309', '#365314', '#7f1d1d', '#4d7c0f']);

const DELVING_TENTACLE_FREQ = Object.freeze([0.85, 1.18, 0.92, 1.28, 0.88, 1.12, 1.22, 0.96, 1.15]);
const DELVING_TENTACLE_PHASE = Object.freeze([0.0, 1.85, 3.70, 0.95, 4.80, 2.30, 5.40, 1.40, 3.10]);
const DELVING_TENTACLE_SPATIAL = Object.freeze([4.8, 5.6, 5.0, 6.2, 5.4, 5.8, 6.5, 5.2, 6.0]);
const DELVING_TENTACLE_LEN = Object.freeze([0.72, 0.92, 0.78, 1.05, 1.25, 1.00, 0.85, 1.08, 0.80]);
const DELVING_TENTACLE_AMP = Object.freeze([1.15, 0.92, 1.08, 1.30, 1.00, 1.20, 1.35, 1.10, 1.25]);
const DELVING_TENTACLE_CURL_FREQ = Object.freeze([1.9, 2.5, 1.7, 2.9, 2.1, 2.6, 3.2, 2.3, 2.8]);
const DELVING_TENTACLE_CURL_AMP = Object.freeze([3.2, 4.0, 3.5, 5.2, 4.5, 4.8, 6.0, 4.2, 5.5]);
const DELVING_TENTACLE_ANCHOR_X = Object.freeze([-0.28, -0.22, -0.16, -0.24, -0.18, -0.10, -0.20, -0.14, -0.06]);
const DELVING_TENTACLE_ANCHOR_Y = Object.freeze([0.10, 0.14, 0.12, 0.16, 0.20, 0.18, 0.22, 0.25, 0.23]);
const DELVING_TENTACLE_BG_COLORS = Object.freeze([
  '#2a0404', '#3f0808', '#450a0a', '#581c87', '#6b1212', '#7f1d1d', '#991b1b', '#7f1d1d', '#450a0a'
]);
const DELVING_TENTACLE_FG_COLORS = Object.freeze([
  '#450a0a', '#6b1212', '#7f1d1d', '#991b1b', '#b91c1c', '#dc2626', '#ef4444', '#dc2626', '#b91c1c'
]);
const DELVING_TENTACLE_SEG_WIDTHS = Object.freeze([3.6, 3.2, 2.8, 2.4, 2.0, 1.7, 1.4, 1.1, 0.9]);

const HARVESTING_FINGER_FREQ = Object.freeze([0.62, 0.91, 0.74, 1.08, 0.82, 0.68]);
const HARVESTING_FINGER_PHASE = Object.freeze([0.0, 2.30, 4.60, 1.15, 3.45, 5.75]);
const HARVESTING_FINGER_SPATIAL = Object.freeze([3.2, 4.1, 3.6, 4.8, 3.9, 4.3]);
const HARVESTING_FINGER_LEN = Object.freeze([0.95, 1.20, 1.40, 1.15, 1.30, 1.05]);
const HARVESTING_FINGER_AMP = Object.freeze([1.10, 0.95, 1.25, 1.05, 1.20, 1.00]);
const HARVESTING_FINGER_WIDTHS = Object.freeze([2.4, 2.0, 1.7, 1.4, 1.1, 0.85, 0.65]);
const HARVESTING_FINGER_CURL_FREQ = Object.freeze([2.8, 3.4, 2.5, 3.8, 3.0, 3.2]);
const HARVESTING_FINGER_CURL_AMP = Object.freeze([4.5, 5.5, 3.8, 6.0, 5.0, 4.8]);
const HARVESTING_FINGER_ANCHOR_X = Object.freeze([0.00, 0.08, 0.32, 0.38, 0.20, 0.26]);
const HARVESTING_FINGER_ANCHOR_Y = Object.freeze([-0.42, -0.40, -0.42, -0.38, -0.02, 0.04]);
const HARVESTING_FINGER_COLORS_BG = Object.freeze([
  '#222240', '#263951', '#271e18', '#21172a', '#1b1918', '#17152c'
]);
const HARVESTING_FINGER_COLORS_FG = Object.freeze([
  '#4c4088', '#3a62ba', '#604731', '#4c4088', '#d5d3d2', '#574182'
]);

const _srRejectedStrandsScratch = Array.from({ length: 5 }, () => Array.from({ length: 7 }, () => ({ x: 0, y: 0 })));
const _srDelvingTentaclesScratch = Array.from({ length: 9 }, () => Array.from({ length: 10 }, () => ({ x: 0, y: 0 })));
const _srHarvestingFingersScratch = Array.from({ length: 6 }, () => Array.from({ length: 10 }, () => ({ x: 0, y: 0 })));
const _srProcessingFeelersScratch = Array.from({ length: 4 }, () => Array.from({ length: 7 }, () => ({ x: 0, y: 0 })));

// Progress > Log: entries shown collapsed vs. after "Show all", and the
// history size the log preview card scales against (progress.js keeps 150).
const PROGRESS_LOG_COLLAPSED_COUNT = 6;
const PROGRESS_LOG_EXPANDED_COUNT = 150;
const XP_EVENT_LABELS = Object.freeze({ directive: 'Daily Quest', daily_jackpot: 'Daily Jackpot' });

/**
 * Computes adaptive hologram lock styles for color swatches.
 * Adapts to the underlying swatch color:
 * - High luminance / bright backgrounds: darker rich shade of the same hue family
 * - Low / medium luminance backgrounds: lighter glowing tint of the same hue family
 * - Non-color / undefined hex: null (falls back to default signature VOID cyan #00f0ff)
 */
export function getAdaptiveLockStyle(hex) {
  if (!hex || typeof hex !== 'string') {
    return null;
  }
  const cleaned = hex.replace('#', '').trim();
  let r, g, b;
  if (cleaned.length === 3) {
    r = parseInt(cleaned[0] + cleaned[0], 16);
    g = parseInt(cleaned[1] + cleaned[1], 16);
    b = parseInt(cleaned[2] + cleaned[2], 16);
  } else if (cleaned.length === 6) {
    r = parseInt(cleaned.slice(0, 2), 16);
    g = parseInt(cleaned.slice(2, 4), 16);
    b = parseInt(cleaned.slice(4, 6), 16);
  } else {
    return null;
  }
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return null;
  }

  // Relative luminance (sRGB linear)
  const toLinear = c => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const lum = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

  // RGB to HSL
  const rf = r / 255, gf = g / 255, bf = b / 255;
  const max = Math.max(rf, gf, bf), min = Math.min(rf, gf, bf);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rf: h = ((gf - bf) / d + (gf < bf ? 6 : 0)) / 6; break;
      case gf: h = ((bf - rf) / d + 2) / 6; break;
      case bf: h = ((rf - gf) / d + 4) / 6; break;
    }
    h = h * 360;
  }

  // HSL to RGB helper
  const hslToHex = (hDeg, sVal, lVal) => {
    const hNorm = (hDeg % 360 + 360) % 360 / 360;
    const q = lVal < 0.5 ? lVal * (1 + sVal) : lVal + sVal - lVal * sVal;
    const p = 2 * lVal - q;
    const hue2rgb = (pVal, qVal, tVal) => {
      let t = tVal;
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return pVal + (qVal - pVal) * 6 * t;
      if (t < 1/2) return qVal;
      if (t < 2/3) return pVal + (qVal - pVal) * (2/3 - t) * 6;
      return pVal;
    };
    const ro = sVal === 0 ? lVal : hue2rgb(p, q, hNorm + 1/3);
    const go = sVal === 0 ? lVal : hue2rgb(p, q, hNorm);
    const bo = sVal === 0 ? lVal : hue2rgb(p, q, hNorm - 1/3);
    const rByte = Math.max(0, Math.min(255, Math.round(ro * 255)));
    const gByte = Math.max(0, Math.min(255, Math.round(go * 255)));
    const bByte = Math.max(0, Math.min(255, Math.round(bo * 255)));
    return {
      hex: '#' + [rByte, gByte, bByte].map(x => x.toString(16).padStart(2, '0')).join(''),
      r: rByte,
      g: gByte,
      b: bByte
    };
  };

  const isBright = lum > 0.55;

  if (isBright) {
    // Darker shade of the same hue
    const targetL = s > 0.25 ? 0.22 : 0.20;
    const targetS = s > 0.25 ? Math.min(1.0, s * 1.15) : 0.15;
    const res = hslToHex(h, targetS, targetL);
    return {
      color: res.hex,
      fill: `rgba(${res.r}, ${res.g}, ${res.b}, 0.22)`,
      filter: `drop-shadow(0 0 4px rgba(${res.r}, ${res.g}, ${res.b}, 0.5)) drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7))`,
      hoverFilter: `drop-shadow(0 0 6px rgba(${res.r}, ${res.g}, ${res.b}, 0.8)) drop-shadow(0 0 12px rgba(${res.r}, ${res.g}, ${res.b}, 0.45)) drop-shadow(0 1px 3px rgba(0, 0, 0, 0.85))`,
      isBright: true
    };
  } else {
    // Lighter, luminous tint of the same hue
    const targetL = s > 0.25 ? 0.84 : 0.90;
    const targetS = s > 0.25 ? Math.min(1.0, s * 0.92 + 0.08) : 0.15;
    const res = hslToHex(h, targetS, targetL);
    const glowHex = '#' + cleaned;
    return {
      color: res.hex,
      fill: `rgba(${res.r}, ${res.g}, ${res.b}, 0.20)`,
      filter: `drop-shadow(0 0 6px ${glowHex}cc) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.85))`,
      hoverFilter: `drop-shadow(0 0 8px ${glowHex}ff) drop-shadow(0 0 16px ${glowHex}88) drop-shadow(0 1px 3px rgba(0, 0, 0, 0.9))`,
      isBright: false
    };
  }
}

/**
 * Renders the HTML markup for a swatch lock badge.
 * If hex is supplied (color swatches), applies adaptive styling.
 * If hex is omitted/null (images, thumbnails, glyphs), retains the default cyan hologram.
 */
export function renderSwatchLockBadge(hex = null, size = 12, extraStyle = '') {
  const adaptive = hex ? getAdaptiveLockStyle(hex) : null;
  const styleVars = adaptive
    ? `--lock-color: ${adaptive.color}; --lock-fill: ${adaptive.fill}; --lock-filter: ${adaptive.filter}; --lock-hover-filter: ${adaptive.hoverFilter};`
    : '';
  const mergedStyle = [styleVars, extraStyle].filter(Boolean).join(' ');
  const styleAttr = mergedStyle ? ` style="${mergedStyle}"` : '';
  return `<span class="swatch-lock-badge"${styleAttr}>${getIconSvg('lock', size)}</span>`;
}

export class ShowroomModal {
  constructor(audioEngine = null, slotMachineModal = null, game = null) {
    this.audio = audioEngine;
    this.slotMachineModal = slotMachineModal;
    this.game = game;

    this.modalEl = document.getElementById('showroom-modal');
    this.stageContainer = document.getElementById('showroom-modal-stage');
    this.btnClose = document.getElementById('btn-close-showroom-modal');
    this.btnBackdrop = document.getElementById('showroom-modal-backdrop');

    this._activeUnitIndex = 0;
    this._units = [];
    this._allUnits = [];
    this._raceFilter = 'ALL'; // 'ALL' | 'HUMAN' | 'SUL'
    this._categoryFilter = 'ALL'; // 'ALL' | 'infantry' | 'vehicle' | 'melt'
    this._activeTab = 'units'; // 'units' | 'misc' | 'progress'
    this._activeUnitCosmetic = 'bullet_color'; // 'bullet_color' | 'unit_aura' | 'rocket_trail'
    this._activeMiscCategory = 'frames'; // 'frames' | 'profile' | 'hp_bar_color' | 'avatar' | 'avatar_border' | 'tray_color' | 'deck_color' | 'taunts'
    this._activeProgressView = 'overview'; // 'overview' | 'achievements' | 'stats' | 'levelpath'
    this._progressLogExpanded = false; // Log tab: 'Show all' toggle
    this._activeAchievementTheme = 'all'; // 'all' | theme id from ACHIEVEMENT_THEMES

    // Interactive Animation State
    this._animFrameId = null;
    this._recoilStartTime = 0;
    this._recoilDuration = 220; // ms
    this._projectiles = [];
    this._droneHitTime = 0;
    this._droneImpactColor = null;

    // Manual aiming: pointer/tap position on the hangar canvas (canvas-internal
    // coordinate space, i.e. the 840x380 drawing buffer, not CSS pixels).
    // Null until the player first moves/taps over the canvas, at which point
    // drawStageFrame() starts easing the target drone toward it each frame
    // instead of using the old fixed base position.
    this._aimTargetX = null;
    this._aimTargetY = null;
    this._droneRenderX = null;
    this._droneRenderY = null;

    // Stable swatch-rail ordering cache (keyed by unit+category) — computed once
    // per key so equipping a swatch (which triggers a re-render) doesn't
    // reshuffle the rail; see getStableSortedStyles().
    this._stableStyleOrderCache = new Map();

    this.init();
  }

  getFilteredUnits() {
    return this._allUnits.filter(u => inventory.hasAnyCustomizationForUnit(u.name));
  }

  unitCanAttack(unit) {
    if (!unit) return false;
    const name = unit.name || '';
    const type = unit.unitType || name;
    if (type === 'WheelieBomber' || name === 'Wheelie Bomber' || unit.isKamikaze) return false;
    if (type === 'RejectedLoaf' || name === 'Rejected Loaf' || unit.canAttack === false) return false;
    if (type === 'TheSalvageCompany' || name === 'The Salvage Company') return false;
    if (type === 'Hacker' || name === 'Hacker') return false;
    if (unit.damage === 0 && !unit.hasTether && !unit.hasBeamTether) return false;
    return true;
  }

  unitCanShoot(unit) {
    if (!this.unitCanAttack(unit)) return false;
    const name = unit.name || '';
    const type = unit.unitType || name;
    if (type === 'HotHand' || name === 'Hot Hand') return false;
    if (type === 'MonitorLock' || name === 'Monitor Lock' || type === 'Monitor_Lock') return false;
    if (type === 'AntiShade' || name === 'Anti Shade' || type === 'Anti_Shade') return false;
    if (type === 'HarvestingPillar' || name === 'Harvesting Pillar' || type === 'Harvesting_Pillar') return false;
    if (!unit.projectileType && !unit.hasTether && !unit.hasBeamTether) return false;
    return true;
  }

  init() {
    this._allUnits = CARDS_DATA
      .filter(c => (c.enabled === true || c.type === 'Melt')
        && !c.isSpell && c.type !== 'Spell'
        && !c.isModule && c.type !== 'Module')
      .map(c => {
        let category = 'infantry';
        if (c.type === 'Melt' || c.isMelt) {
          category = 'melt';
        } else if (c.isVehicle || c.unitType === 'Panzer' || c.unitType === 'TeslaWalker'
          || c.unitType === 'HyperTeslaWalker' || c.unitType === 'Rocketship'
          || c.unitType === 'A9Piranha' || c.unitType === 'A9Grizzly' || c.unitType === 'WheelieBomber'
          || c.unitType === 'TheSalvageCompany' || c.unitType === 'Orchestra') {
          category = 'vehicle';
        }

        return {
          name: c.name,
          unitType: c.unitType,
          race: c.race || 'Human',
          caste: c.caste || null,
          category,
          images: c.images,
          rarity: c.rarity,
          cost: c.cost,
          description: c.description,
          canAttack: c.canAttack,
          damage: c.damage,
          projectileType: c.projectileType,
          isKamikaze: c.isKamikaze,
          hasTether: c.hasTether,
          dualWeapon: c.dualWeapon,
          dualMissile: c.dualMissile,
          cardData: c
        };
      });

    this._units = this.getFilteredUnits();

    if (this.btnClose) {
      this.btnClose.addEventListener('click', () => this.close());
    }
    if (this.btnBackdrop) {
      this.btnBackdrop.addEventListener('click', () => this.close());
    }

    inventory.addListener(() => {
      if (this.isOpen()) {
        this.renderStage();
      }
    });

    gameProgress.addListener(() => {
      // Progress changes (like target drone hits from weapon test-fire) do not
      // affect the Units showcase tab. Re-rendering during Units causes the DOM
      // (including the top artwork carousel) to be rebuilt and flash on WebKit/iOS.
      if (this.isOpen() && this._activeTab !== 'units') {
        this.renderStage();
      }
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', (e) => {
        if (!this.isOpen()) return;

        if (e.key === 'Escape') {
          if (this.game?.directivesModal?.isOpen?.()) return;
          e.stopPropagation();
          hubNavigator.navigateTo('archive');
        } else if (e.key === 'ArrowLeft' && this._activeTab === 'units') {
          e.preventDefault();
          this.selectRelativeUnit(-1);
        } else if (e.key === 'ArrowRight' && this._activeTab === 'units') {
          e.preventDefault();
          this.selectRelativeUnit(1);
        } else if (e.key === ' ' && this._activeTab === 'units') {
          e.preventDefault();
          this.triggerCurrentTestFire();
        } else if (e.key === 'ArrowLeft' && this._activeTab === 'misc') {
          e.preventDefault();
          this.selectRelativeMiscCategory(-1);
        } else if (e.key === 'ArrowRight' && this._activeTab === 'misc') {
          e.preventDefault();
          this.selectRelativeMiscCategory(1);
        } else if (e.key === 'ArrowLeft' && this._activeTab === 'progress') {
          e.preventDefault();
          this.selectRelativeProgressView(-1);
        } else if (e.key === 'ArrowRight' && this._activeTab === 'progress') {
          e.preventDefault();
          this.selectRelativeProgressView(1);
        }
      });
    }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      const onShowroomResize = () => {
        this._stageRectDirty = true;
        try {
          window.scrollTo(0, 0);
          if (document.documentElement) document.documentElement.scrollTop = 0;
          if (document.body) document.body.scrollTop = 0;
        } catch (e) {}
        if (this.isOpen() && this.stageContainer) {
          this.stageContainer.scrollTop = 0;
        }
      };
      window.addEventListener('resize', onShowroomResize);
      window.addEventListener('orientationchange', () => {
        onShowroomResize();
        setTimeout(onShowroomResize, 50);
        setTimeout(onShowroomResize, 150);
        setTimeout(onShowroomResize, 350);
      });
    }
  }

  selectRelativeUnit(delta) {
    if (this._units.length === 0) return;
    const nextIdx = (this._activeUnitIndex + delta + this._units.length) % this._units.length;
    this._activeUnitIndex = nextIdx;
    if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
    this.renderStage();
    this.scrollActiveUnitIntoView();
  }

  // Master tab equivalent of selectRelativeUnit() - steps through
  // MISC_CATEGORIES, wrapping at both ends, driven by the preview's arrow
  // buttons/swipe gesture (bindStageEvents()) and ArrowLeft/ArrowRight.
  selectRelativeMiscCategory(delta) {
    const idx = MISC_CATEGORIES.indexOf(this._activeMiscCategory);
    const nextIdx = (idx + delta + MISC_CATEGORIES.length) % MISC_CATEGORIES.length;
    this._activeMiscCategory = MISC_CATEGORIES[nextIdx];
    if (this.audio) this.audio.playBark?.('ui_click');
    this.renderStage();
    this.scrollActiveMiscCategoryIntoView();
  }

  // Progress tab equivalent of selectRelativeUnit() - steps through
  // PROGRESS_VIEWS (Log -> Achievements -> Career -> Level Track)
  selectRelativeProgressView(delta) {
    const idx = PROGRESS_VIEWS.indexOf(this._activeProgressView);
    const nextIdx = (idx + delta + PROGRESS_VIEWS.length) % PROGRESS_VIEWS.length;
    this._activeProgressView = PROGRESS_VIEWS[nextIdx];
    if (this.audio) this.audio.playBark?.('ui_click');
    this.renderStage();
    this.scrollActiveProgressViewIntoView();
  }

  scrollActiveUnitIntoView() {
    requestAnimationFrame(() => {
      const activeCard = this.stageContainer?.querySelector(`.showroom-unit-card[data-index="${this._activeUnitIndex}"]`);
      if (activeCard) {
        if (this._activeUnitIndex === 0) {
          const wrap = document.getElementById('showroom-carousel-track');
          if (wrap) wrap.scrollLeft = 0;
        } else {
          activeCard.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }
    });
  }

  scrollActiveMiscCategoryIntoView() {
    requestAnimationFrame(() => {
      const activeCard = this.stageContainer?.querySelector(`.misc-category-card[data-misc-cat="${this._activeMiscCategory}"]`);
      if (activeCard) {
        if (this._activeMiscCategory === MISC_CATEGORIES[0]) {
          const wrap = document.getElementById('showroom-misc-swipe-area');
          if (wrap) wrap.scrollLeft = 0;
        } else {
          activeCard.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
        }
      }
    });
  }

  scrollActiveProgressViewIntoView() {
    requestAnimationFrame(() => {
      const activeCard = this.stageContainer?.querySelector(`.progress-category-card[data-progress-view="${this._activeProgressView}"]`);
      if (activeCard) {
        if (this._activeProgressView === PROGRESS_VIEWS[0]) {
          const wrap = document.getElementById('progress-slider-wrap');
          if (wrap) wrap.scrollLeft = 0;
        } else {
          activeCard.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
        }
      }
    });
  }

  open(options = {}) {
    if (!this.modalEl) return;
    if (options?.tab) {
      this._activeTab = options.tab;
    }
    if (options?.miscCategory) {
      this._activeTab = 'misc';
      this._activeMiscCategory = options.miscCategory;
    }
    if (options?.progressView) {
      this._activeTab = 'progress';
      this._activeProgressView = options.progressView;
      this._progressLogExpanded = false;
    }
    this.game?.pauseForMenu?.('showroom');
    this.modalEl.classList.remove('hidden');
    this.modalEl.classList.add('active');
    hubNavigator.setActiveDeck('arsenal');
    this._stableStyleOrderCache.clear();
    this.renderStage();
    this.startAnimationLoop();
    if (this._activeTab === 'misc') {
      this.scrollActiveMiscCategoryIntoView();
    } else if (this._activeTab === 'progress') {
      this.scrollActiveProgressViewIntoView();
    } else {
      this.scrollActiveUnitIntoView();
    }
  }

  close() {
    if (!this.modalEl) return;
    const wasOpen = this.isOpen();
    this.stopAnimationLoop();
    this._stageCanvas = null;
    this._stageCtx = null;
    this._stageRectDirty = true;
    if (this.stageContainer) {
      const imgs = this.stageContainer.querySelectorAll('img');
      imgs.forEach(img => {
        img.onload = null;
        img.onerror = null;
        img.src = '';
      });
      this.stageContainer.innerHTML = '';
    }
    this.modalEl.classList.remove('active');
    this.modalEl.classList.add('hidden');
    if (wasOpen) {
      this.game?.resumeForMenu?.('showroom');
    }
  }

  isOpen() {
    return this.modalEl && this.modalEl.classList.contains('active');
  }

  startAnimationLoop() {
    this.stopAnimationLoop();
    if (this._activeTab === 'progress') return;
    this._lastAnimTime = 0;
    const loop = (now) => {
      if (!this.isOpen() || this._activeTab === 'progress') {
        this.stopAnimationLoop();
        return;
      }
      if (typeof document !== 'undefined' && document.hidden) {
        this.stopAnimationLoop();
        const onVis = () => {
          if (!document.hidden) {
            document.removeEventListener('visibilitychange', onVis);
            if (this.isOpen() && this._activeTab !== 'progress') {
              this.startAnimationLoop();
            }
          }
        };
        document.addEventListener('visibilitychange', onVis);
        return;
      }
      const isMobile = (typeof window !== 'undefined') && window.matchMedia && (window.matchMedia('(max-width: 900px)').matches || window.matchMedia('(pointer: coarse)').matches);
      const minInterval = isMobile ? 32.0 : 13.6;
      if (now - this._lastAnimTime >= minInterval) {
        this._lastAnimTime = now;
        try {
          this.drawStageFrame(now);
        } catch (err) {
          console.error('Error in ShowroomModal animation frame:', err);
        }
      }
      this._animFrameId = requestAnimationFrame(loop);
    };
    this._animFrameId = requestAnimationFrame(loop);
  }

  stopAnimationLoop() {
    if (this._animFrameId) {
      cancelAnimationFrame(this._animFrameId);
      this._animFrameId = null;
    }
  }

  sortStylesUnlockedFirst(styles, isUnlockedFn, isEquippedFn = null) {
    return [...styles].sort((a, b) => {
      const aUn = !!isUnlockedFn(a.id);
      const bUn = !!isUnlockedFn(b.id);
      if (aUn && !bUn) return -1;
      if (!aUn && bUn) return 1;
      if (aUn && bUn && isEquippedFn) {
        const aEq = !!isEquippedFn(a.id);
        const bEq = !!isEquippedFn(b.id);
        if (aEq && !bEq) return -1;
        if (!aEq && bEq) return 1;
      }
      return 0;
    });
  }

  // Wraps sortStylesUnlockedFirst() with a per-key cache so the swatch rail's
  // order is computed once when a unit+category is first shown, then reused
  // on subsequent re-renders (e.g. triggered by equip-clicks) instead of being
  // recomputed every render — this stops the rail from reshuffling under the
  // user's cursor the moment they click a swatch to equip it. The cache is
  // cleared on open() so a fresh session re-evaluates unlocked-first grouping.
  getStableSortedStyles(cacheKey, styles, isUnlockedFn, isEquippedFn = null) {
    if (!this._stableStyleOrderCache.has(cacheKey)) {
      const sorted = this.sortStylesUnlockedFirst(styles, isUnlockedFn, isEquippedFn);
      this._stableStyleOrderCache.set(cacheKey, sorted.map(s => s.id));
    }
    const order = this._stableStyleOrderCache.get(cacheKey);
    const byId = new Map(styles.map(s => [s.id, s]));
    return order.map(id => byId.get(id)).filter(Boolean);
  }

  renderStage() {
    this._units = this.getFilteredUnits();
    if (!this.stageContainer) return;

    // Preserve scroll positions of horizontal tracks across re-renders within the same tab
    const savedScrolls = new Map();
    const tabUnchanged = this._previousActiveTab === this._activeTab;
    this._previousActiveTab = this._activeTab;

    if (tabUnchanged) {
      const scrollSelectors = [
        '#showroom-misc-swipe-area',
        '#progress-slider-wrap',
        '#showroom-carousel-track',
        '.showroom-misc-slider-wrap',
        '.progress-slider-wrap',
        '.showroom-carousel-track',
        '.color-rail',
        '.color-rail-container',
        '.workbench-dock-compact',
        '.progress-view-content',
        '.showroom-progress-layout',
        '.showroom-container'
      ];
      for (const sel of scrollSelectors) {
        const el = this.stageContainer.querySelector(sel);
        if (el && (el.scrollLeft > 0 || el.scrollTop > 0)) {
          savedScrolls.set(sel, { left: el.scrollLeft, top: el.scrollTop });
        }
      }
    }

    ensureSlotIconDefs();
    this._activeUnitIndex = Math.min(this._activeUnitIndex, Math.max(0, this._units.length - 1));
    const currentUnit = (this._units.length > 0 ? this._units[this._activeUnitIndex] : null) || this._allUnits[0] || {
      name: 'Marine',
      rarity: 'common',
      cost: 1,
      images: ['Marine_1.png'],
      unitType: 'Marine',
      race: 'Human'
    };
    const hasGold = inventory.hasSpecialCard(currentUnit.name, 'gold');
    const isGold = inventory.isSpecialCardActive ? inventory.isSpecialCardActive(currentUnit.name, 'gold') : hasGold;

    const hasRocketTrail = isRocketTrailUnit(currentUnit.name);
    const bulletExcluded = isBulletColorExcluded(currentUnit.name);
    if (bulletExcluded && this._activeUnitCosmetic === 'bullet_color') {
      this._activeUnitCosmetic = 'unit_aura';
    }

    // Bullet Color
    const equippedBulletStyleId = inventory.getEquippedBulletColor(currentUnit.name);
    const equippedBulletStyle = REEL_3_STYLES.find(s => s.id === equippedBulletStyleId) || null;
    const isLootingVultureUnit = (currentUnit.name === 'Looting Vulture' || currentUnit.unitType === 'LootingVulture');
    const isPreserverLockUnit = (currentUnit.name === 'Preserver Lock' || currentUnit.unitType === 'PreserverLock');
    const activeBulletColorHex = equippedBulletStyle?.hex || (
      isLootingVultureUnit ? '#fde047' : (
        isPreserverLockUnit ? '#7dd3fc' : (
          currentUnit.race === 'Sul' ? '#d946ef' : '#ffdd44'
        )
      )
    );
    const unlockedBulletColors = inventory.getBulletColors().filter(b => b.unitName === currentUnit.name);

    // Unit Aura
    const equippedAuraStyleId = inventory.getEquippedUnitAura(currentUnit.name);
    const equippedAuraStyle = REEL_3_STYLES.find(s => s.id === equippedAuraStyleId) || null;
    const activeAuraColorHex = equippedAuraStyle?.hex || null;
    const unlockedAuras = inventory.getUnitAuras().filter(a => a.unitName === currentUnit.name);

    // Rocket Trail
    const equippedTrailStyleId = inventory.getEquippedRocketTrail(currentUnit.name);
    const equippedTrailStyle = REEL_3_STYLES.find(s => s.id === equippedTrailStyleId) || null;
    const activeTrailColorHex = equippedTrailStyle?.hex || '#f97316';
    const unlockedTrails = inventory.getRocketTrails().filter(r => r.unitName === currentUnit.name);

    // Misc / Player Categories
    const equippedFrameId = inventory.getEquippedFrame();
    const equippedFrameStyle = REEL_3_STYLES.find(s => s.id === equippedFrameId) || null;
    const unlockedFrames = inventory.getFrames();

    const equippedHpStyleId = inventory.getEquippedHpBarColor();
    const equippedHpStyle = REEL_3_STYLES.find(s => s.id === equippedHpStyleId) || null;
    const unlockedHpBars = inventory.getHpBarColors();

    const equippedAvatarStyleId = inventory.getEquippedAvatarBorder();
    const equippedAvatarStyle = REEL_3_STYLES.find(s => s.id === equippedAvatarStyleId) || null;
    const unlockedAvatarBorders = inventory.getAvatarBorders();
    const playerLevel = gameProgress.getLevelProgress().level;
    const avatarCatalog = getAvatarCatalog();
    const unlockedAvatars = avatarCatalog.filter(a => a.unlockLevel <= playerLevel);
    const equippedAvatar = gameProgress.getEquippedAvatar() || avatarCatalog[0];
    const equippedAvatarThumbUrl = equippedAvatar ? resolveAvatarImageUrl(equippedAvatar) : null;

    const allProfileCards = getProfileArtworkCards();
    const unlockedProfileNames = inventory.getProfileArtworks();
    const equippedProfileName = inventory.getEquippedProfileArtwork();
    const equippedProfileCard = allProfileCards.find(c => c.name === equippedProfileName) || allProfileCards[0];
    const equippedProfileUrl = equippedProfileCard?.images?.[0] ? getCardThumbUrl(equippedProfileCard.images[0], 'm') : getCardThumbUrl('1_Hypermarine.png', 'm');

    const equippedTrayStyleId = inventory.getEquippedTrayColor();
    const equippedTrayStyle = REEL_3_STYLES.find(s => s.id === equippedTrayStyleId) || null;
    const unlockedTrayColors = inventory.getTrayColors();

    const equippedDeckStyleId = inventory.getEquippedDeckColor();
    const equippedDeckStyle = equippedDeckStyleId ? REEL_3_STYLES.find(s => s.id === equippedDeckStyleId) : null;
    const unlockedDeckColors = inventory.getDeckColors();

    const equippedDeckIconId = inventory.getEquippedDeckIcon?.() || 'generic-a';
    const unlockedDeckIcons = inventory.getDeckIcons?.() || [];

    const equippedDeckbuilderThemeId = inventory.getEquippedDeckbuilderTheme?.() || 'glass';
    const equippedDeckbuilderTheme = getDeckbuilderThemeById(equippedDeckbuilderThemeId);
    const unlockedDeckbuilderThemes = DECKBUILDER_THEMES.filter(t => inventory.hasDeckbuilderTheme(t.id));

    const equippedCardThemeId = inventory.getEquippedCardTheme?.() || 'glass';
    const equippedCardTheme = getCardThemeById(equippedCardThemeId);
    const unlockedCardThemes = CARD_THEMES.filter(t => inventory.hasCardTheme(t.id));

    // Taunts: unlike the color-style categories above, "unlocked" and
    // "equipped" aren't the same axis here - a taunt can be owned but not
    // in the active 3-slot loadout. allTauntDefs/unlockedTauntIds cover the
    // unlock axis (for the collection counter below); equippedTauntIds is
    // the separate MAX_EQUIPPED_TAUNTS-capped multiselect (js/engine/
    // inventory.js setEquippedTaunts()).
    const allTauntDefs = Object.values(TAUNT_DEFINITIONS);
    const unlockedTauntIds = inventory.getTaunts();
    const equippedTauntIds = inventory.getEquippedTaunts();

    const testFireColorHex = this._activeUnitCosmetic === 'rocket_trail' && hasRocketTrail
      ? activeTrailColorHex
      : this._activeUnitCosmetic === 'unit_aura' && activeAuraColorHex
        ? activeAuraColorHex
        : activeBulletColorHex;

    const testFireLabel = this._activeUnitCosmetic === 'rocket_trail' && hasRocketTrail
      ? `${getIconSvg('rocket', 14)} TEST FIRE`
      : (currentUnit.name === 'Rejected Loaf' ? `${getIconSvg('structure', 14)} TEST REFLECT` : `${getIconSvg('energy', 14)} TEST FIRE`);

    this.stageContainer.innerHTML = `
      <div class="showroom-container" data-active-tab="${this._activeTab}">
        <!-- Top-Level Menu Tabs: Units vs. Boss Cosmetics -->
        <div class="showroom-nav-tabs">
          <button class="showroom-tab-btn ${this._activeTab === 'units' ? 'active' : ''}" data-tab="units">
            <span class="tab-label">Units</span>
          </button>
          <button class="showroom-tab-btn ${this._activeTab === 'misc' ? 'active' : ''}" data-tab="misc">
            <span class="tab-label">Player</span>
          </button>
          <button class="showroom-tab-btn ${this._activeTab === 'progress' ? 'active' : ''}" data-tab="progress">
            <span class="tab-label">Progress</span>
          </button>
        </div>

        ${this._activeTab === 'units' ? (
          this._units.length === 0 ? `
            <div class="showroom-empty-units-notice">
              <div class="empty-notice-icon">${getIconSvg('dice', 28)}</div>
              <div class="empty-notice-title">NO CUSTOM UNITS AVAILABLE</div>
              <div class="empty-notice-desc">Units appear here once at least one custom bullet color, aura, or rocket trail is unlocked. Spin in Supply Surge to unlock cosmetic upgrades!</div>
              <button class="empty-notice-btn" id="btn-empty-goto-slots">GO TO SUPPLY SURGE</button>
            </div>
          ` : `
          <!-- HERO SELECTION CAROUSEL (WITHOUT FILTER BAR) -->
          <div class="showroom-carousel-section">
            <div class="carousel-track-wrapper">
              <div class="showroom-carousel-track" id="showroom-carousel-track">
                ${this._units.map((u, idx) => {
                  const hasGold = inventory.hasSpecialCard(u.name, 'gold');
                  const isGoldActive = inventory.isSpecialCardActive ? inventory.isSpecialCardActive(u.name, 'gold') : hasGold;
                  const isSelected = (idx === this._activeUnitIndex);

                  const thumbImg = (isGoldActive && getGoldCardImage(u.name)) || (u.images?.[0] || 'Marine_1.png');
                  const nameLen = u.name?.length || 0;
                  const nameClass = nameLen > 21 ? 'name-tiny' : (nameLen > 18 ? 'name-long' : (nameLen > 14 ? 'name-compact' : ''));
                  return `
                    <button class="showroom-unit-card ${isSelected ? 'active' : ''}" data-index="${idx}" title="${u.name}">
                      <span class="unit-card-thumb-wrap ${isGoldActive ? 'thumb-gold-edition' : ''}">
                        <img src="${getCardThumbUrl(thumbImg)}" class="unit-card-thumb" alt="${u.name}" onerror="this.style.display='none'">
                        ${hasGold ? `<span class="unit-card-badge badge-gold ${isGoldActive ? 'active' : 'inactive'}" title="${isGoldActive ? 'Gold Edition aktiv (Klicken zum Umschalten)' : 'Gold Edition deaktiviert (Klicken zum Umschalten)'}">${getIconSvg('crown', 12)}</span>` : ''}
                      </span>
                      <span class="unit-card-name ${nameClass}">${u.name}</span>
                    </button>
                  `;
                }).join('')}
              </div>
            </div>
          </div>

          <!-- TAB 1: CONSOLIDATED HANGAR BAY & SPRITE TESTBED (SHOWCASE) -->
          <div class="showroom-hangar-stage" id="showroom-hangar-stage">
            <div class="hangar-canvas-wrap" id="hangar-canvas-wrap">
              <!-- Single 60 FPS Unified Testing Canvas -->
              <canvas id="hangar-stage-canvas" class="hangar-stage-canvas" width="840" height="380"></canvas>
              <canvas id="showroom-fire-canvas" class="showroom-fire-canvas" width="340" height="280" style="display:none;"></canvas>

              <!-- Unit Telemetry Badge Overlay -->
              <div class="hangar-unit-telemetry">
                <div class="telemetry-unit-header">
                  <span class="telemetry-unit-name">
                    ${currentUnit.name}
                    ${currentUnit.race === 'Sul' ? `<span class="caste-hieroglyph-tag" title="Sul Caste: ${currentUnit.caste || 'Loaf'}">𓏏 ${currentUnit.caste?.toUpperCase() || 'LOAF'}</span>` : ''}
                  </span>
                  ${hasGold ? `
                    <button type="button" id="btn-toggle-gold" class="unit-card-badge badge-gold-inline ${isGold ? 'active' : 'inactive'}" title="${isGold ? 'Gold Edition aktiv (Klicken zum Deaktivieren)' : 'Gold Edition deaktiviert (Klicken zum Aktivieren)'}" data-card-name="${currentUnit.name}">
                      ${getIconSvg('crown', 12)} GOLD${isGold ? '' : ' (OFF)'}
                    </button>
                  ` : ''}
                </div>
                <div class="telemetry-active-pills">
                  <span class="telemetry-pill" style="color: ${activeBulletColorHex}; border-color: ${activeBulletColorHex}55;">
                    <span class="telemetry-pill-cat-icon">${getSlotCategorySvg('bullet_color', 'mini')}</span>${bulletExcluded ? 'N/A' : (equippedBulletStyle ? equippedBulletStyle.name : 'Default')}
                  </span>
                  <span class="telemetry-pill" style="color: ${activeAuraColorHex || '#94a3b8'}; border-color: ${activeAuraColorHex ? `${activeAuraColorHex}55` : '#334155'};">
                    <span class="telemetry-pill-cat-icon">${getSlotCategorySvg('unit_aura', 'mini')}</span>${equippedAuraStyle ? equippedAuraStyle.name : 'No Aura'}
                  </span>
                  ${hasRocketTrail ? `
                  <span class="telemetry-pill" style="color: ${activeTrailColorHex}; border-color: ${activeTrailColorHex}55;">
                    <span class="telemetry-pill-cat-icon">${getSlotCategorySvg('rocket_trail', 'mini')}</span>${equippedTrailStyle ? equippedTrailStyle.name : 'Default Trail'}
                  </span>` : ''}
                </div>
              </div>
            </div>
          </div>

          <!-- COMPACT MOD-MATRIX WORKBENCH DOCK -->
          <div class="workbench-dock-compact">
            <div class="dock-header">
              <div class="workbench-mode-toggle">
                <button class="workbench-mode-btn ${this._activeUnitCosmetic === 'bullet_color' ? 'active' : ''}" data-unit-cosmetic="bullet_color" ${bulletExcluded ? 'disabled aria-disabled="true"' : ''}>
                  <span class="workbench-mode-icon">${getSlotCategorySvg('bullet_color', 'mini')}</span> BULLETS
                </button>
                <button class="workbench-mode-btn ${this._activeUnitCosmetic === 'unit_aura' ? 'active' : ''}" data-unit-cosmetic="unit_aura">
                  <span class="workbench-mode-icon">${getSlotCategorySvg('unit_aura', 'mini')}</span> AURA
                </button>
                <button class="workbench-mode-btn ${this._activeUnitCosmetic === 'rocket_trail' ? 'active' : ''}" data-unit-cosmetic="rocket_trail" ${!hasRocketTrail ? 'disabled aria-disabled="true"' : ''}>
                  <span class="workbench-mode-icon">${getSlotCategorySvg('rocket_trail', 'mini')}</span> TRAIL
                </button>
              </div>
            </div>

            <div class="color-rail-container">
              <div class="color-rail ${(this._activeUnitCosmetic === 'bullet_color' && bulletExcluded) || (this._activeUnitCosmetic === 'rocket_trail' && !hasRocketTrail) ? 'notice-mode' : ''}">
                ${this._activeUnitCosmetic === 'bullet_color' ? (
                  bulletExcluded ? `
                    <div class="workbench-not-available-notice">
                      <span class="not-available-icon">${getIconSvg('warning', 14)}</span>
                      <span class="not-available-text">Not available for this unit</span>
                    </div>
                  ` : `
                    <div class="color-swatch-card ${!equippedBulletStyleId ? 'equipped' : ''}" data-style-id="default">
                      <span class="swatch-bubble" style="background-color: ${isPreserverLockUnit ? '#7dd3fc' : '#ffdd44'}; box-shadow: 0 0 8px ${isPreserverLockUnit ? '#7dd3fc' : '#ffdd44'}"></span>
                      <span class="swatch-name">Default</span>
                    </div>

                    ${this.getStableSortedStyles(
                      `bullet_color:${currentUnit.name}`,
                      REEL_3_STYLES,
                      id => inventory.hasBulletColor(currentUnit.name, id),
                      id => equippedBulletStyleId === id
                    ).map(style => {
                      const isUnlocked = inventory.hasBulletColor(currentUnit.name, style.id);
                      const isEquipped = equippedBulletStyleId === style.id;
                      return `
                        <div class="color-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                             data-style-id="${style.id}"
                             data-unlocked="${isUnlocked}"
                             title="${style.name}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                          <span class="swatch-bubble" style="background-color: ${style.hex}; box-shadow: 0 0 12px ${style.hex}">
                            ${!isUnlocked ? renderSwatchLockBadge(style.hex, 12) : ''}
                          </span>
                          <span class="swatch-name">${style.name}</span>
                        </div>
                      `;
                    }).join('')}
                  `
                ) : this._activeUnitCosmetic === 'unit_aura' ? `
                  <div class="color-swatch-card ${!equippedAuraStyleId ? 'equipped' : ''}" data-style-id="default" title="No Aura">
                    <span class="swatch-bubble" style="background-color: #0f172a; box-shadow: 0 0 8px rgba(255,255,255,0.15)"></span>
                    <span class="swatch-name">No Aura</span>
                  </div>

                  ${this.getStableSortedStyles(
                    `unit_aura:${currentUnit.name}`,
                    REEL_3_STYLES,
                    id => inventory.hasUnitAura(currentUnit.name, id),
                    id => equippedAuraStyleId === id
                  ).map(style => {
                    const isUnlocked = inventory.hasUnitAura(currentUnit.name, style.id);
                    const isEquipped = equippedAuraStyleId === style.id;
                    return `
                      <div class="color-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                           data-style-id="${style.id}"
                           data-unlocked="${isUnlocked}"
                           title="${style.name}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <span class="swatch-bubble" style="background-color: ${style.hex}; box-shadow: 0 0 12px ${style.hex}">
                          ${!isUnlocked ? renderSwatchLockBadge(style.hex, 12) : ''}
                        </span>
                        <span class="swatch-name">${style.name}</span>
                      </div>
                    `;
                  }).join('')}
                ` : hasRocketTrail ? `
                  <div class="color-swatch-card ${!equippedTrailStyleId ? 'equipped' : ''}" data-style-id="default" title="Default">
                    <span class="swatch-bubble" style="background-color: #f97316; box-shadow: 0 0 8px rgba(249,115,22,0.5)"></span>
                    <span class="swatch-name">Default</span>
                  </div>

                  ${this.getStableSortedStyles(
                    `rocket_trail:${currentUnit.name}`,
                    REEL_3_STYLES,
                    id => inventory.hasRocketTrail(currentUnit.name, id),
                    id => equippedTrailStyleId === id
                  ).map(style => {
                    const isUnlocked = inventory.hasRocketTrail(currentUnit.name, style.id);
                    const isEquipped = equippedTrailStyleId === style.id;
                    return `
                      <div class="color-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                           data-style-id="${style.id}"
                           data-unlocked="${isUnlocked}"
                           title="${style.name}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <span class="swatch-bubble" style="background-color: ${style.hex}; box-shadow: 0 0 12px ${style.hex}">
                          ${!isUnlocked ? renderSwatchLockBadge(style.hex, 12) : ''}
                        </span>
                        <span class="swatch-name">${style.name}</span>
                      </div>
                    `;
                  }).join('')}
                ` : `
                  <div class="workbench-not-available-notice">
                    <span class="not-available-icon">${getIconSvg('warning', 14)}</span>
                    <span class="not-available-text">Not available for this unit</span>
                  </div>
                `}
              </div>
            </div>
          </div>
        `) : this._activeTab === 'misc' ? (() => {
          const miscCategoriesData = [
            {
              id: 'frames',
              title: 'Card Frame',
              unlockedCount: unlockedFrames.length,
              totalCount: REEL_3_STYLES.length,
              previewHtml: `
                <div class="card-slot filled rarity-${(currentUnit.rarity || 'common').toLowerCase()} ${equippedFrameId ? `frame-${equippedFrameId}` : ''}">
                  <div class="card-inner ${isGold ? 'card-gold-edition' : ''}">
                    <img src="${getCardThumbUrl((isGold && getGoldCardImage(currentUnit.name)) || currentUnit.images?.[0] || 'Marine_1.png', 'm')}" class="card-bg-img" alt="Frame Preview" draggable="false">
                    <div class="card-content-overlay">
                      <div class="card-header">
                        <span class="card-name">${currentUnit.name}</span>
                        <span class="card-cost">${currentUnit.cost}</span>
                      </div>
                    </div>
                  </div>
                </div>
              `
            },
            {
              id: 'hp_bar_color',
              title: 'HP Bar',
              unlockedCount: unlockedHpBars.length,
              totalCount: REEL_3_STYLES.length,
              previewHtml: `
                <div class="boss-hp-track" style="height: 24px; border-radius: 4px; width: 140px; max-width: 90%; box-shadow: 0 0 12px rgba(0,0,0,0.6);">
                  <div class="boss-hp-fill custom-player-hp" style="width: 78%; ${equippedHpStyle ? `background: linear-gradient(90deg, ${equippedHpStyle.hex}, #ffffff 180%) !important; box-shadow: 0 0 14px ${equippedHpStyle.hex} !important;` : ''}"></div>
                  <span class="boss-hp-label" style="font-size: 10px; font-weight: 800;">1170 / 1500 HP</span>
                </div>
              `
            },
            {
              id: 'profile',
              title: 'PROFILE',
              unlockedCount: unlockedProfileNames.length,
              totalCount: allProfileCards.length,
              previewHtml: `
                <div class="boss-avatar custom-player-avatar" style="width: 96px; height: 128px; aspect-ratio: 3 / 4; position: relative; border-radius: 10px !important; overflow: hidden; ${equippedAvatarStyle ? `border-color: ${equippedAvatarStyle.hex} !important; box-shadow: 0 0 16px ${equippedAvatarStyle.hex} !important;` : 'border: 2px solid rgba(0, 240, 255, 0.4); box-shadow: 0 0 14px rgba(0, 240, 255, 0.25);'}">
                  <img src="${equippedProfileUrl}" alt="Profile Artwork" style="width: 100%; height: 100%; object-fit: cover; object-position: center top; display: block;">
                  <div class="start-avatar-scanline" style="position: absolute; inset: 0; background: linear-gradient(rgba(0, 240, 255, 0) 50%, rgba(0, 0, 0, 0.25) 50%); background-size: 100% 4px; pointer-events: none; opacity: 0.55;"></div>
                  <div class="start-level-badge" style="position: absolute; bottom: 6px; right: 6px; width: 24px; height: 24px; font-size: 10.5px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-family: 'Orbitron', sans-serif; font-weight: 900; color: var(--accent-green, #00ff88); background: radial-gradient(circle at 50% 32%, rgba(17, 31, 46, 0.94) 0%, rgba(4, 8, 14, 0.96) 100%); border: 1.5px solid var(--accent-green, #00ff88); box-shadow: 0 0 8px rgba(0, 255, 136, 0.5); pointer-events: none;">${playerLevel}</div>
                </div>
              `
            },
            {
              id: 'avatar',
              title: 'Game Avatar',
              unlockedCount: unlockedAvatars.length,
              totalCount: avatarCatalog.length,
              previewHtml: `
                <div class="boss-avatar custom-player-avatar" style="width: 68px; height: 68px; position: relative; ${equippedAvatarStyle ? `border-color: ${equippedAvatarStyle.hex} !important; box-shadow: 0 0 18px ${equippedAvatarStyle.hex} !important;` : ''}">
                  ${equippedAvatarThumbUrl
                    ? `<img src="${equippedAvatarThumbUrl}" alt="${equippedAvatar?.unitName || 'Avatar'}" style="width: 100%; height: 100%; object-fit: ${equippedAvatar?.tier === 'artwork' ? 'cover' : 'contain'}; image-rendering: pixelated;">`
                    : `<span class="avatar-card-default" style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">${getIconSvg('avatar_placeholder', 32)}</span>`
                  }
                  <div class="boss-avatar-level-badge" style="position: absolute; bottom: -4px; right: -4px; ${equippedAvatarStyle ? `border-color: ${equippedAvatarStyle.hex} !important; box-shadow: 0 0 8px ${equippedAvatarStyle.hex} !important; text-shadow: 0 0 4px ${equippedAvatarStyle.hex} !important;` : ''}">${playerLevel}</div>
                </div>
              `
            },
            {
              id: 'avatar_border',
              title: 'Contour',
              unlockedCount: unlockedAvatarBorders.length,
              totalCount: REEL_3_STYLES.length,
              previewHtml: `
                <div class="boss-avatar custom-player-avatar" style="width: 68px; height: 68px; position: relative; ${equippedAvatarStyle ? `border-color: ${equippedAvatarStyle.hex} !important; box-shadow: 0 0 18px ${equippedAvatarStyle.hex} !important;` : ''}">
                  <span class="avatar-card-default" style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">${getIconSvg('avatar_placeholder', 32)}</span>
                  <div class="boss-avatar-level-badge" style="position: absolute; bottom: -4px; right: -4px;">${playerLevel}</div>
                </div>
              `
            },
            {
              id: 'tray_color',
              title: 'Card Tray',
              unlockedCount: unlockedTrayColors.length,
              totalCount: REEL_3_STYLES.length,
              previewHtml: `
                <div id="showroom-tray-preview" class="cards-hand-tray-preview" style="width: 140px; max-width: 90%; height: 56px; border-radius: 8px; ${equippedTrayStyle ? `background: linear-gradient(180deg, ${equippedTrayStyle.hex}33 0%, ${equippedTrayStyle.hex}1a 100%); border: 1px solid ${equippedTrayStyle.hex}; box-shadow: inset 0 0 16px ${equippedTrayStyle.hex}55, 0 0 10px ${equippedTrayStyle.hex}55;` : 'background: linear-gradient(180deg, #09131f 0%, #0e1b2c 100%); border: 1px solid rgba(16, 44, 76, 0.95);'}"></div>
              `
            },
            {
              id: 'deck_color',
              title: 'Deck Color',
              unlockedCount: unlockedDeckColors.length,
              totalCount: REEL_3_STYLES.length,
              previewHtml: `
                <div class="showroom-deck-preview ${equippedDeckStyleId ? `has-custom-color deck-theme-${equippedDeckStyleId}` : ''}" style="${equippedDeckStyle ? `--deck-accent: ${equippedDeckStyle.hex}; --deck-accent-glow: ${equippedDeckStyle.hex}cc; --deck-accent-core: ${equippedDeckStyle.hex}b3;` : ''}">
                  <div class="deck-card-slab preview-slab">
                    <div class="deck-card-face preview-face">
                      <div class="deck-plate-pattern"></div>
                      <div class="deck-card-bezel"></div>
                      <div class="deck-card-crest preview-crest">◈</div>
                      <div class="deck-front-glass"></div>
                    </div>
                  </div>
                </div>
              `
            },
            {
              id: 'deck_icon',
              title: 'Deck Icon',
              unlockedCount: unlockedDeckIcons.length,
              totalCount: DECK_ICON_PRESETS.length,
              previewHtml: `
                <div class="showroom-deck-preview showroom-deck-icon-preview">
                  <div class="deck-card-slab preview-slab">
                    <div class="deck-card-face preview-face">
                      <div class="deck-plate-pattern"></div>
                      <div class="deck-card-bezel"></div>
                      <div class="deck-card-crest preview-crest has-crest">
                        ${getDeckIconMarkup(equippedDeckIconId, 'showroom-preview')}
                      </div>
                      <div class="deck-front-glass"></div>
                    </div>
                  </div>
                </div>
              `
            },
            {
              id: 'taunts',
              title: 'Taunts',
              unlockedCount: unlockedTauntIds.length,
              totalCount: allTauntDefs.length,
              previewHtml: `
                <div class="taunt-loadout-preview">
                  ${equippedTauntIds.map(id => {
                    const t = TAUNT_DEFINITIONS[id];
                    if (!t) return '';
                    return `
                      <button class="taunt-loadout-slot" data-preview-taunt-id="${t.id}" style="border-color: ${t.themeColor}; box-shadow: 0 0 10px ${t.glowColor};" title="Preview ${t.label}">
                        <span class="taunt-slot-icon">${t.icon}</span>
                        <span class="taunt-slot-label">${t.label}</span>
                      </button>
                    `;
                  }).join('')}
                  ${Array.from({ length: Math.max(0, MAX_EQUIPPED_TAUNTS - equippedTauntIds.length) }).map(() => `
                    <div class="taunt-loadout-slot taunt-loadout-slot-empty">
                      <span class="taunt-slot-icon">+</span>
                      <span class="taunt-slot-label">Empty</span>
                    </div>
                  `).join('')}
                </div>
              `
            },
            {
              id: 'deckbuilder',
              title: 'Deck Builder',
              unlockedCount: unlockedDeckbuilderThemes.length,
              totalCount: DECKBUILDER_THEMES.length,
              previewHtml: this._renderDeckbuilderCardPreview(equippedDeckbuilderThemeId)
            },
            {
              id: 'cards',
              title: 'Cards in Game',
              unlockedCount: unlockedCardThemes.length,
              totalCount: CARD_THEMES.length,
              previewHtml: this._renderCardsCardPreview(equippedCardThemeId)
            }
          ];

          let dockHtml = '';
          if (this._activeMiscCategory === 'frames') {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  <div class="color-swatch-card ${!equippedFrameId ? 'equipped' : ''}" data-misc-style-id="default">
                    <span class="swatch-bubble" style="background-color: #0e1116; box-shadow: 0 0 8px rgba(255,255,255,0.15)"></span>
                    <span class="swatch-name">Default</span>
                  </div>
                  ${this.getStableSortedStyles(
                    'frames',
                    REEL_3_STYLES,
                    id => inventory.hasFrame(id),
                    id => equippedFrameId === id
                  ).map(style => {
                    const isUnlocked = inventory.hasFrame(style.id);
                    const isEquipped = equippedFrameId === style.id;
                    return `
                      <div class="color-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                            data-misc-style-id="${style.id}"
                            data-unlocked="${isUnlocked}"
                            title="${style.name}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <span class="swatch-bubble" style="background-color: ${style.hex}; box-shadow: 0 0 12px ${style.hex}">
                          ${!isUnlocked ? renderSwatchLockBadge(style.hex, 12) : ''}
                        </span>
                        <span class="swatch-name">${style.name}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else if (this._activeMiscCategory === 'profile') {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  ${this.getStableSortedStyles(
                    'profile',
                    allProfileCards.map(c => ({ ...c, id: c.name })),
                    name => inventory.hasProfileArtwork(name)
                  ).map(card => {
                    const isUnlocked = inventory.hasProfileArtwork(card.name);
                    const isEquipped = equippedProfileName === card.name;
                    const thumbUrl = card.images?.[0] ? getCardThumbUrl(card.images[0]) : '';
                    return `
                      <div class="color-swatch-card profile-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                           data-profile-artwork-name="${card.name}"
                           data-unlocked="${isUnlocked}"
                           title="${card.name}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <div class="swatch-bubble profile-swatch-box">
                          ${isUnlocked && thumbUrl
                            ? `<img src="${thumbUrl}" alt="${card.name}" class="profile-swatch-img" />`
                            : renderSwatchLockBadge(null, 12)
                          }
                        </div>
                        <span class="swatch-name">${card.name}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else if (this._activeMiscCategory === 'hp_bar_color') {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  <div class="color-swatch-card ${!equippedHpStyleId ? 'equipped' : ''}" data-misc-style-id="default" title="Default">
                    <span class="swatch-bubble" style="background: linear-gradient(90deg, #0097a7, #00e5ff); box-shadow: 0 0 8px rgba(0,229,255,0.4)"></span>
                    <span class="swatch-name">Default</span>
                  </div>
                  ${this.getStableSortedStyles(
                    'hp_bar_color',
                    REEL_3_STYLES,
                    id => inventory.hasHpBarColor(id),
                    id => equippedHpStyleId === id
                  ).map(style => {
                    const isUnlocked = inventory.hasHpBarColor(style.id);
                    const isEquipped = equippedHpStyleId === style.id;
                    return `
                      <div class="color-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                            data-misc-style-id="${style.id}"
                            data-unlocked="${isUnlocked}"
                            title="${style.name}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <span class="swatch-bubble" style="background-color: ${style.hex}; box-shadow: 0 0 12px ${style.hex}">
                          ${!isUnlocked ? renderSwatchLockBadge(style.hex, 12) : ''}
                        </span>
                        <span class="swatch-name">${style.name}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else if (this._activeMiscCategory === 'avatar') {
            const equippedAvatarId = gameProgress.getEquippedAvatarId() || avatarCatalog[0]?.id;
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  ${avatarCatalog.map(a => {
                    const isUnlocked = a.unlockLevel <= playerLevel;
                    const isEquipped = equippedAvatarId === a.id;
                    const thumbUrl = isUnlocked ? resolveAvatarImageUrl(a) : null;
                    return `
                      <div class="color-swatch-card profile-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                           data-avatar-id="${a.id}"
                           data-unlocked="${isUnlocked}"
                           title="${a.unitName}${!isUnlocked ? ` (Locked – Requires Level ${a.unlockLevel})` : ''}">
                        <div class="swatch-bubble profile-swatch-box">
                          ${isUnlocked && thumbUrl
                            ? `<img src="${thumbUrl}" alt="${a.unitName}" class="profile-swatch-img" style="object-fit: ${a.tier === 'artwork' ? 'cover' : 'contain'}; image-rendering: pixelated;" />`
                            : renderSwatchLockBadge(null, 12)
                          }
                        </div>
                        <span class="swatch-name">${a.unitName}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else if (this._activeMiscCategory === 'avatar_border') {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  <div class="color-swatch-card ${!equippedAvatarStyleId ? 'equipped' : ''}" data-misc-style-id="default" title="Default">
                    <span class="swatch-bubble" style="background-color: #38bdf8; box-shadow: 0 0 8px rgba(56,189,248,0.5)"></span>
                    <span class="swatch-name">Default</span>
                  </div>
                  ${this.getStableSortedStyles(
                    'avatar_border',
                    REEL_3_STYLES,
                    id => inventory.hasAvatarBorder(id),
                    id => equippedAvatarStyleId === id
                  ).map(style => {
                    const isUnlocked = inventory.hasAvatarBorder(style.id);
                    const isEquipped = equippedAvatarStyleId === style.id;
                    return `
                      <div class="color-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                            data-misc-style-id="${style.id}"
                            data-unlocked="${isUnlocked}"
                            title="${style.name}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <span class="swatch-bubble" style="background-color: ${style.hex}; box-shadow: 0 0 12px ${style.hex}">
                          ${!isUnlocked ? renderSwatchLockBadge(style.hex, 12) : ''}
                        </span>
                        <span class="swatch-name">${style.name}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else if (this._activeMiscCategory === 'taunts') {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  ${allTauntDefs.map(t => {
                    const isUnlocked = unlockedTauntIds.includes(t.id);
                    const isEquipped = equippedTauntIds.includes(t.id);
                    return `
                      <div class="color-swatch-card taunt-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                           data-taunt-id="${t.id}"
                           data-unlocked="${isUnlocked}"
                           title="${t.label}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <div class="swatch-bubble taunt-swatch-box" style="position: relative;">
                          <span class="taunt-swatch-bubble" style="${isUnlocked ? `background-color: ${t.themeColor}; box-shadow: 0 0 12px ${t.themeColor};` : 'opacity: 0.35;'}">${t.icon}</span>
                          ${!isUnlocked ? renderSwatchLockBadge(null, 12, 'position: absolute;') : ''}
                        </div>
                        <span class="swatch-name">${t.label}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else if (this._activeMiscCategory === 'deck_color') {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  <div class="color-swatch-card ${!equippedDeckStyleId ? 'equipped' : ''}" data-misc-style-id="default" title="Default">
                    <span class="swatch-bubble" style="background-color: #00f0ff; box-shadow: 0 0 8px rgba(0,240,255,0.5)"></span>
                    <span class="swatch-name">Default</span>
                  </div>
                  ${this.getStableSortedStyles(
                    'deck_color',
                    REEL_3_STYLES,
                    id => inventory.hasDeckColor(id),
                    id => equippedDeckStyleId === id
                  ).map(style => {
                    const isUnlocked = inventory.hasDeckColor(style.id);
                    const isEquipped = equippedDeckStyleId === style.id;
                    return `
                      <div class="color-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                            data-misc-style-id="${style.id}"
                            data-unlocked="${isUnlocked}"
                            title="${style.name}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <span class="swatch-bubble" style="background-color: ${style.hex}; box-shadow: 0 0 12px ${style.hex}">
                          ${!isUnlocked ? renderSwatchLockBadge(style.hex, 12) : ''}
                        </span>
                        <span class="swatch-name">${style.name}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else if (this._activeMiscCategory === 'deck_icon') {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  ${DECK_ICON_PRESETS.map(iconPreset => {
                    const isUnlocked = inventory.hasDeckIcon(iconPreset.id);
                    const isEquipped = equippedDeckIconId === iconPreset.id;
                    const rarityColor = iconPreset.rarity === 'epic' ? '#f59e0b' : (iconPreset.rarity === 'rare' ? '#c084fc' : '#38bdf8');
                    return `
                      <div class="color-swatch-card deck-icon-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                           data-deck-icon-id="${iconPreset.id}"
                           data-unlocked="${isUnlocked}"
                           title="${iconPreset.label}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <span class="swatch-bubble deck-icon-swatch-bubble" style="display:inline-flex;align-items:center;justify-content:center;background:#060a12;border:1.5px solid ${isEquipped ? '#f59e0b' : rarityColor};box-shadow:0 0 10px ${isEquipped ? '#f59e0b' : rarityColor}66;position:relative;">
                          <span style="${!isUnlocked ? 'opacity: 0.25;' : ''}">${iconPreset.glyph || '◈'}</span>
                          ${!isUnlocked ? renderSwatchLockBadge(null, 11, 'position: absolute;') : ''}
                        </span>
                        <span class="swatch-name">${iconPreset.label}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else if (this._activeMiscCategory === 'deckbuilder') {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  ${DECKBUILDER_THEMES.map(theme => {
                    const isUnlocked = inventory.hasDeckbuilderTheme(theme.id);
                    const isEquipped = equippedDeckbuilderThemeId === theme.id;
                    return `
                      <div class="color-swatch-card deckbuilder-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                           data-deckbuilder-theme-id="${theme.id}"
                           data-unlocked="${isUnlocked}"
                           title="${theme.name}${!isUnlocked ? ' (Obtainable in Supply Surge)' : ''}">
                        ${this._renderThemeSwatchThumb(theme.id, isUnlocked, 'deckbuilder')}
                        <span class="swatch-name">${theme.name}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else if (this._activeMiscCategory === 'cards') {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  ${CARD_THEMES.map(theme => {
                    const isUnlocked = inventory.hasCardTheme(theme.id);
                    const isEquipped = equippedCardThemeId === theme.id;
                    return `
                      <div class="color-swatch-card cards-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                           data-card-theme-id="${theme.id}"
                           data-unlocked="${isUnlocked}"
                           title="${theme.name}${!isUnlocked ? ' (Obtainable in Supply Surge)' : ''}">
                        ${this._renderThemeSwatchThumb(theme.id, isUnlocked, 'cards')}
                        <span class="swatch-name">${theme.name}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          } else {
            dockHtml = `
              <div class="color-rail-container">
                <div class="color-rail">
                  <div class="color-swatch-card ${!equippedTrayStyleId ? 'equipped' : ''}" data-misc-style-id="default" title="Default">
                    <span class="swatch-bubble" style="background-color: #38bdf8; box-shadow: 0 0 8px rgba(56,189,248,0.5)"></span>
                    <span class="swatch-name">Default</span>
                  </div>
                  ${this.getStableSortedStyles(
                    'tray_color',
                    REEL_3_STYLES,
                    id => inventory.hasTrayColor(id),
                    id => equippedTrayStyleId === id
                  ).map(style => {
                    const isUnlocked = inventory.hasTrayColor(style.id);
                    const isEquipped = equippedTrayStyleId === style.id;
                    return `
                      <div class="color-swatch-card ${isEquipped ? 'equipped' : ''} ${!isUnlocked ? 'locked' : ''}"
                            data-misc-style-id="${style.id}"
                            data-unlocked="${isUnlocked}"
                            title="${style.name}${!isUnlocked ? ' (Locked – Unlockable in Supply Surge)' : ''}">
                        <span class="swatch-bubble" style="background-color: ${style.hex}; box-shadow: 0 0 12px ${style.hex}">
                          ${!isUnlocked ? renderSwatchLockBadge(style.hex, 12) : ''}
                        </span>
                        <span class="swatch-name">${style.name}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          }

          return `
            <div class="showroom-misc-layout">
              <div class="showroom-misc-preview-row">
                <div class="showroom-misc-slider-wrap" id="showroom-misc-swipe-area">
                  <div class="showroom-misc-track" id="showroom-misc-track">
                    ${miscCategoriesData.map(cat => `
                      <div class="misc-category-card ${cat.id === this._activeMiscCategory ? 'active' : ''}" data-misc-cat="${cat.id}">
                        <div class="misc-card-header">
                          <span class="misc-card-title">${cat.title}</span>
                          <span class="misc-card-badge">${cat.unlockedCount}/${cat.totalCount}</span>
                        </div>
                        <div class="misc-card-body">
                          ${cat.previewHtml}
                        </div>
                      </div>
                    `).join('')}
                  </div>
                </div>
              </div>
              <div class="workbench-dock-compact">${dockHtml}</div>
            </div>
          `;
        })() : this.renderProgressTab()}
      </div>
    `;

    if (savedScrolls.size > 0) {
      const restorePositions = () => {
        savedScrolls.forEach((pos, sel) => {
          const el = this.stageContainer?.querySelector(sel);
          if (el) {
            const prev = el.style.scrollBehavior;
            el.style.scrollBehavior = 'auto';
            el.scrollLeft = pos.left;
            el.scrollTop = pos.top;
            el.style.scrollBehavior = prev;
          }
        });
      };
      restorePositions();
      requestAnimationFrame(restorePositions);
    }

    this.bindStageEvents(currentUnit, testFireColorHex);
  }

  // Renders a miniature card thumbnail for Deck Builder & Cards chassis swatches in Arsenal > Player
  _renderThemeSwatchThumb(themeId, isUnlocked = true, category = 'deckbuilder') {
    const marineCard = (Array.isArray(CARDS_DATA) ? CARDS_DATA.find(c => c.name === 'Marine') : null) || {
      images: ['Marine_1.png']
    };
    const cardThumb = getCardThumbUrl(marineCard.images?.[0] || 'Marine_1.png', 's');
    const fallbackSrc = getCardThumbUrl(marineCard.images?.[0] || 'Marine_1.png', 's');

    const isCards = category === 'cards';
    let overlayHtml = '';

    if (isCards) {
      // In-Game Hand Cards ("Cards"):
      // 1. NO 1-2-3 Overdrive pips on any theme
      // 2. NO top header bar with blue 5 on Classic Steel
      // 3. All themes EXCEPT artwork show the in-game bottom-right green cost badge circle with '5'
      const costBadgeHtml = themeId !== 'artwork' ? `<div class="theme-swatch-hand-cost">5</div>` : '';
      if (themeId === 'glass') {
        overlayHtml = `<div class="theme-swatch-knurl"></div><div class="theme-swatch-sheen"></div>${costBadgeHtml}`;
      } else if (themeId === 'fluted') {
        overlayHtml = `<div class="theme-swatch-flutes"></div>${costBadgeHtml}`;
      } else if (themeId === 'holo') {
        overlayHtml = `<div class="theme-swatch-holo"></div><div class="theme-swatch-topo"></div>${costBadgeHtml}`;
      } else if (themeId === 'classic') {
        overlayHtml = `${costBadgeHtml}`;
      } else if (themeId === 'artwork') {
        overlayHtml = '';
      }
    } else {
      // Tactical Deck Builder ("Deck Builder"):
      // Shows the 1-2-3 Overdrive pips at top-center and steel header on classic
      if (themeId === 'glass') {
        overlayHtml = `<div class="theme-swatch-knurl"></div><div class="theme-swatch-sheen"></div><div class="theme-swatch-pips"><span></span><span></span><span></span></div>`;
      } else if (themeId === 'fluted') {
        overlayHtml = `<div class="theme-swatch-flutes"></div><div class="theme-swatch-pips"><span></span><span></span><span></span></div>`;
      } else if (themeId === 'holo') {
        overlayHtml = `<div class="theme-swatch-holo"></div><div class="theme-swatch-topo"></div><div class="theme-swatch-pips"><span></span><span></span><span></span></div>`;
      } else if (themeId === 'classic') {
        overlayHtml = `<div class="theme-swatch-steel"></div>`;
      } else if (themeId === 'artwork') {
        overlayHtml = '';
      }
    }

    return `
      <div class="swatch-bubble theme-swatch-box deckbuilder-swatch-box ${isCards ? 'cards-swatch-box' : ''}">
        <div class="theme-swatch-card theme-${themeId}" style="${!isUnlocked ? 'filter: grayscale(0.8) brightness(0.5);' : ''}">
          <img src="${cardThumb}" class="theme-swatch-art" alt="${themeId}" draggable="false" decoding="async" onerror="if (!this.dataset.fallback) { this.dataset.fallback = '1'; this.src = '${fallbackSrc}'; }" />
          ${overlayHtml}
        </div>
        ${!isUnlocked ? renderSwatchLockBadge(null, 12, 'position: absolute; z-index: 3;') : ''}
      </div>
    `;
  }

  // Renders a miniature preview card for Arsenal > Player > Deckbuilder
  _renderDeckbuilderCardPreview(themeId = 'glass') {
    const isGlass = (themeId === 'glass' || themeId === 'fluted' || themeId === 'holo');
    const isArtwork = (themeId === 'artwork');
    const cutClass = (themeId === 'holo') ? 'cut-topography' : ((themeId === 'fluted') ? 'cut-micro-fluted' : 'cut-knurled');
    const matClass = (themeId === 'holo') ? 'mat-holo' : 'mat-crystal';
    const marineCard = (Array.isArray(CARDS_DATA) ? CARDS_DATA.find(c => c.name === 'Marine') : null) || {
      name: 'Marine',
      cost: 5,
      hp: 45,
      damage: 12,
      tier: 1,
      description: 'Basic infantry unit',
      lore: 'Why me?',
      images: ['Marine_1.png']
    };

    const cardImg = getCardThumbUrl(marineCard.images?.[0] || 'Marine_1.png', 'm');
    const fallbackSrc = getCardThumbUrl(marineCard.images?.[0] || 'Marine_1.png', 's');

    const glassClasses = isGlass
      ? `theme-glass glass-chassis glass-active style-crystal ${cutClass} frame-carbon ${matClass} shape-classic appeal-arcade ind-pips-top pal-overdrive in-deck copies-1-active`
      : (isArtwork
        ? 'theme-artwork shape-classic pal-overdrive in-deck copies-1-active'
        : 'theme-classic in-deck copies-1');

    const cardInner = `
      <img class="card-bg-img" src="${cardImg}" alt="${marineCard.name}" draggable="false" decoding="async" onerror="if (!this.dataset.fallback) { this.dataset.fallback = '1'; this.src = '${fallbackSrc}'; }" />

      <div class="card-header-bar">
        <div class="card-header-left">
          <span class="card-name">${marineCard.name}</span>
        </div>
        <span class="card-cost-badge">${marineCard.cost}</span>
      </div>

      <div class="copies-pips-cluster pips-top-center" aria-hidden="true">
        <span class="pip pip-1 active" data-pip="1"></span>
        <span class="pip pip-2" data-pip="2"></span>
        <span class="pip pip-3" data-pip="3"></span>
      </div>

      <div class="card-body-scrim">
        ${isGlass ? '<div class="scrim-gradient-blur" aria-hidden="true"></div>' : ''}
        <p class="card-desc">${marineCard.description}</p>
        <p class="card-lore">${marineCard.lore}</p>
      </div>

      <div class="card-footer-bar">
        <div class="card-footer-stats">
          <span class="card-footer-stat hp" title="Health">${getIconSvg('hp', 12)} ${marineCard.hp}</span>
          <span class="card-footer-stat dmg" title="Damage">${getIconSvg('attack', 12)} ${marineCard.damage}</span>
        </div>

        <div class="card-footer-actions">
          <span class="card-tier-tag tier-1" title="Tier ${marineCard.tier}">1</span>
          <span class="card-qty-tag" title="Add to Deck">+</span>
        </div>
      </div>
    `;

    return `
      <div class="showroom-deckbuilder-preview" style="pointer-events: none;">
        <div class="showroom-deckbuilder-scaler">
          <div class="card-item ${glassClasses} rarity-common copies-0 showroom-card-preview-item"
               style="--glass-bezel: 3px; --card-glass-radius: 22px; --inner-card-radius: 17px; --glass-blur: 2px; --btn-depth: 0px; --knurl-gap: 10px; --micro-gap: 3.5px; --topo-gap: 14px;">
            ${isGlass ? `
              <div class="glass-button-dome" aria-hidden="true"></div>
              <div class="glass-bezel-fiberglow" aria-hidden="true"></div>
              <div class="glass-recess-glow" aria-hidden="true"></div>
              <div class="glass-overlay-container" aria-hidden="true">
                <div class="glass-material-tint"></div>
                <div class="glass-sheen-diagonal"></div>
                <div class="glass-glare-interactive"></div>
                <div class="glass-prismatic-border"></div>
                <div class="glass-noise-grain"></div>
              </div>
              <div class="card-inner-chassis">
                ${cardInner}
              </div>
            ` : cardInner}
          </div>
        </div>
      </div>
    `;
  }

  _renderCardsCardPreview(themeId = 'glass') {
    const isGlass = (themeId === 'glass' || themeId === 'fluted' || themeId === 'holo');
    const isArtwork = (themeId === 'artwork');
    const cutClass = (themeId === 'holo') ? 'cut-topography' : ((themeId === 'fluted') ? 'cut-micro-fluted' : 'cut-knurled');
    const matClass = (themeId === 'holo') ? 'mat-holo' : 'mat-crystal';
    const marineCard = (Array.isArray(CARDS_DATA) ? CARDS_DATA.find(c => c.name === 'Marine') : null) || {
      name: 'Marine',
      cost: 5,
      hp: 45,
      damage: 12,
      tier: 1,
      description: 'Basic infantry unit',
      lore: 'Why me?',
      images: ['Marine_1.png']
    };

    const cardImg = getCardThumbUrl(marineCard.images?.[0] || 'Marine_1.png', 'm');
    const fallbackSrc = getCardThumbUrl(marineCard.images?.[0] || 'Marine_1.png', 's');

    const themeClasses = isGlass
      ? `theme-glass glass-chassis glass-active style-crystal ${cutClass} frame-carbon ${matClass}`
      : (isArtwork
        ? 'theme-artwork'
        : 'theme-classic');

    const glassLayersHtml = isGlass ? `
      <div class="glass-button-dome" aria-hidden="true"></div>
      <div class="glass-bezel-fiberglow" aria-hidden="true"></div>
      <div class="glass-recess-glow" aria-hidden="true"></div>
      <div class="glass-overlay-container" aria-hidden="true">
        <div class="glass-material-tint"></div>
        <div class="glass-sheen-diagonal"></div>
        <div class="glass-glare-interactive"></div>
        <div class="glass-prismatic-border"></div>
        <div class="glass-noise-grain"></div>
      </div>
    ` : '';

    return `
      <div class="card-slot filled ${themeClasses} rarity-common caste-infantry affordable"
           style="pointer-events: none;">
        ${glassLayersHtml}
        <div class="card-inner">
          <img src="${cardImg}" alt="${marineCard.name}" class="card-bg-img" draggable="false" decoding="async" onerror="if (!this.dataset.fallback) { this.dataset.fallback = '1'; this.src = '${fallbackSrc}'; }" />
          <div class="card-content-overlay">
            <div class="card-header">
              <span class="card-name">${marineCard.name}</span>
              <span class="card-cost">${marineCard.cost}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // ==========================================================
  // TAB 3: PROGRESS (docs/XP_PROGRESSION_PLAN.md)
  // Own sub-navigation (Übersicht/Erfolge/Statistiken/Level-Pfad) instead
  // of the Master tab's single-detail-plus-category-cards pattern - there
  // is simply more content here than that pattern scales to (section 3 of
  // the plan doc).
  // ==========================================================

  renderProgressTab() {
    const state = gameProgress.getState();
    const levelInfo = gameProgress.getLevelProgress();
    const avgGrade = gameProgress.getAverageGrade();
    const avgGradeLetter = avgGrade.gradedMatches > 0 ? gradeFromComposite(avgGrade.average) : '—';
    const xpRatio = Math.max(0, Math.min(1, levelInfo.ratio));

    const view = this._activeProgressView;

    const totalEvents = state ? gameProgress.getRecentEventCount() : 0;
    const unlockedAchievements = Array.isArray(state?.unlockedAchievements)
      ? state.unlockedAchievements
      : (state?.achievements?.unlocked
          ? Object.keys(state.achievements.unlocked)
          : (typeof gameProgress.getUnlockedAchievementIds === 'function' ? gameProgress.getUnlockedAchievementIds() : []));
    const totalAchievements = ACHIEVEMENTS.length;
    const achievementsPct = totalAchievements > 0 ? Math.round((unlockedAchievements.length / totalAchievements) * 100) : 0;

    const combat = state?.combat || (typeof gameProgress.getCombatStats === 'function' ? gameProgress.getCombatStats() : {});
    const matchesPlayed = (state?.stats?.matchesPlayed != null)
      ? state.stats.matchesPlayed
      : (((combat.matchesByMode?.skirmish || 0) + (combat.matchesByMode?.online || 0)) || ((combat.wins || 0) + (combat.losses || 0)));
    const wins = (state?.stats?.wins != null) ? state.stats.wins : (combat.wins || 0);
    const winRate = matchesPlayed > 0 ? Math.round((wins / matchesPlayed) * 100) : 0;

    const progressCategoriesData = [
      {
        id: 'overview',
        title: 'Log',
        iconSvg: `
          <svg class="kpi-header-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M1 8h3l2-5 4 10 2-5h3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `,
        badge: `${totalEvents} Events`,
        previewHtml: `
          <div class="progress-card-preview">
            <div class="progress-kpi-gauge kpi-gauge-log">
              <svg class="kpi-svg-log" viewBox="0 0 120 44" fill="none">
                <line x1="0" y1="22" x2="120" y2="22" stroke="rgba(56,189,248,0.14)" stroke-dasharray="2 3" />
                <line x1="30" y1="2" x2="30" y2="42" stroke="rgba(56,189,248,0.08)" stroke-dasharray="2 3" />
                <line x1="60" y1="2" x2="60" y2="42" stroke="rgba(56,189,248,0.08)" stroke-dasharray="2 3" />
                <line x1="90" y1="2" x2="90" y2="42" stroke="rgba(56,189,248,0.08)" stroke-dasharray="2 3" />
                <path class="kpi-wave-path ${totalEvents > 0 ? 'active-feed' : ''}" d="M 0 22 L 34 22 L 42 10 L 50 36 L 58 14 L 66 28 L 74 22 L 120 22" 
                      stroke="#38bdf8" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
                <circle cx="58" cy="14" r="3" fill="#f0f9ff" class="kpi-ping-dot" />
              </svg>
            </div>
            <span class="progress-card-main-stat">${totalEvents} Events</span>
            <div class="progress-card-mini-bar">
              <div class="progress-card-mini-fill" style="width: ${totalEvents > 0 ? Math.min(100, Math.max(15, (totalEvents / PROGRESS_LOG_EXPANDED_COUNT) * 100)) : 10}%"></div>
            </div>
            <span class="progress-card-sub-stat">${totalEvents > 0 ? 'Telemetry Active' : 'Signal: Standby'}</span>
          </div>
        `
      },
      {
        id: 'achievements',
        title: 'Achievements',
        iconSvg: `
          <svg class="kpi-header-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6">
            <polygon points="8 1 10.2 5.5 15 6.2 11.5 9.6 12.3 14.5 8 12.2 3.7 14.5 4.5 9.6 1 6.2 5.8 5.5 8 1" stroke-linejoin="round"/>
          </svg>
        `,
        badge: `${unlockedAchievements.length}/${totalAchievements}`,
        previewHtml: `
          <div class="progress-card-preview">
            <div class="progress-kpi-gauge kpi-gauge-radial">
              <svg class="kpi-svg-donut" viewBox="0 0 54 54">
                <circle cx="27" cy="27" r="21" class="kpi-track-ring" stroke="rgba(255,255,255,0.08)" stroke-width="4.5" fill="none"/>
                <circle cx="27" cy="27" r="21" class="kpi-fill-ring kpi-ring-amber" stroke="#f59e0b" stroke-width="4.5" 
                        stroke-dasharray="132" stroke-dashoffset="${(132 * (1 - Math.min(100, Math.max(0, achievementsPct)) / 100)).toFixed(1)}" 
                        stroke-linecap="round" fill="none"/>
              </svg>
              <div class="kpi-gauge-center">
                <span class="kpi-center-val kpi-val-amber">${achievementsPct}%</span>
              </div>
            </div>
            <span class="progress-card-main-stat">${unlockedAchievements.length} Unlocked</span>
            <div class="progress-card-mini-bar">
              <div class="progress-card-mini-fill kpi-mini-fill-amber" style="width: ${achievementsPct}%"></div>
            </div>
            <span class="progress-card-sub-stat">${achievementsPct}% Complete</span>
          </div>
        `
      },
      {
        id: 'stats',
        title: 'Career',
        iconSvg: `
          <svg class="kpi-header-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6">
            <circle cx="8" cy="8" r="6.5"/>
            <line x1="8" y1="1" x2="8" y2="4"/>
            <line x1="8" y1="12" x2="8" y2="15"/>
            <line x1="1" y1="8" x2="4" y2="8"/>
            <line x1="12" y1="8" x2="15" y2="8"/>
          </svg>
        `,
        badge: `${avgGradeLetter} Grade`,
        previewHtml: `
          <div class="progress-card-preview">
            <div class="progress-kpi-gauge kpi-gauge-combat">
              <svg class="kpi-svg-combat" viewBox="0 0 54 54">
                <circle cx="27" cy="27" r="22" stroke="rgba(16,185,129,0.18)" stroke-width="1.5" stroke-dasharray="2 3" fill="none"/>
                <circle cx="27" cy="27" r="18" class="kpi-fill-ring kpi-ring-emerald" stroke="#10b981" stroke-width="3.5"
                        stroke-dasharray="113" stroke-dashoffset="${(113 * (1 - Math.min(100, Math.max(0, winRate)) / 100)).toFixed(1)}"
                        stroke-linecap="round" fill="none"/>
                <polygon points="27,14 36,19 36,33 27,38 18,33 18,19" fill="#064e3b" stroke="#10b981" stroke-width="1.2"/>
              </svg>
              <div class="kpi-gauge-center">
                <span class="kpi-combat-grade">${avgGradeLetter || '—'}</span>
              </div>
            </div>
            <span class="progress-card-main-stat">${matchesPlayed} Matches</span>
            <div class="progress-card-mini-bar">
              <div class="progress-card-mini-fill kpi-mini-fill-emerald" style="width: ${winRate}%"></div>
            </div>
            <span class="progress-card-sub-stat">${wins}W · ${winRate}% Win</span>
          </div>
        `
      },
      {
        id: 'levelpath',
        title: 'Level',
        iconSvg: `
          <svg class="kpi-header-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 7 8 2 13 7"/>
            <polyline points="3 12 8 7 13 12"/>
          </svg>
        `,
        badge: `Lvl ${levelInfo.level}`,
        previewHtml: `
          <div class="progress-card-preview">
            <div class="progress-kpi-gauge kpi-gauge-rank">
              <svg class="kpi-svg-reactor" viewBox="0 0 54 54">
                <circle cx="27" cy="27" r="22" stroke="rgba(168,85,247,0.18)" stroke-width="2.5" fill="none"/>
                <circle cx="27" cy="27" r="22" class="kpi-fill-ring kpi-ring-purple" stroke="#c084fc" stroke-width="2.5"
                        stroke-dasharray="138" stroke-dashoffset="${(138 * (1 - Math.min(1, Math.max(0, isNaN(xpRatio) ? 0 : xpRatio)))).toFixed(1)}"
                        stroke-linecap="round" fill="none"/>
              </svg>
              <div class="kpi-gauge-center kpi-rank-center">
                <svg class="kpi-chevrons" viewBox="0 0 20 16" fill="none" stroke="#e9d5ff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="4 9 10 4 16 9"/>
                  <polyline points="4 13 10 8 16 13"/>
                </svg>
                <span class="kpi-rank-lvl">LVL ${levelInfo.level}</span>
              </div>
            </div>
            <span class="progress-card-main-stat">Level ${levelInfo.level}</span>
            <div class="progress-card-mini-bar">
              <div class="progress-card-mini-fill kpi-mini-fill-purple" style="width: ${(xpRatio * 100).toFixed(0)}%"></div>
            </div>
            <span class="progress-card-sub-stat">${levelInfo.title}</span>
          </div>
        `
      },
      {
        id: 'ladder',
        title: 'Ranked',
        iconSvg: `
          <svg class="kpi-header-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6">
            <polygon points="8 1 10.2 5.5 15 6.2 11.5 9.6 12.3 14.5 8 12.2 3.7 14.5 4.5 9.6 1 6.2 5.8 5.5 8 1" stroke-linejoin="round"/>
          </svg>
        `,
        badge: `${getMMR()} MMR`,
        previewHtml: `
          <div class="progress-card-preview">
            <div class="progress-kpi-gauge kpi-gauge-ladder">
              <svg class="kpi-svg-donut" viewBox="0 0 54 54">
                <circle cx="27" cy="27" r="21" class="kpi-track-ring" stroke="rgba(255,255,255,0.08)" stroke-width="4.5" fill="none"/>
                <circle cx="27" cy="27" r="21" class="kpi-fill-ring" stroke="${getRankTier(getMMR()).color}" stroke-width="4.5" 
                        stroke-dasharray="132" stroke-dashoffset="${(132 * (1 - Math.min(1, Math.max(0, (getMMR() - 1000) / 1000)))).toFixed(1)}" 
                        stroke-linecap="round" fill="none"/>
              </svg>
              <div class="kpi-gauge-center">
                <span class="kpi-center-val" style="color: ${getRankTier(getMMR()).color}; font-size: 13px;">${getRankTier(getMMR()).icon}</span>
              </div>
            </div>
            <span class="progress-card-main-stat">${getMMR()} MMR</span>
            <div class="progress-card-mini-bar">
              <div class="progress-card-mini-fill" style="background: ${getRankTier(getMMR()).color}; width: ${Math.min(100, Math.max(10, ((getMMR() - 1000) / 1000) * 100))}%"></div>
            </div>
            <span class="progress-card-sub-stat">${getRankTier(getMMR()).name}</span>
          </div>
        `
      }
    ];

    return `
      <div class="showroom-progress-layout">
        <!-- Horizontal Category Slider Track (matching Player tab) -->
        <div class="showroom-misc-preview-row progress-slider-row">
          <div class="showroom-misc-slider-wrap progress-slider-wrap" id="progress-slider-wrap">
            <div class="showroom-misc-track progress-slider-track" id="progress-slider-track">
              ${progressCategoriesData.map(cat => `
                <div class="misc-category-card progress-category-card progress-subnav-btn ${cat.id === view ? 'active' : ''}" data-progress-view="${cat.id}">
                  <div class="misc-card-header">
                    <span class="misc-card-title"><span class="progress-tab-icon">${cat.iconSvg}</span> ${cat.title}</span>
                  </div>
                  <div class="misc-card-body progress-card-body">
                    ${cat.previewHtml}
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        </div>

        <div class="progress-view-content">
          ${
            view === 'achievements' ? this._renderProgressAchievements(state) :
            view === 'stats' ? this._renderProgressStats(state, avgGrade, avgGradeLetter, levelInfo) :
            view === 'levelpath' ? this._renderProgressLevelPath(levelInfo.level) :
            view === 'ladder' ? this._renderProgressLadder() :
            this._renderProgressOverview(state, levelInfo, avgGrade, avgGradeLetter, xpRatio)
          }
        </div>
      </div>
    `;
  }

  _renderProgressOverview(state, levelInfo, avgGrade, avgGradeLetter, xpRatio) {
    const totalEvents = state ? gameProgress.getRecentEventCount() : 0;
    const expanded = this._progressLogExpanded && totalEvents > PROGRESS_LOG_COLLAPSED_COUNT;
    const recent = state
      ? gameProgress.getRecentEvents(expanded ? PROGRESS_LOG_EXPANDED_COUNT : PROGRESS_LOG_COLLAPSED_COUNT)
      : [];
    const nextGoals = state
      ? getNextGoals(
          ACHIEVEMENTS,
          buildAchievementFacts({ progress: gameProgress, inventory }),
          state.achievements?.unlocked || {},
          3
        )
      : [];

    const formatRelativeTime = (isoString) => {
      if (!isoString) return '';
      const now = Date.now();
      const past = new Date(isoString).getTime();
      if (isNaN(past)) return '';
      const diffSec = Math.max(0, Math.floor((now - past) / 1000));
      if (diffSec < 60) return 'Just now';
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDays = Math.floor(diffHr / 24);
      if (diffDays < 7) return `${diffDays}d ago`;
      return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return `
      <div class="progress-overview">
        <div class="progress-level-hero">
          <div class="progress-level-badge">
            <span class="progress-level-num">${levelInfo.level}</span>
          </div>
          <div class="progress-level-info">
            <span class="progress-level-title">${levelInfo.title}</span>
            <div class="progress-xp-bar"><div class="progress-xp-fill" style="width:${(xpRatio * 100).toFixed(1)}%"></div></div>
            <span class="progress-xp-label">${levelInfo.xpIntoLevel} / ${levelInfo.xpForThisLevel} XP · ${levelInfo.xpToNextLevel} XP to Level ${levelInfo.level + 1}</span>
          </div>
        </div>

        <div class="progress-kpi-row">
          <div class="progress-kpi-card">
            <span class="kpi-label">Overall Rank</span>
            <span class="kpi-value">${avgGradeLetter}</span>
            <span class="kpi-sub">${avgGrade.gradedMatches > 0 ? `Ø ${avgGrade.average.toFixed(1)}/100 · ${avgGrade.gradedMatches} Matches` : 'No graded matches recorded yet'}</span>
          </div>
          <div class="progress-kpi-card">
            <span class="kpi-label">Perks & Gold Cards</span>
            <span class="kpi-value">${state.uniquePerksCollected}</span>
            <span class="kpi-sub">Unique Pack & Supply Unlocks</span>
          </div>
          <div class="progress-kpi-card">
            <span class="kpi-label">Fugazi (All-Time)</span>
            <span class="kpi-value">${state.lifetimeFugaziEarned.toLocaleString()}</span>
            <span class="kpi-sub">Lifetime Earnings</span>
          </div>
        </div>

        ${nextGoals.length === 0 ? '' : `
        <div class="progress-goals">
          <div class="progress-goals-header">
            <span class="progress-goals-title">Next Goals</span>
            <span class="progress-goals-count">${nextGoals.length} Active</span>
          </div>
          <div class="progress-goals-grid">
            ${nextGoals.map(g => `
              <div class="progress-goal-card" data-tier="${g.def.tier}">
                <div class="progress-goal-icon">
                  <img src="${buildAchievementIconBitmap(g.def.icon, 40).toDataURL()}" alt="${g.def.name}" class="progress-goal-icon-img">
                </div>
                <div class="progress-goal-info">
                  <span class="progress-goal-name">${g.def.name}</span>
                  <div class="progress-goal-bar"><div class="progress-goal-fill" style="width: ${(g.ratio * 100).toFixed(1)}%"></div></div>
                  <div class="progress-goal-stats">
                    <span class="progress-goal-count">${g.value.toLocaleString('en-US')} / ${g.target.toLocaleString('en-US')}</span>
                    ${g.def.xp > 0 ? `<span class="progress-goal-reward">+${g.def.xp} XP</span>` : ''}
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
        </div>`}

        <div class="progress-recent">
          <div class="progress-recent-header">
            <span class="progress-recent-title">Recent Activity</span>
            <span class="progress-recent-count">${totalEvents} Logged</span>
          </div>
          ${recent.length === 0
            ? `<div class="progress-recent-empty">No combat or progression events recorded yet. Enter Battle to earn XP and unlock achievements!</div>`
            : `<div class="progress-timeline${expanded ? ' expanded' : ''}">
                ${recent.map(ev => {
                  const relTime = formatRelativeTime(ev.at);
                  let icon = getIconSvg('energy', 14);
                  let title = '';
                  let detail = '';
                  let badge = '';
                  let badgeCls = '';

                  if (ev.type === 'match') {
                    icon = ev.isVictory ? getIconSvg('trophy', 14) : getIconSvg('skull', 14);
                    title = ev.isVictory ? 'Victory' : 'Defeat';
                    detail = `${ev.mode === 'online' ? 'Online' : 'Skirmish'} · Combat Rank ${ev.grade || '—'}`;
                    badge = `+${ev.xp} XP`;
                    badgeCls = ev.isVictory ? 'victory' : 'defeat';
                  } else if (ev.type === 'achievement') {
                    const def = ACHIEVEMENTS.find(a => a.id === ev.id);
                    let icon = getIconSvg('trophy', 14);
                    if (def?.icon) {
                      try {
                        const iconUrl = buildAchievementIconBitmap(def.icon, 32).toDataURL();
                        if (iconUrl) icon = `<img src="${iconUrl}" alt="${def.name}" class="timeline-achievement-thumb">`;
                      } catch (_) {}
                    }
                    title = def ? def.name : 'Achievement';
                    const tierLabel = def?.tier ? def.tier.toUpperCase() : '';
                    detail = def ? `${tierLabel} · ${def.description}` : 'Achievement Unlocked';
                    badge = ev.xp > 0 ? `+${ev.xp} XP` : (tierLabel || 'UNLOCKED');
                    badgeCls = 'achievement';
                  } else if (ev.type === 'levelup') {
                    icon = '▲';
                    title = `Reached Level ${ev.to}`;
                    detail = ev.title || '';
                    badge = 'LEVEL UP';
                    badgeCls = 'levelup';
                  } else if (ev.type === 'xp') {
                    icon = '★';
                    title = XP_EVENT_LABELS[ev.reason] || 'Bonus';
                    detail = 'Experience Awarded';
                    badge = `+${ev.amount} XP`;
                    badgeCls = 'xp';
                  }

                  return `
                    <div class="progress-timeline-item">
                      <div class="timeline-marker">
                        <span class="timeline-icon">${icon}</span>
                        <div class="timeline-line"></div>
                      </div>
                      <div class="timeline-content">
                        <div class="timeline-top">
                          <span class="timeline-event-title">${title}</span>
                          ${relTime ? `<span class="timeline-timestamp">${relTime}</span>` : ''}
                        </div>
                        <div class="timeline-sub">
                          <span class="timeline-detail">${detail}</span>
                          ${badge ? `<span class="timeline-badge ${badgeCls}">${badge}</span>` : ''}
                        </div>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
              ${totalEvents > PROGRESS_LOG_COLLAPSED_COUNT
                ? `<button type="button" class="progress-log-toggle" data-progress-log-toggle>${expanded ? 'Show less' : `Show all (${totalEvents})`}</button>`
                : ''}`
          }
        </div>
      </div>
    `;
  }

  _renderProgressAchievements(state) {
    const unlocked = state?.achievements?.unlocked || (typeof gameProgress.getState === 'function' ? gameProgress.getState()?.achievements?.unlocked : {}) || {};
    const activeTheme = this._activeAchievementTheme || 'all';

    const getAchievementTitleStyle = (name) => {
      const len = (name || '').length;
      const fs = len > 23 ? '13px' : len > 18 ? '14px' : '15px';
      return `style="--ach-title-size: ${fs}; font-size: var(--ach-title-size, ${fs});"`;
    };

    const getAchievementDescStyle = (desc) => {
      const len = (desc || '').length;
      const fs = len > 72 ? '12px' : len > 45 ? '12.8px' : '13.5px';
      return `style="--ach-desc-size: ${fs}; font-size: var(--ach-desc-size, ${fs});"`;
    };

    const totalCount = ACHIEVEMENTS.length;
    const totalUnlocked = ACHIEVEMENTS.filter(a => !!unlocked[a.id]).length;

    const filteredAchievements = activeTheme === 'all'
      ? ACHIEVEMENTS
      : ACHIEVEMENTS.filter(a => a.theme === activeTheme);

    const themeMap = new Map((ACHIEVEMENT_THEMES || []).map(t => [t.id, t]));

    return `
      <div class="progress-achievements-container">
        <!-- 9 Themenwelten Filter Bar -->
        <div class="achievement-theme-filter-bar">
          <button type="button" class="achievement-theme-filter-btn ${activeTheme === 'all' ? 'active' : ''}" data-achievement-theme="all">
            <span class="theme-btn-label">All Themes</span>
            <span class="theme-btn-count">${totalUnlocked}/${totalCount}</span>
          </button>
          ${(ACHIEVEMENT_THEMES || []).map(theme => {
            const themeAchs = ACHIEVEMENTS.filter(a => a.theme === theme.id);
            const themeUnlocked = themeAchs.filter(a => !!unlocked[a.id]).length;
            const isThemeActive = activeTheme === theme.id;
            return `
              <button type="button" class="achievement-theme-filter-btn ${isThemeActive ? 'active' : ''}" data-achievement-theme="${theme.id}" title="${escapeHtml(theme.description)}">
                <span class="theme-btn-icon">${theme.icon}</span>
                <span class="theme-btn-label">${escapeHtml(theme.name)}</span>
                <span class="theme-btn-count">${themeUnlocked}/${themeAchs.length}</span>
              </button>
            `;
          }).join('')}
        </div>

        <div class="progress-achievement-grid">
          ${filteredAchievements.map(a => {
            const isUnlocked = !!unlocked[a.id];
            const iconUrl = buildAchievementIconBitmap(a.icon, 64).toDataURL();
            const xpLabel = a.xp > 0 ? `${a.xp} XP` : 'MAX LEVEL';
            const themeDef = themeMap.get(a.theme);
            const themeTagHtml = themeDef ? `${themeDef.icon} <span>${escapeHtml(themeDef.name)}</span>` : '';
            return `
              <div class="progress-achievement-card ${isUnlocked ? 'unlocked' : 'locked'}" data-tier="${a.tier}" data-theme="${a.theme || ''}">
                <div class="achievement-card-body">
                  ${themeTagHtml ? `<div class="achievement-card-theme-tag">${themeTagHtml}</div>` : ''}
                  <div class="achievement-icon-wrap">
                    <img src="${iconUrl}" alt="${a.name}" class="achievement-icon-img">
                  </div>
                  <div class="achievement-name" ${getAchievementTitleStyle(a.name)}>${a.name}</div>
                  <div class="achievement-desc" ${getAchievementDescStyle(a.description)}>${a.description}</div>
                </div>
                <div class="achievement-card-footer">
                  <span class="achievement-xp-badge">${isUnlocked ? '<span class="achievement-footer-check">✓</span> ' : ''}${xpLabel}</span>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  _renderProgressStats(state, avgGrade, avgGradeLetter, levelInfo) {
    const c = state?.combat || {};
    const matchesByMode = c.matchesByMode || { skirmish: 0, online: 0 };
    const skirmishMatches = matchesByMode.skirmish || 0;
    const onlineMatches = matchesByMode.online || 0;
    const totalMatches = skirmishMatches + onlineMatches;
    const wins = c.wins || 0;
    const losses = c.losses || 0;
    const lifetimeDamage = c.lifetimeDamage || 0;
    const formattedDamage = lifetimeDamage >= 1000 ? (lifetimeDamage / 1000).toFixed(1) + 'K' : lifetimeDamage.toLocaleString();
    const currentWinStreak = c.currentWinStreak || 0;
    const bestWinStreak = c.bestWinStreak || 0;
    const gradeCounts = c.gradeCounts || { 'S+': 0, 'S': 0, 'S-': 0, 'A+': 0, 'A': 0, 'B+': 0, 'B': 0, 'C': 0, 'D': 0 };

    // Player level
    const playerLevel = levelInfo?.level || (typeof gameProgress !== 'undefined' && gameProgress.getLevelProgress ? gameProgress.getLevelProgress().level : 1);

    // Combat Performance / Grade
    const gradedMatches = avgGrade?.gradedMatches || 0;
    const avgScore = gradedMatches > 0 ? avgGrade.average.toFixed(1) : '—';
    const evalGrade = (avgGradeLetter && avgGradeLetter !== '—') ? avgGradeLetter : '—';
    const evalScore = gradedMatches > 0 ? avgGrade.average : 0;
    const evalDashoffset = (263.9 * (1 - Math.min(100, Math.max(0, evalScore)) / 100)).toFixed(1);

    // Win/Loss Ratio and Dial (radius 42, circumference 263.9)
    const winRatePct = totalMatches > 0 ? Math.round((wins / totalMatches) * 100) : 0;
    const wlRatio = totalMatches > 0 ? (wins / Math.max(1, losses)).toFixed(2) : '0.00';
    const winDashoffset = (263.9 * (1 - winRatePct / 100)).toFixed(1);

    // Top Units (Actual MVP Units from player state, matched with actual unit sprites from CARDS_DATA)
    const mvpEntries = Object.entries(state?.unitMvpCounts || {})
      .filter(([_, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);

    const defaultUnitNames = ['Panzer', 'Marine', 'Shade'];
    const top3Units = [];
    for (let i = 0; i < 3; i++) {
      if (mvpEntries[i]) {
        top3Units.push({ name: mvpEntries[i][0], count: mvpEntries[i][1] });
      } else {
        const fallback = defaultUnitNames.find(d => !top3Units.some(u => u.name.toLowerCase() === d.toLowerCase())) || 'Marine';
        top3Units.push({ name: fallback, count: 0 });
      }
    }

    const rankPillStyles = [
      'background: #00e5ff; color: #000000;',
      'background: #38bdf8; color: #000000;',
      'background: #94a3b8; color: #000000;'
    ];

    const topUnitsHtml = top3Units.map((item, idx) => {
      const card = CARDS_DATA.find(cd => 
        cd.name.toLowerCase() === item.name.toLowerCase() || 
        cd.unitType?.toLowerCase() === item.name.toLowerCase()
      );
      const displayName = (card?.name || item.name).toUpperCase();
      const unitClass = card 
        ? `${card.race || 'Human'} ${card.caste || card.elementType || card.type || 'Unit'}`.toUpperCase()
        : 'COMBAT ASSET';
      const imageName = card?.images?.[0] || (item.name.toLowerCase() === 'panzer' ? 'Siege_Tank_1.png' : 'Marine_1.png');
      const spriteUrl = getCardThumbUrl(imageName, 'm');

      return `
        <div class="mw-unit-weapon-card">
          <div class="mw-unit-card-rank-header">
            <div class="mw-rank-num-pill" style="${rankPillStyles[idx]}">${idx + 1}</div>
            <div class="mw-unit-card-titles">
              <span class="mw-unit-main-title" title="${displayName}">${displayName}</span>
              <span class="mw-unit-class-sub">${unitClass}</span>
            </div>
          </div>
          <div class="mw-unit-visual-stage">
            <img src="${spriteUrl}" data-image="${imageName}" alt="${displayName}" class="mw-unit-art-img" onerror="this.style.display='none'">
          </div>
          <div class="mw-unit-tally-bottom">
            <span class="mw-unit-tally-label">CAREER MVPS</span>
            <span class="mw-unit-tally-num">${item.count}</span>
          </div>
        </div>
      `;
    }).join('');

    // Wins by Combat Rating (Horizontal Bar Chart)
    const ratingTiers = [
      { grade: 'S+ GRADE', key: 'S+', color: '#00e5ff' },
      { grade: 'S GRADE', key: 'S', color: '#ffcc00' },
      { grade: 'S- GRADE', key: 'S-', color: '#f59e0b' },
      { grade: 'A+ GRADE', key: 'A+', color: '#d946ef' },
      { grade: 'A GRADE', key: 'A', color: '#c044ff' },
      { grade: 'B+ GRADE', key: 'B+', color: '#06b6d4' },
      { grade: 'B GRADE', key: 'B', color: '#38bdf8' },
      { grade: 'C GRADE', key: 'C', color: '#64748b' },
      { grade: 'D GRADE', key: 'D', color: '#ef4444' }
    ];
    const maxGradeCount = Math.max(1, ...ratingTiers.map(t => gradeCounts[t.key] || 0));

    const ratingBarsHtml = ratingTiers.map(t => {
      const count = gradeCounts[t.key] || 0;
      const barPct = maxGradeCount > 0 ? Math.round((count / maxGradeCount) * 100) : 0;
      return `
        <div class="mw-chart-row">
          <span class="mw-chart-label">${t.grade}</span>
          <div class="mw-chart-bar-track">
            <div class="mw-chart-bar-fill" style="width: ${barPct}%; background: ${t.color};"></div>
          </div>
          <span class="mw-chart-val">${count}</span>
        </div>
      `;
    }).join('');

    const targetDrones = state?.targetDroneHits || 0;
    const dailyJackpots = state?.dailyJackpotDates ? state.dailyJackpotDates.length : 0;

    return `
      <div class="progress-career-combat-record mw-career-screen">
        
        <!-- LEFT COLUMN: MASTER CAREER SUMMARY (EXPANDED TO FULL HEIGHT) -->
        <div class="mw-career-col-left">

          <!-- 2x2 Primary Numbers Grid (Spacious & Enlarged) -->
          <div class="mw-stat-2x2-grid">
            <div class="mw-stat-item">
              <span class="mw-stat-val-big">${totalMatches}</span>
              <span class="mw-stat-label-muted">GAMES PLAYED</span>
            </div>
            <div class="mw-stat-item">
              <span class="mw-stat-val-big">${formattedDamage}</span>
              <span class="mw-stat-label-muted">TOTAL DAMAGE</span>
            </div>
            <div class="mw-stat-item">
              <span class="mw-stat-val-big">${avgScore}</span>
              <span class="mw-stat-label-muted">AVG PERFORMANCE</span>
            </div>
            <div class="mw-stat-item">
              <span class="mw-stat-val-big">LVL ${playerLevel}</span>
              <span class="mw-stat-label-muted">RANK TIER</span>
            </div>
          </div>

          <!-- Twin Circular Dials (Enlarged to 118px) -->
          <div class="mw-twin-dials-row">
            
            <!-- Dial 1: Win/Loss Ratio -->
            <div class="mw-dial-card">
              <div class="mw-dial-circle-wrap">
                <svg class="mw-dial-svg" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" class="mw-dial-track" />
                  <circle cx="50" cy="50" r="42" class="mw-dial-fill-green" style="stroke-dashoffset: ${winDashoffset};" />
                </svg>
                <div class="mw-dial-center-text">
                  <span class="mw-dial-num">${wlRatio}</span>
                  <span class="mw-dial-sub-tag">W/L RATIO</span>
                </div>
              </div>
              <div class="mw-dial-breakdown-row">
                <div class="mw-breakdown-item">
                  <span class="mw-breakdown-label">WINS</span>
                  <span class="mw-breakdown-val" style="color: #00ff88;">${wins}</span>
                </div>
                <div class="mw-breakdown-item">
                  <span class="mw-breakdown-label">LOSSES</span>
                  <span class="mw-breakdown-val" style="color: #ff3344;">${losses}</span>
                </div>
              </div>
            </div>

            <!-- Dial 2: Evaluation Grade -->
            <div class="mw-dial-card">
              <div class="mw-dial-circle-wrap">
                <svg class="mw-dial-svg" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" class="mw-dial-track" />
                  <circle cx="50" cy="50" r="42" class="mw-dial-fill-cyan" style="stroke-dashoffset: ${evalDashoffset};" />
                </svg>
                <div class="mw-dial-center-text">
                  <span class="mw-dial-num" style="color: #00e5ff;">${evalGrade}</span>
                  <span class="mw-dial-sub-tag">EVALUATION</span>
                </div>
              </div>
              <div class="mw-dial-breakdown-row">
                <div class="mw-breakdown-item">
                  <span class="mw-breakdown-label">SCORE</span>
                  <span class="mw-breakdown-val">${avgScore}</span>
                </div>
                <div class="mw-breakdown-item">
                  <span class="mw-breakdown-label">TIER</span>
                  <span class="mw-breakdown-val" style="color: #00e5ff; font-size: 13px;">${evalGrade !== '—' ? evalGrade + ' TIER' : 'UNRANKED'}</span>
                </div>
              </div>
            </div>

          </div>

        </div>

        <!-- RIGHT COLUMN: ASSETS & INDEPENDENT BOTTOM TILES -->
        <div class="mw-career-col-right">
          
          <!-- TOP COMBAT UNITS (Analogue to Modern Warfare Top Weapons) -->
          <div class="mw-loadout-panel">
            <div class="mw-top-units-row">
              ${topUnitsHtml}
            </div>
          </div>

          <!-- BOTTOM SECTION: THREE INDEPENDENT STANDALONE TILES (NO NESTING!) -->
          <div class="mw-bottom-tiles-row">
            
            <!-- Tile 1: Personal Bests (Decoupled from left column) -->
            <div class="mw-bottom-tile mw-bests-tile">
              <div class="mw-bests-standalone-card">
                <div class="mw-bests-tag">PERSONAL BESTS</div>
                <div class="mw-bests-standalone-grid">
                  <div class="mw-bests-standalone-item">
                    <span class="mw-bests-standalone-val" style="color: #00ff88;">${currentWinStreak}</span>
                    <span class="mw-bests-standalone-label">CURRENT STREAK</span>
                  </div>
                  <div class="mw-bests-standalone-item">
                    <span class="mw-bests-standalone-val" style="color: #ffcc00;">${bestWinStreak}</span>
                    <span class="mw-bests-standalone-label">HIGHEST STREAK</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tile 2: Theatre Operations (2x2 Square Grid) -->
            <div class="mw-bottom-tile mw-operations-tile">
              <div class="mw-operations-square-grid">
                <div class="mw-m-stat">
                  <span class="mw-m-stat-val">${skirmishMatches}</span>
                  <span class="mw-m-stat-label">SKIRMISH</span>
                </div>
                <div class="mw-m-stat">
                  <span class="mw-m-stat-val">${onlineMatches}</span>
                  <span class="mw-m-stat-label">ONLINE</span>
                </div>
                <div class="mw-m-stat">
                  <span class="mw-m-stat-val">${targetDrones}</span>
                  <span class="mw-m-stat-label">DRONES</span>
                </div>
                <div class="mw-m-stat">
                  <span class="mw-m-stat-val" style="color: #ffcc00;">${dailyJackpots}</span>
                  <span class="mw-m-stat-label">JACKPOTS</span>
                </div>
              </div>
            </div>

            <!-- Tile 3: Combat Rating Distribution -->
            <div class="mw-bottom-tile mw-ratings-tile">
              <div class="mw-bars-chart-block">
                ${ratingBarsHtml}
              </div>
            </div>

          </div>

        </div>

      </div>
    `;
  }

  _renderProgressLevelPath(currentLevel) {
    const catalog = getAvatarCatalog();
    const equippedId = gameProgress.getEquippedAvatarId() || catalog[0]?.id;
    return `
      <div class="progress-levelpath">
        <div class="levelpath-hint">Level milestones unlock Commander Avatars</div>
        <div class="levelpath-avatar-grid">
          ${catalog.map(a => {
            const unlocked = a.unlockLevel <= currentLevel;
            const isEquipped = equippedId === a.id;
            const thumbUrl = unlocked ? resolveAvatarImageUrl(a) : null;
            return `
              <div class="levelpath-avatar-card ${isEquipped ? 'equipped' : ''} ${!unlocked ? 'locked' : ''}" data-avatar-id="${a.id}" data-unlocked="${unlocked}">
                <span class="avatar-card-thumb">${thumbUrl ? `<img src="${thumbUrl}" alt="${a.unitName}">` : ''}</span>
                <span class="avatar-card-label">${a.unitName}</span>
                <span class="avatar-card-sub">Level ${a.unlockLevel}</span>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  _renderProgressLadder() {
    const auth = getAuth();
    const mmr = getMMR();
    const tier = getRankTier(mmr);
    const name = getDisplayName();

    return `
      <div class="progress-ladder-view">
        <div class="progress-ladder-switch-bar">
          <div class="ladder-mode-switch" id="showroom-ladder-mode-switch">
            <button type="button" class="ladder-tab-btn active" id="btn-showroom-ladder-1v1" data-mode="1v1">1v1 DUEL</button>
            <button type="button" class="ladder-tab-btn" id="btn-showroom-ladder-2v2" data-mode="2v2">2v2 DUO</button>
          </div>
        </div>
        <div class="ladder-hero-card" id="showroom-ladder-hero-card">
          <div class="ladder-hero-profile">
            <div class="ladder-hero-tier-icon" style="color: ${tier.color}; font-size: 28px;">${tier.icon}</div>
            <div class="ladder-hero-info">
              <div class="ladder-hero-name" style="font-size: 16px; font-weight: 700; color: #f8fafc;">${escapeHtml(name)}</div>
              <div class="ladder-hero-tier" style="color: ${tier.color}; font-size: 12px; margin-top: 2px;">${tier.name} · <span class="ladder-hero-mmr" style="color: #00f0ff; font-weight: 700;">${mmr} MMR</span></div>
            </div>
          </div>
          <div class="ladder-hero-status">
            <span class="ladder-status-badge ${auth ? 'status-registered' : 'status-guest'}">
              ${auth ? 'VERIFIED' : 'GUEST RECRUIT'}
            </span>
          </div>
        </div>

        <div class="ladder-table-container" id="showroom-ladder-table-wrap">
          <div class="ladder-loading-spinner" style="padding: 30px; text-align: center; color: #64748b; font-size: 13px;">
            Connecting to Central Command Leaderboard...
          </div>
        </div>
      </div>
    `;
  }

  async _loadLadderData(mode = this._activeLadderMode || '1v1') {
    this._activeLadderMode = mode;
    const tableWrap = this.stageContainer?.querySelector('#showroom-ladder-table-wrap');
    if (!tableWrap) return;

    const btn1v1 = this.stageContainer?.querySelector('#btn-showroom-ladder-1v1');
    const btn2v2 = this.stageContainer?.querySelector('#btn-showroom-ladder-2v2');
    btn1v1?.classList.toggle('active', mode === '1v1');
    btn2v2?.classList.toggle('active', mode === '2v2');

    const heroCard = this.stageContainer?.querySelector('#showroom-ladder-hero-card');
    const auth = getAuth();
    const mmr = getMMR(mode);
    const tier = getRankTier(mmr);
    const name = getDisplayName();

    if (heroCard) {
      heroCard.innerHTML = `
        <div class="ladder-hero-profile">
          <div class="ladder-hero-tier-icon" style="color: ${tier.color}; font-size: 28px;">${tier.icon}</div>
          <div class="ladder-hero-info">
            <div class="ladder-hero-name" style="font-size: 16px; font-weight: 700; color: #f8fafc;">${escapeHtml(name)}</div>
            <div class="ladder-hero-tier" style="color: ${tier.color}; font-size: 12px; margin-top: 2px;">${tier.name} · <span class="ladder-hero-mmr" style="color: #00f0ff; font-weight: 700;">${mmr} MMR</span></div>
          </div>
        </div>
        <div class="ladder-hero-status">
          <span class="ladder-status-badge ${auth ? 'status-registered' : 'status-guest'}">
            ${auth ? 'VERIFIED' : 'GUEST RECRUIT'}
          </span>
        </div>
      `;
    }

    const is2v2 = (mode === '2v2');
    const jsonPath = is2v2 ? `api/leaderboard_2v2.json?t=${Date.now()}` : `api/leaderboard.json?t=${Date.now()}`;
    const query = auth?.accountId ? (is2v2 ? `?mode=2v2&accountId=${encodeURIComponent(auth.accountId)}` : `?accountId=${encodeURIComponent(auth.accountId)}`) : (is2v2 ? '?mode=2v2' : '');

    try {
      const [boardRes, myRes] = await Promise.all([
        fetch(jsonPath),
        fetch(`api/leaderboard.php${query}`).catch(() => ({ ok: false }))
      ]);

      const board = (boardRes.ok ? await boardRes.json().catch(() => null) : null) || {};
      const myData = (myRes.ok ? await myRes.json().catch(() => null) : null) || {};
      const boardList = Array.isArray(board.leaderboard) ? board.leaderboard : [];
      const myList = Array.isArray(myData.leaderboard) ? myData.leaderboard : [];
      const leaderboard = myList.length >= boardList.length ? myList : boardList;
      const data = { leaderboard, myAccount: myData.myAccount };

      if (!tableWrap.isConnected) return;

      if (!Array.isArray(data.leaderboard) || data.leaderboard.length === 0) {
        tableWrap.innerHTML = `
          <div style="padding: 32px 20px; text-align: center;">
            <div style="font-family: 'Rajdhani', sans-serif; font-size: 15px; font-weight: 700; letter-spacing: 1.5px; color: #00f0ff; margin-bottom: 6px;">
              ${is2v2 ? '2v2 DUO SATELLITE ONLINE // LADDER STANDBY' : 'ARENA SATELLITE ONLINE // LADDER STANDBY'}
            </div>
            <div style="font-size: 12px; color: #94a3b8; line-height: 1.6;">
              Awaiting combat reports. Deploy into ${is2v2 ? '2v2 Duo Battles' : 'the Arena'} to claim Rank #1!
            </div>
          </div>
        `;
        return;
      }

      let rowsHtml = `
        <table class="showroom-ladder-table">
          <thead>
            <tr>
              <th class="rank-col">Rank</th>
              <th class="name-col">Commander</th>
              <th class="tier-col">Tier</th>
              <th class="mmr-col">MMR</th>
              <th class="record-col">W/L</th>
            </tr>
          </thead>
          <tbody>
      `;

      data.leaderboard.forEach((p, idx) => {
        const pTier = getRankTier(p.mmr);
        const isMe = auth?.accountId && p.id === auth.accountId;
        const bgClass = isMe ? 'is-me' : (idx % 2 === 0 ? 'even' : 'odd');
        const medal = p.rank === 1 ? getIconSvg('trophy', 12) + ' ' : (p.rank === 2 ? getIconSvg('rank_gold', 12) + ' ' : (p.rank === 3 ? getIconSvg('rank_cadet', 12) + ' ' : ''));
        rowsHtml += `
          <tr class="${bgClass}">
            <td class="rank-col ${p.rank <= 3 ? 'top-three' : ''}">${medal}${p.rank}</td>
            <td class="name-col ${isMe ? 'my-name' : ''}">${escapeHtml(p.displayName)}</td>
            <td class="tier-col" style="color: ${pTier.color};">${pTier.icon} ${pTier.name}</td>
            <td class="mmr-col">${p.mmr}</td>
            <td class="record-col">${p.wins}/${p.losses}</td>
          </tr>
        `;
      });

      rowsHtml += '</tbody></table>';
      tableWrap.innerHTML = rowsHtml;
    } catch (err) {
      if (tableWrap.isConnected) {
        tableWrap.innerHTML = '<div style="padding: 24px; text-align: center; color: #ff4455;">Connection to Central Command failed.</div>';
      }
    }
  }

  bindProgressTabEvents() {
    const subnavBtns = this.stageContainer.querySelectorAll('[data-progress-view]');
    subnavBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const view = btn.getAttribute('data-progress-view');
        if (view && view !== this._activeProgressView) {
          this._activeProgressView = view;
          this._progressLogExpanded = false;
          if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
          this.renderStage();
          this.scrollActiveProgressViewIntoView();
        }
      });
    });

    const logToggle = this.stageContainer.querySelector('[data-progress-log-toggle]');
    logToggle?.addEventListener('click', () => {
      this._progressLogExpanded = !this._progressLogExpanded;
      if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
      this.renderStage();
    });

    if (this._activeProgressView === 'achievements') {
      const themeBtns = this.stageContainer.querySelectorAll('[data-achievement-theme]');
      themeBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const theme = btn.getAttribute('data-achievement-theme');
          if (theme && theme !== this._activeAchievementTheme) {
            this._activeAchievementTheme = theme;
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
          }
        });
      });
    }

    if (this._activeProgressView === 'levelpath') {
      const avatarCards = this.stageContainer.querySelectorAll('.levelpath-avatar-card');
      avatarCards.forEach(card => {
        card.addEventListener('click', () => {
          const unlocked = card.getAttribute('data-unlocked');
          if (unlocked === 'false') return;
          const avatarId = card.getAttribute('data-avatar-id') || null;
          if (gameProgress.equipAvatar(avatarId)) {
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
          }
        });
      });
    }

    if (this._activeProgressView === 'ladder') {
      const btn1v1 = this.stageContainer.querySelector('#btn-showroom-ladder-1v1');
      const btn2v2 = this.stageContainer.querySelector('#btn-showroom-ladder-2v2');
      btn1v1?.addEventListener('click', () => {
        if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
        this._loadLadderData('1v1');
      });
      btn2v2?.addEventListener('click', () => {
        if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
        this._loadLadderData('2v2');
      });
      this._loadLadderData();
    }
  }

  _fitUnitCardNames() {
    requestAnimationFrame(() => {
      const nameEls = this.stageContainer?.querySelectorAll('.showroom-unit-card .unit-card-name');
      if (!nameEls) return;
      nameEls.forEach(el => {
        const parent = el.parentElement;
        if (!parent) return;
        const maxW = parent.clientWidth ? (parent.clientWidth - 8) : 96;
        let fontSize = parseFloat(window.getComputedStyle(el).fontSize) || 10;
        let guard = 0;
        while (el.scrollWidth > maxW && fontSize > 6.0 && guard < 10) {
          fontSize -= 0.4;
          el.style.fontSize = `${fontSize}px`;
          guard++;
        }
      });
    });
  }

  _playLockedDenial(swatch) {
    if (this.audio) {
      if (typeof this.audio.playError === 'function') {
        this.audio.playError();
      } else if (typeof this.audio.playDenied === 'function') {
        this.audio.playDenied();
      } else if (typeof this.audio.playBark === 'function') {
        this.audio.playBark('ui_error');
      }
    }
    if (swatch) {
      swatch.classList.remove('deny-shake');
      void swatch.offsetWidth;
      swatch.classList.add('deny-shake');
    }
  }

  bindStageEvents(currentUnit, testFireColorHex) {
    // Top Tabs (Units vs. Master vs. Progress)
    const tabBtns = this.stageContainer.querySelectorAll('.showroom-tab-btn');
    tabBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const nextTab = btn.getAttribute('data-tab');
        if (nextTab !== this._activeTab) this._progressLogExpanded = false;
        this._activeTab = nextTab;
        if (this.audio) (this.audio.playUiTabSwitch ? this.audio.playUiTabSwitch() : this.audio.playSample?.('mouseclick1.ogg'));
        this.renderStage();
        if (this._activeTab === 'misc') {
          this.startAnimationLoop();
          this.scrollActiveMiscCategoryIntoView();
        } else if (this._activeTab === 'progress') {
          this.stopAnimationLoop();
          this.scrollActiveProgressViewIntoView();
        } else if (this._activeTab === 'units') {
          this.startAnimationLoop();
          this.scrollActiveUnitIntoView();
        }
      });
    });

    const btnGotoSlots = document.getElementById('btn-empty-goto-slots');
    btnGotoSlots?.addEventListener('click', () => {
      this.close();
      hubNavigator.navigateTo('matrix');
    });

    if (this._activeTab === 'units') {
      this._fitUnitCardNames();
      if (this._units.length === 0) return;

      // Carousel Cards Selection
      const unitCards = this.stageContainer.querySelectorAll('.showroom-unit-card');
      unitCards.forEach(card => {
        card.addEventListener('click', (e) => {
          const idx = parseInt(card.getAttribute('data-index'), 10);
          if (e.target.closest('.badge-gold')) {
            const u = this._units[idx];
            if (u && inventory.hasSpecialCard(u.name, 'gold')) {
              this._activeUnitIndex = idx;
              inventory.toggleSpecialCard(u.name, 'gold');
              if (this.audio) this.audio.playBark?.('ui_click');
              this.renderStage();
              return;
            }
          }
          if (!isNaN(idx) && idx !== this._activeUnitIndex) {
            this._activeUnitIndex = idx;
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
            this.scrollActiveUnitIntoView();
          }
        });
      });

      // Gold Edition Toggle Button in Telemetry Header
      const btnToggleGold = document.getElementById('btn-toggle-gold');
      btnToggleGold?.addEventListener('click', (e) => {
        e.stopPropagation();
        const cardName = btnToggleGold.getAttribute('data-card-name') || currentUnit.name;
        inventory.toggleSpecialCard(cardName, 'gold');
        if (this.audio) this.audio.playBark?.('ui_click');
        this.renderStage();
      });

      // Direct Canvas Click / Tap: Manual Aiming + Interactive Weapon Test Fire
      const stageCanvas = document.getElementById('hangar-stage-canvas');
      const canvasWrap = document.getElementById('hangar-canvas-wrap');

      // Maps a mouse/touch client position to the canvas's internal drawing
      // coordinates (its width/height attributes, e.g. 840x380), independent
      // of how large the canvas is stretched on screen via CSS.
      const clientPointToCanvasCoords = (clientX, clientY) => {
        if (!stageCanvas) return null;
        const rect = stageCanvas.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return null;
        const x = (clientX - rect.left) * (stageCanvas.width / rect.width);
        const y = (clientY - rect.top) * (stageCanvas.height / rect.height);
        return { x, y };
      };

      const setAimFromEvent = (e) => {
        const point = e.touches?.[0] || e.changedTouches?.[0] || e;
        const coords = clientPointToCanvasCoords(point.clientX, point.clientY);
        if (!coords) return;
        this._aimTargetX = coords.x;
        this._aimTargetY = coords.y;
      };

      const onStageFire = (e) => {
        if (e && e.cancelable && e.type === 'touchstart') {
          e.preventDefault();
        }
        setAimFromEvent(e);
        this.executeTestFire(testFireColorHex, currentUnit);
      };
      stageCanvas?.addEventListener('mousemove', setAimFromEvent);
      stageCanvas?.addEventListener('touchmove', setAimFromEvent, { passive: true });
      stageCanvas?.addEventListener('click', onStageFire);
      stageCanvas?.addEventListener('touchstart', onStageFire, { passive: false });
      canvasWrap?.addEventListener('click', (e) => {
        if (e.target === canvasWrap) onStageFire(e);
      });

      // Unit Cosmetic Mode Tabs (bullet_color vs unit_aura vs rocket_trail)
      const modeBtns = this.stageContainer.querySelectorAll('.workbench-mode-btn');
      modeBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
          this._activeUnitCosmetic = btn.getAttribute('data-unit-cosmetic');
          if (this.audio) this.audio.playBark?.('ui_click');
          this.renderStage();
        });
      });

      // Option 3A: Instant-Feedback Swatch Click
      const swatches = this.stageContainer.querySelectorAll('.color-swatch-card[data-style-id]');
      swatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          const styleId = swatch.getAttribute('data-style-id');
          const isUnlocked = swatch.getAttribute('data-unlocked') === 'true' || styleId === 'default';

          if (!isUnlocked) {
            this._playLockedDenial(swatch);
            return;
          }

          const val = styleId === 'default' ? null : styleId;
          let newlyEquippedHex = null;
          if (val) {
            const st = REEL_3_STYLES.find(s => s.id === val);
            newlyEquippedHex = st?.hex || null;
          }

          if (this._activeUnitCosmetic === 'bullet_color') {
            inventory.equipBulletColor(currentUnit.name, val);
          } else if (this._activeUnitCosmetic === 'unit_aura') {
            inventory.equipUnitAura(currentUnit.name, val);
          } else {
            inventory.equipRocketTrail(currentUnit.name, val);
          }

          // Option 3A: Immediate test fire with visual & audio feedback!
          const isPreserverUnit = (currentUnit.name === 'Preserver Lock' || currentUnit.unitType === 'PreserverLock');
          const defaultFireHex = (currentUnit.name === 'Looting Vulture' || currentUnit.unitType === 'LootingVulture')
            ? '#fde047'
            : (isPreserverUnit ? '#7dd3fc' : (currentUnit.race === 'Sul' ? '#d946ef' : '#ffdd44'));
          const fireHex = newlyEquippedHex || (this._activeUnitCosmetic === 'rocket_trail' ? '#f97316' : (this._activeUnitCosmetic === 'unit_aura' ? '#00f0ff' : defaultFireHex));
          this.executeTestFire(fireHex, currentUnit);
        });
      });

      // Switch to Rocket Unit Buttons
      const jumpBtns = this.stageContainer.querySelectorAll('.btn-switch-rocket-unit');
      jumpBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const targetName = btn.getAttribute('data-unit-name');
          const targetIdx = this._units.findIndex(u => u.name === targetName);
          if (targetIdx !== -1) {
            this._activeUnitIndex = targetIdx;
            this._activeUnitCosmetic = 'rocket_trail';
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
            this.scrollActiveUnitIntoView();
          }
        });
      });

    } else if (this._activeTab === 'misc') {
      // Category card selection from horizontal slider
      const catCards = this.stageContainer.querySelectorAll('.misc-category-card[data-misc-cat]');
      catCards.forEach(card => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('[data-preview-taunt-id]')) return;
          const cat = card.getAttribute('data-misc-cat');
          if (cat && cat !== this._activeMiscCategory) {
            this._activeMiscCategory = cat;
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
            this.scrollActiveMiscCategoryIntoView();
          }
        });
      });

      // Mobile swipe relies on native inertia touch scrolling with CSS scroll-snap
      // (.showroom-misc-slider-wrap), matching the Units carousel without disruptive DOM re-rendering.

      // Misc Swatches
      const miscSwatches = this.stageContainer.querySelectorAll('.color-swatch-card[data-misc-style-id]');
      miscSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          const styleId = swatch.getAttribute('data-misc-style-id');
          const isUnlocked = swatch.getAttribute('data-unlocked') === 'true' || styleId === 'default';

          if (!isUnlocked) {
            this._playLockedDenial(swatch);
            return;
          }

          const val = styleId === 'default' ? null : styleId;
          if (this._activeMiscCategory === 'frames') {
            inventory.equipFrame(val);
          } else if (this._activeMiscCategory === 'hp_bar_color') {
            inventory.equipHpBarColor(val);
          } else if (this._activeMiscCategory === 'avatar_border') {
            inventory.equipAvatarBorder(val);
          } else if (this._activeMiscCategory === 'tray_color') {
            inventory.equipTrayColor(val);
          } else if (this._activeMiscCategory === 'deck_color') {
            inventory.equipDeckColor(val);
          }
          if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
          this.renderStage();
        });
      });

      // Profile Artworks collection: equip clicked unlocked artwork
      const profileSwatches = this.stageContainer.querySelectorAll('.color-swatch-card[data-profile-artwork-name]');
      profileSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          const cardName = swatch.getAttribute('data-profile-artwork-name');
          const isUnlocked = swatch.getAttribute('data-unlocked') === 'true';
          if (!isUnlocked) {
            this._playLockedDenial(swatch);
            return;
          }

          if (inventory.equipProfileArtwork(cardName)) {
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
          }
        });
      });

      // Deckbuilder Chassis collection: equip clicked unlocked theme (Cyber Glass vs Classic Steel)
      const deckbuilderSwatches = this.stageContainer.querySelectorAll('.color-swatch-card[data-deckbuilder-theme-id]');
      deckbuilderSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          const themeId = swatch.getAttribute('data-deckbuilder-theme-id');
          const isUnlocked = swatch.getAttribute('data-unlocked') === 'true';
          if (!isUnlocked) {
            this._playLockedDenial(swatch);
            return;
          }

          if (inventory.equipDeckbuilderTheme(themeId)) {
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
          }
        });
      });

      // Cards In-Game Chassis collection: equip clicked unlocked theme ("Cards")
      const cardsSwatches = this.stageContainer.querySelectorAll('.color-swatch-card[data-card-theme-id]');
      cardsSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          const themeId = swatch.getAttribute('data-card-theme-id');
          const isUnlocked = swatch.getAttribute('data-unlocked') === 'true';
          if (!isUnlocked) {
            this._playLockedDenial(swatch);
            return;
          }

          if (inventory.equipCardTheme(themeId)) {
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
          }
        });
      });

      // Deck Icon collection: equip clicked unlocked icon
      const deckIconSwatches = this.stageContainer.querySelectorAll('.color-swatch-card[data-deck-icon-id]');
      deckIconSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          const iconId = swatch.getAttribute('data-deck-icon-id');
          const isUnlocked = swatch.getAttribute('data-unlocked') === 'true';
          if (!isUnlocked) {
            this._playLockedDenial(swatch);
            return;
          }

          if (inventory.equipDeckIcon(iconId)) {
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
          }
        });
      });

      // Taunt Collection: multiselect toggle (equip/unequip), not the
      // single-swatch-equip pattern above - a taunt can be un-equipped by
      // clicking it again, and setEquippedTaunts() enforces the 1..
      // MAX_EQUIPPED_TAUNTS bounds so a stray click can never leave the
      // player with zero equipped taunts or silently drop one past the cap.
      const tauntSwatches = this.stageContainer.querySelectorAll('.color-swatch-card[data-taunt-id]');
      tauntSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          const tauntId = swatch.getAttribute('data-taunt-id');
          const isUnlocked = swatch.getAttribute('data-unlocked') === 'true';
          if (!isUnlocked) {
            this._playLockedDenial(swatch);
            return;
          }

          const current = inventory.getEquippedTaunts();
          const isEquipped = current.includes(tauntId);
          let next;
          if (isEquipped) {
            if (current.length <= 1) return; // keep at least 1 equipped
            next = current.filter(id => id !== tauntId);
          } else {
            if (current.length >= MAX_EQUIPPED_TAUNTS) return; // loadout full
            next = [...current, tauntId];
          }

          if (inventory.setEquippedTaunts(next)) {
            this.audio?.playTauntBlip?.(tauntId);
            this.renderStage();
          }
        });
      });

      // Loadout slot preview: replay a currently-equipped taunt's sound
      const tauntPreviewBtns = this.stageContainer.querySelectorAll('.taunt-loadout-slot[data-preview-taunt-id]');
      tauntPreviewBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const tauntId = btn.getAttribute('data-preview-taunt-id');
          this.audio?.playTauntBlip?.(tauntId);
        });
      });

      // Avatar selection (Player > Avatar)
      const avatarCards = this.stageContainer.querySelectorAll('.color-swatch-card[data-avatar-id]');
      avatarCards.forEach(card => {
        card.addEventListener('click', () => {
          const unlocked = card.getAttribute('data-unlocked');
          if (unlocked === 'false') {
            this._playLockedDenial(card);
            return;
          }
          const avatarId = card.getAttribute('data-avatar-id') || null;
          if (gameProgress.equipAvatar(avatarId)) {
            if (this.audio) (this.audio.playArtworkSelect ? this.audio.playArtworkSelect() : this.audio.playBark?.('ui_click'));
            this.renderStage();
          }
        });
      });
    } else if (this._activeTab === 'progress') {
      this.bindProgressTabEvents();
    }
  }

  triggerCurrentTestFire() {
    const currentUnit = this._units[this._activeUnitIndex];
    if (!currentUnit || !this.unitCanAttack(currentUnit)) return;
    const hasRocketTrail = isRocketTrailUnit(currentUnit.name);
    const equippedBulletStyleId = inventory.getEquippedBulletColor(currentUnit.name);
    const equippedBulletStyle = REEL_3_STYLES.find(s => s.id === equippedBulletStyleId) || null;
    const isLootingVultureUnit = (currentUnit.name === 'Looting Vulture' || currentUnit.unitType === 'LootingVulture');
    const isPreserverLockUnit = (currentUnit.name === 'Preserver Lock' || currentUnit.unitType === 'PreserverLock');
    const activeBulletColorHex = equippedBulletStyle?.hex || (
      isLootingVultureUnit ? '#fde047' : (
        isPreserverLockUnit ? '#7dd3fc' : (
          currentUnit.race === 'Sul' ? '#d946ef' : '#ffdd44'
        )
      )
    );

    const equippedTrailStyleId = inventory.getEquippedRocketTrail(currentUnit.name);
    const equippedTrailStyle = REEL_3_STYLES.find(s => s.id === equippedTrailStyleId) || null;
    const activeTrailColorHex = equippedTrailStyle?.hex || '#f97316';

    const testFireColorHex = (this._activeUnitCosmetic === 'rocket_trail' && hasRocketTrail)
      ? activeTrailColorHex
      : activeBulletColorHex;

    this.executeTestFire(testFireColorHex, currentUnit);
  }

  executeTestFire(colorHex, targetUnit = null) {
    const currentUnit = targetUnit || this._units[this._activeUnitIndex] || this._units[0];
    if (!currentUnit || !this.unitCanAttack(currentUnit)) return;

    const unitName = currentUnit.name || '';
    const unitType = currentUnit.unitType || currentUnit.name;

    const isTesla = (unitName.includes('Tesla') || unitType.includes('Tesla'));
    const isHyperTesla = (unitName === 'Hyper Tesla Walker' || unitType === 'HyperTeslaWalker');
    const isShade = (unitName.includes('Shade') || unitType.includes('Shade'));
    const isDefectiveLoaf = (unitName === 'Defective Loaf' || unitType === 'DefectiveLoaf');
    const isFlame = (unitName === 'Devil Patrol' || unitType === 'DevilPatrol');
    const isRocketship = (unitName === 'Rocketship' || unitType === 'Rocketship');
    const isOrchestra = (unitName === 'Orchestra' || unitType === 'Orchestra');
    const isApostate = (unitName === 'Apostate Loaf' || unitType === 'ApostateLoaf');
    const isPiranha = (unitName === 'A9 Piranha' || unitType === 'A9Piranha');
    const isA9Grizzly = (unitName === 'A9 Grizzly' || unitType === 'A9Grizzly');
    const isA9Pigeon = (unitName === 'A9 Pigeon' || unitType === 'A9Pigeon');
    const isDelvingPillar = (unitName === 'Delving Pillar' || unitType === 'DelvingPillar');
    const isHarvestingPillar = (unitName === 'Harvesting Pillar' || unitType === 'HarvestingPillar');
    const isHyperDevilPatrol = (unitName === 'Hyper Devil Patrol' || unitType === 'HyperDevilPatrol');
    const isHyperPanzer = (unitName === 'Hyper Panzer' || unitType === 'HyperPanzer');
    const isGrenade = (unitName === 'Panzer' || unitType === 'Panzer' || unitType === 'DeployableCannon' || unitName === 'Deployable Cannon' || isHyperDevilPatrol || isHyperPanzer);
    const isLootingVulture = (unitName === 'Looting Vulture' || unitType === 'LootingVulture' || unitType === 'Looting_Vulture');
    const isEnforcerVulture = (unitName === 'Enforcer Vulture' || unitType === 'EnforcerVulture' || unitType === 'Enforcer_Vulture');
    const isProcessingPillar = (unitName === 'Processing Pillar' || unitType === 'ProcessingPillar' || unitType === 'Processing_Pillar');
    const isSentinalLock = (unitName === 'Sentinal Lock' || unitType === 'SentinalLock' || unitType === 'Sentinal_Lock');
    const isPulp = (unitName === 'Pulp' || unitType === 'Pulp');
    const isStickman = (unitName === 'Stickman' || unitType === 'Stickman');
    const isHotHand = (unitName === 'Hot Hand' || unitType === 'HotHand' || unitType === 'Hot Hand');
    const isPinkBeret = (unitName === 'Pink Beret' || unitType === 'PinkBeret' || unitType === 'Pink_Beret');
    const isClaimingPillar = (unitName === 'Claiming Pillar' || unitType === 'ClaimingPillar' || unitType === 'Claiming_Pillar');
    const isPreserverLock = (unitName === 'Preserver Lock' || unitType === 'PreserverLock' || unitType === 'Preserver_Lock');
    const isBastionLock = (unitName === 'Bastion Lock' || unitType === 'BastionLock' || unitType === 'Bastion_Lock');
    const isMonitorLock = (unitName === 'Monitor Lock' || unitType === 'MonitorLock' || unitType === 'Monitor_Lock');
    const isParasiteVulture = (unitName === 'Parasite Vulture' || unitType === 'ParasiteVulture' || unitType === 'Parasite_Vulture');
    const isAntiShade = (unitName === 'Anti Shade' || unitType === 'AntiShade' || unitType === 'Anti_Shade');
    const isForgottenLoaf = (unitName === 'Forgotten Loaf' || unitType === 'ForgottenLoaf');
    const isHacker = (unitName === 'Hacker' || unitType === 'Hacker');

    // Sound Trigger matching in-game weapon profiles
    if (this.audio) {
      if (isTesla) {
        const isHyperTesla = (unitName === 'Hyper Tesla Walker' || unitType === 'HyperTeslaWalker');
        if (isHyperTesla && this.audio.playHyperTesla) this.audio.playHyperTesla();
        else if (this.audio.playTesla) this.audio.playTesla();
        else this.audio.playLaser?.();
      } else if (isDefectiveLoaf) {
        this.audio.playBark?.('Sul', 'spawn');
      } else if (isApostate) {
        if (this.audio.playSporeLob) this.audio.playSporeLob();
        else this.audio.playBark?.('Sul', 'spawn');
      } else if (isLootingVulture) {
        if (this.audio.playLootingApexBeam) this.audio.playLootingApexBeam({ x: 0 });
        else this.audio.playLaser?.();
      } else if (isEnforcerVulture) {
        if (this.audio.playVultureBallLaunch) this.audio.playVultureBallLaunch();
        else if (this.audio.playGauss) this.audio.playGauss();
        else this.audio.playLaser?.();
      } else if (isProcessingPillar) {
        if (this.audio.playProcessingPillar) this.audio.playProcessingPillar({ durationMs: 1400 });
        else this.audio.playLaser?.();
      } else if (isPreserverLock) {
        if (this.audio.playPreserverBeamPulse) this.audio.playPreserverBeamPulse();
        else this.audio.playLaser?.();
      } else if (isBastionLock) {
        if (this.audio.playBastionShot) this.audio.playBastionShot({ x: 0 });
        else this.audio.playGauss?.();
      } else if (isMonitorLock) {
        if (this.audio.playMonitorLockSnap) this.audio.playMonitorLockSnap({ x: 0 });
        else if (this.audio.playUnitSound) this.audio.playUnitSound('MonitorLock', 'attack');
        else this.audio.playBark?.('Sul', 'spawn');
      } else if (isAntiShade) {
        if (this.audio.playUnitSound) this.audio.playUnitSound('AntiShade', 'attack');
        else this.audio.playGauss?.();
      } else if (isHarvestingPillar) {
        if (this.audio.playHarvestingTether) this.audio.playHarvestingTether({ x: 0 });
        else if (this.audio.playUnitSound) this.audio.playUnitSound('HarvestingPillar', 'attack');
        else this.audio.playLaser?.();
      } else if (isSentinalLock) {
        if (this.audio.playSentinalSpit) this.audio.playSentinalSpit({ x: 0 });
        else this.audio.playGauss?.();
      } else if (isFlame) {
        this.audio.playExplosion?.('small');
      } else if (isRocketship || isOrchestra || isDelvingPillar) {
        this.audio.playRocketLaunch ? this.audio.playRocketLaunch() : this.audio.playExplosion?.('small');
      } else if (isGrenade) {
        if ((unitName === 'Panzer' || unitType === 'Panzer' || isHyperPanzer) && this.audio.playPanzerCannon) {
          this.audio.playPanzerCannon();
        } else if (isHyperDevilPatrol && this.audio.playHyperDevilShot) {
          this.audio.playHyperDevilShot();
        } else {
          this.audio.playExplosion?.('small');
        }
      } else if (isShade) {
        if (this.audio.playSuppressedShot) this.audio.playSuppressedShot();
        else this.audio.playGauss?.() || this.audio.playLaser?.();
      } else if (isPiranha) {
        this.audio.playGauss?.();
      } else if (isA9Pigeon) {
        if (this.audio.playA9PigeonShot) this.audio.playA9PigeonShot();
        else this.audio.playGauss?.();
      } else if (isPulp) {
        if (this.audio.playPulpAttack) this.audio.playPulpAttack();
        else this.audio.playExplosion?.('small');
      } else if (isStickman) {
        if (this.audio.playStickmanAttack) this.audio.playStickmanAttack();
        else if (this.audio.playPanzerCannon) this.audio.playPanzerCannon();
        else this.audio.playExplosion?.('heavy');
      } else if (isHotHand) {
        if (this.audio.playUnitSound) this.audio.playUnitSound('Hot Hand', 'attack');
        else this.audio.playGauss?.() || this.audio.playLaser?.();
      } else if (isPinkBeret) {
        const isRocket = (this._activeUnitCosmetic === 'rocket_trail');
        if (isRocket) {
          if (this.audio?.playPinkBeretRocketLaunch) this.audio.playPinkBeretRocketLaunch();
          else if (this.audio?.playRocketLaunch) this.audio.playRocketLaunch('heavy', { unitType: 'PinkBeret' });
          else this.audio?.playExplosion?.('small');
        } else {
          if (this.audio?.playPinkBeretPistol) this.audio.playPinkBeretPistol();
          else if (this.audio?.playGauss) this.audio.playGauss();
          else this.audio?.playLaser?.();
        }
      } else if (isHacker) {
        if (this.audio.playHackerHarpoon) this.audio.playHackerHarpoon();
        else this.audio.playLaser?.();
      } else if (isForgottenLoaf) {
        this.audio.playExplosion?.('heavy');
      } else {
        this.audio.playGauss?.() || this.audio.playLaser?.();
      }
    }

    // Trigger Damped Recoil on Sprite
    const now = performance.now();
    this._recoilStartTime = now;
    if (isHarvestingPillar) {
      this._harvestingHitTriggered = false;
    }
    this._recoilDuration = isTesla ? 260
      : isDefectiveLoaf ? 360
      : isApostate ? 380
      : isLootingVulture ? 240
      : isEnforcerVulture ? 240
      : isProcessingPillar ? 340
      : isPreserverLock ? 240
      : isBastionLock ? 140
      : isMonitorLock ? 200
      : isAntiShade ? 160
      : isHarvestingPillar ? 560
      : isSentinalLock ? 200
      : isStickman ? 320
      : isPulp ? 240
      : isHotHand ? 160
      : isPinkBeret ? (this._activeUnitCosmetic === 'rocket_trail' ? 300 : 180)
      : isHacker ? 180
      : isFlame ? 300
      : isRocketship ? 320
      : isOrchestra ? 340
      : isDelvingPillar ? 300
      : isGrenade ? 280
      : isPiranha ? 180
      : isA9Pigeon ? 160
      : 200;

    // Spawn Projectile on Unified Canvas Stage (only for shooting-capable units)
    if (this.unitCanShoot(currentUnit)) {
      const trailStyleId = inventory.getEquippedRocketTrail(currentUnit.name);
      const trailStyle = REEL_3_STYLES.find(s => s.id === trailStyleId);
      const trailHex = trailStyle?.hex || (currentUnit.race === 'Sul' ? '#c084fc' : '#f97316');

      // Default bullet color resolution: Apostate default is toxic green, Delving Pillar is plasma purple, Looting Vulture is solar gold, Enforcer Vulture is bio crimson, Processing Pillar is cyan plasma, Sentinal Lock is toxic glob
      let effectiveColorHex = colorHex;
      const equippedBulletStyleId = inventory.getEquippedBulletColor(currentUnit.name);
      if (!equippedBulletStyleId) {
        if (isApostate) effectiveColorHex = '#84cc16';
        else if (isPulp) effectiveColorHex = '#d946ef';
        else if (isDelvingPillar) effectiveColorHex = '#c084fc';
        else if (isLootingVulture) effectiveColorHex = '#fde047';
        else if (isEnforcerVulture) effectiveColorHex = '#ff0033';
        else if (isProcessingPillar) effectiveColorHex = '#38bdf8';
        else if (isBastionLock) effectiveColorHex = '#38bdf8';
        else if (isSentinalLock) effectiveColorHex = '#eaff00';
        else if (isPreserverLock) effectiveColorHex = '#7dd3fc';
      }

      const flightDuration = isApostate ? 480
        : isPulp ? 480
        : isEnforcerVulture ? 520
        : isOrchestra ? 460
        : isProcessingPillar ? 420
        : isPreserverLock ? 240
        : isBastionLock ? 160
        : isSentinalLock ? 320
        : isRocketship ? 320
        : isDelvingPillar ? 320
        : isGrenade ? 280
        : isTesla ? 240
        : isDefectiveLoaf ? 360
        : (isPinkBeret && this._activeUnitCosmetic === 'rocket_trail') ? 340
        : isLootingVulture ? 220
        : isPiranha ? 190
        : isA9Pigeon ? 170
        : 200;

      this._projectiles.push({
        startTime: now,
        duration: flightDuration,
        colorHex: effectiveColorHex,
        trailHex: trailHex,
        isTesla,
        isHyperTesla,
        isShade,
        isDefectiveLoaf,
        isFlame,
        isRocketship,
        isOrchestra,
        isApostate,
        isPulp,
        isPiranha,
        isA9Grizzly,
        isA9Pigeon,
        isDelvingPillar,
        isHarvestingPillar,
        isGrenade,
        isHyperDevilPatrol,
        isLootingVulture,
        isEnforcerVulture,
        isProcessingPillar,
        isPreserverLock,
        isBastionLock,
        isSentinalLock,
        isStickman,
        isPinkBeret,
        isPinkBeretRocket: (isPinkBeret && this._activeUnitCosmetic === 'rocket_trail'),
        unitName: currentUnit.name,
        unitType: currentUnit.unitType || currentUnit.name
      });
    }
  }

  drawStageFrame(now) {
    if (!this._stageCanvas || !this._stageCanvas.isConnected) {
      this._stageCanvas = document.getElementById('hangar-stage-canvas');
      this._stageCtx = this._stageCanvas?.getContext('2d');
      this._stageRectDirty = true;
    }
    const canvas = this._stageCanvas;
    if (!canvas) return;
    const ctx = this._stageCtx;
    if (!ctx) return;

    if (this._stageRectDirty || !this._stageCachedW) {
      this._stageRectDirty = false;
      const rect = canvas.getBoundingClientRect();
      const isDesktop = (typeof window !== 'undefined') && (window.innerWidth > 768);
      let targetW = isDesktop && rect.width > 0 ? Math.round(rect.width) : 840;
      let targetH = isDesktop && rect.height > 0 ? Math.round(rect.height) : 380;
      const isMobileLandscape = (typeof window !== 'undefined') && (window.innerHeight <= 500 && window.innerWidth > window.innerHeight);
      if (isMobileLandscape && rect.width > 0 && rect.height > 0) {
        targetW = Math.round(rect.width);
        targetH = Math.round(rect.height);
      }
      this._stageCachedW = targetW;
      this._stageCachedH = targetH;
      if (targetW > 0 && targetH > 0 && (canvas.width !== targetW || canvas.height !== targetH)) {
        canvas.width = targetW;
        canvas.height = targetH;
      }
    }

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const currentUnit = this._units[this._activeUnitIndex];
    if (!currentUnit) return;

    const isMobileStage = (typeof window !== 'undefined') && (window.innerWidth <= 768 || window.innerHeight <= 500);
    const isLowQuality = (this.game?.performanceManager?.tier === 'low') || isMobileStage;

    // ==========================================
    // LAYER 1: CYBER HANGAR PERSPECTIVE GRID & AMBIENT GLOW
    // ==========================================
    ctx.save();

    // Soft radial ambient light cone from ceiling (cached per canvas size)
    const spotX = w * 0.32;
    const spotY = h * 0.65;
    if (!this._stageSpotGrad || this._stageSpotW !== w || this._stageSpotH !== h) {
      this._stageSpotGrad = ctx.createRadialGradient(spotX, spotY, 20, spotX, spotY, Math.max(260, w * 0.32));
      this._stageSpotGrad.addColorStop(0, 'rgba(14, 165, 233, 0.12)');
      this._stageSpotGrad.addColorStop(0.6, 'rgba(15, 23, 42, 0.06)');
      this._stageSpotGrad.addColorStop(1, 'rgba(5, 8, 15, 0)');
      this._stageSpotW = w;
      this._stageSpotH = h;
    }
    ctx.fillStyle = this._stageSpotGrad;
    ctx.fillRect(0, 0, w, h);

    // Perspective floor grid lines
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.14)';
    ctx.lineWidth = 1;
    const vanishX = w * 0.45;
    const vanishY = -80;
    const floorY = h * 0.68;

    // Longitudinal grid lines (single batched path spanning dynamic canvas width)
    ctx.beginPath();
    const minGridI = Math.floor(-vanishX / 75) - 3;
    const maxGridI = Math.ceil((w - vanishX) / 75) + 3;
    for (let i = minGridI; i <= maxGridI; i++) {
      const bottomX = vanishX + i * 75;
      ctx.moveTo(vanishX, vanishY);
      ctx.lineTo(bottomX, h);
    }
    ctx.stroke();

    // Transverse horizon lines with fading alpha
    for (let y = floorY; y <= h; y += 22) {
      const alpha = ((y - floorY) / (h - floorY)) * 0.22;
      ctx.strokeStyle = `rgba(56, 189, 248, ${alpha})`;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.restore();

    // ==========================================
    // LAYER 2: 3D HOLOGRAPHIC PEDESTAL (under Sprite)
    // ==========================================
    const isCompactStage = h < 260;
    const stageScale = Math.min(1.0, Math.max(0.52, (h / 380) * 1.25));
    const pedestalX = w * 0.30;
    const pedestalY = isCompactStage ? Math.round(h * 0.76) : (h * 0.72);

    const equippedAuraStyleId = inventory.getEquippedUnitAura(currentUnit.name);
    const equippedAuraStyle = REEL_3_STYLES.find(s => s.id === equippedAuraStyleId) || null;
    const activeAuraColorHex = equippedAuraStyle?.hex || null;

    const equippedBulletStyleId = inventory.getEquippedBulletColor(currentUnit.name);
    const equippedBulletStyle = REEL_3_STYLES.find(s => s.id === equippedBulletStyleId) || null;
    const isLootingVultureUnit = (currentUnit.name === 'Looting Vulture' || currentUnit.unitType === 'LootingVulture');
    const isPreserverLockUnit = (currentUnit.name === 'Preserver Lock' || currentUnit.unitType === 'PreserverLock');
    const activeBulletColorHex = equippedBulletStyle?.hex || (
      isLootingVultureUnit ? '#fde047' : (
        isPreserverLockUnit ? '#7dd3fc' : (
          currentUnit.race === 'Sul' ? '#d946ef' : '#00f0ff'
        )
      )
    );

    const equippedTrailStyleId = inventory.getEquippedRocketTrail(currentUnit.name);
    const equippedTrailStyle = REEL_3_STYLES.find(s => s.id === equippedTrailStyleId) || null;
    const activeTrailColorHex = equippedTrailStyle?.hex || '#f97316';

    ctx.save();
    // Outer glowing ellipse ring (zero shadowBlur dual-stroke neon glow)
    const auraHue = activeAuraColorHex || '#38bdf8';
    if (!isLowQuality) {
      ctx.save();
      ctx.globalAlpha = 0.28;
      ctx.strokeStyle = auraHue;
      ctx.lineWidth = Math.max(3.5, 6.0 * stageScale);
      ctx.beginPath();
      ctx.ellipse(pedestalX, pedestalY, 115 * stageScale, 36 * stageScale, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
    ctx.strokeStyle = auraHue;
    ctx.lineWidth = Math.max(1.5, 2.2 * stageScale);
    ctx.beginPath();
    ctx.ellipse(pedestalX, pedestalY, 115 * stageScale, 36 * stageScale, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Inner concentric pulsating core
    const pulseScale = 0.95 + Math.sin(now * 0.004) * 0.05;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = Math.max(1.0, 1.2 * stageScale);
    ctx.beginPath();
    ctx.ellipse(pedestalX, pedestalY, (80 * stageScale) * pulseScale, (24 * stageScale) * pulseScale, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Semi-transparent pedestal floor fill
    ctx.fillStyle = activeAuraColorHex ? `${activeAuraColorHex}22` : 'rgba(56, 189, 248, 0.08)';
    ctx.beginPath();
    ctx.ellipse(pedestalX, pedestalY, 112 * stageScale, 34 * stageScale, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // ==========================================
    // LAYER 3: LIVE PROCEDURAL SPRITE & AURA RENDERING
    // ==========================================
    ctx.save();
    ctx.imageSmoothingEnabled = false;

    // Recoil Spring Calculation
    let recoilOffsetX = 0;
    const elapsedRecoil = now - this._recoilStartTime;
    if (elapsedRecoil >= 0 && elapsedRecoil < this._recoilDuration) {
      const p = elapsedRecoil / this._recoilDuration;
      recoilOffsetX = dampedSpringRecoil(p) * (-16 * stageScale); // kick backwards
    }

    // Continuous Walk-Cycle (always walking, no idle freeze)
    const walkPhase = (now * 0.0075) % (Math.PI * 2);
    const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);

    const spriteDisplaySize = Math.round(160 * stageScale);
    const spriteX = pedestalX - spriteDisplaySize / 2;
    const spriteY = pedestalY - spriteDisplaySize + Math.round(18 * stageScale);

    const unitName = currentUnit.name || '';
    const unitType = currentUnit.unitType || currentUnit.name;
    const isTesla = (unitName.includes('Tesla') || unitType.includes('Tesla'));
    const isHyperTesla = (unitName === 'Hyper Tesla Walker' || unitType === 'HyperTeslaWalker');
    const isMarine = (unitName === 'Marine' || unitType === 'Marine');
    const isHyperMarine = (unitName === 'Hyper Marine' || unitType === 'HyperMarine');
    const isDevilPatrol = (unitName === 'Devil Patrol' || unitType === 'DevilPatrol');
    const isHyperDevilPatrol = (unitName === 'Hyper Devil Patrol' || unitType === 'HyperDevilPatrol');
    const isShade = (unitName === 'Shade' || unitType === 'Shade');
    const isHyperShade = (unitName === 'Hyper Shade' || unitType === 'HyperShade');
    const isPanzer = (unitName === 'Panzer' || unitType === 'Panzer');
    const isHyperPanzer = (unitName === 'Hyper Panzer' || unitType === 'HyperPanzer');
    const isOrchestra = (unitName === 'Orchestra' || unitType === 'Orchestra');
    const isWheelieBomber = (unitName === 'Wheelie Bomber' || unitType === 'WheelieBomber');
    const isApostateLoaf = (unitName === 'Apostate Loaf' || unitType === 'ApostateLoaf');
    const isRocketship = (unitName === 'Rocketship' || unitType === 'Rocketship');
    const isA9Piranha = (unitName === 'A9 Piranha' || unitType === 'A9Piranha');
    const isA9Grizzly = (unitName === 'A9 Grizzly' || unitType === 'A9Grizzly');
    const isA9Pigeon = (unitName === 'A9 Pigeon' || unitType === 'A9Pigeon');
    const isDefectiveLoaf = (unitName === 'Defective Loaf' || unitType === 'DefectiveLoaf');
    const isRejectedLoaf = (unitName === 'Rejected Loaf' || unitType === 'RejectedLoaf');
    const isDelvingPillar = (unitName === 'Delving Pillar' || unitType === 'DelvingPillar');
    const isHarvestingPillar = (unitName === 'Harvesting Pillar' || unitType === 'HarvestingPillar');
    const isDeployableCannon = (unitName === 'Deployable Cannon' || unitType === 'DeployableCannon' || unitType === 'Deployable_Cannon');
    const isSulLoaf = (currentUnit.race === 'Sul' && (unitName.includes('Loaf') || unitType.includes('Loaf')));
    const isLootingVulture = (unitName === 'Looting Vulture' || unitType === 'LootingVulture' || unitType === 'Looting_Vulture');
    const isEnforcerVulture = (unitName === 'Enforcer Vulture' || unitType === 'EnforcerVulture' || unitType === 'Enforcer_Vulture');
    const isProcessingPillar = (unitName === 'Processing Pillar' || unitType === 'ProcessingPillar' || unitType === 'Processing_Pillar');
    const isSentinalLock = (unitName === 'Sentinal Lock' || unitType === 'SentinalLock' || unitType === 'Sentinal_Lock');
    const isPulp = (unitName === 'Pulp' || unitType === 'Pulp');
    const isStickman = (unitName === 'Stickman' || unitType === 'Stickman');
    const isHotHand = (unitName === 'Hot Hand' || unitType === 'HotHand' || unitType === 'Hot Hand');
    const isPinkBeret = (unitName === 'Pink Beret' || unitType === 'PinkBeret' || unitType === 'Pink_Beret');
    const isClaimingPillar = (unitName === 'Claiming Pillar' || unitType === 'ClaimingPillar' || unitType === 'Claiming_Pillar');
    const isPreserverLock = (unitName === 'Preserver Lock' || unitType === 'PreserverLock' || unitType === 'Preserver_Lock');
    const isBastionLock = (unitName === 'Bastion Lock' || unitType === 'BastionLock' || unitType === 'Bastion_Lock');
    const isMonitorLock = (unitName === 'Monitor Lock' || unitType === 'MonitorLock' || unitType === 'Monitor_Lock');
    const isParasiteVulture = (unitName === 'Parasite Vulture' || unitType === 'ParasiteVulture' || unitType === 'Parasite_Vulture');
    const isAntiShade = (unitName === 'Anti Shade' || unitType === 'AntiShade' || unitType === 'Anti_Shade');
    const isForgottenLoaf = (unitName === 'Forgotten Loaf' || unitType === 'ForgottenLoaf');
    const isHacker = (unitName === 'Hacker' || unitType === 'Hacker');

    // Procedural 3-Frame Ground Aura
    if (activeAuraColorHex) {
      let auraOffsetY = 0;
      if (isPulp) {
        const hopCycle = ((walkPhase * 0.42) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
        const u = hopCycle / (Math.PI * 2);
        if (u < 0.78) {
          const uAir = u / 0.78;
          const hang = Math.pow(Math.sin(uAir * Math.PI), 0.72);
          auraOffsetY = -hang * (spriteDisplaySize * (8.0 / 24.0));
        } else {
          const uLand = (u - 0.78) / 0.22;
          const squash = Math.sin(uLand * Math.PI);
          auraOffsetY = squash * (spriteDisplaySize * (1.8 / 24.0));
        }
      } else if (isStickman) {
        const cycle = ((walkPhase * 0.55 % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 8);
        auraOffsetY = (phaseIdx === 1 || phaseIdx === 5) ? (2.2 * stageScale) : ((phaseIdx === 3 || phaseIdx === 7) ? (-1.4 * stageScale) : 0);
      } else if (isHotHand) {
        const sprintCadence = (now * 0.0105) % (Math.PI * 2);
        const strideCycle = ((sprintCadence % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const bounce = Math.sin(strideCycle * 2);
        auraOffsetY = bounce * 1.5;
      }

      const frameIdx = Math.floor((now * 0.009) % 3);
      const auraEntry = buildAuraBitmap(currentUnit.unitType, currentUnit.race, 'western', activeAuraColorHex, frameIdx);
      if (auraEntry && auraEntry.canvas) {
        const isPb = isPinkBeret && !(currentUnit.variant === 'classic' || currentUnit.forceHD);
        const auraDisplaySize = isPb ? Math.round(spriteDisplaySize * 1.20) : spriteDisplaySize;
        const auraDiff = auraDisplaySize - spriteDisplaySize;
        const auraBaseX = isPb ? (spriteX - auraDiff / 2) : spriteX;
        const auraBaseY = isPb ? (spriteY - auraDiff) : spriteY;
        const cellScreenSize = auraDisplaySize / auraEntry.gridSize;
        const auraDrawW = (auraEntry.gridSize + auraEntry.padX * 2) * cellScreenSize;
        const auraDrawH = (auraEntry.gridSize + auraEntry.padTop + auraEntry.padBottom) * cellScreenSize;
        const auraDrawX = auraBaseX - auraEntry.padX * cellScreenSize;
        const auraDrawY = auraBaseY + auraOffsetY - auraEntry.padTop * cellScreenSize;
        ctx.drawImage(auraEntry.canvas, auraDrawX, auraDrawY, auraDrawW, auraDrawH);
      }

      // Rising spark motes
      ctx.fillStyle = activeAuraColorHex;
      for (let sp = 0; sp < 4; sp++) {
        const sparkPhase = (now * 0.0018 + sp * 0.25) % 1.0;
        const sparkY = (spriteY + auraOffsetY + spriteDisplaySize * 0.8) - sparkPhase * (spriteDisplaySize * 0.9);
        const sparkX = pedestalX + Math.sin(now * 0.008 + sp * 2) * (spriteDisplaySize * 0.35);
        const sparkAlpha = Math.sin(sparkPhase * Math.PI) * 0.85;
        ctx.globalAlpha = Math.max(0, sparkAlpha);
        ctx.fillRect(Math.floor(sparkX), Math.floor(sparkY), 2.5, 2.5);
      }
      ctx.globalAlpha = 1.0;
    }

    const t = now * 0.001;
    const isFlash = (elapsedRecoil >= 0 && elapsedRecoil < 45);

    let muzzleX = spriteX + spriteDisplaySize * 0.82 + recoilOffsetX;
    let muzzleY = spriteY + spriteDisplaySize * 0.52;

    if (isTesla) {
      // ⚡ Tesla Walker / Hyper Tesla Walker: 4-Phase Quadruped Spider Gait
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4);
      const walkFrames = isHyperTesla
        ? ['HyperTeslaWalker_Legs_Walk1', 'HyperTeslaWalker_Legs_Walk2', 'HyperTeslaWalker_Legs_Walk3', 'HyperTeslaWalker_Legs_Walk4']
        : ['TeslaWalker_Legs_Walk1', 'TeslaWalker_Legs_Walk2', 'TeslaWalker_Legs_Walk3', 'TeslaWalker_Legs_Walk4'];
      const legsKey = walkFrames[phaseIdx] || (isHyperTesla ? 'HyperTeslaWalker_Legs_Walk1' : 'TeslaWalker_Legs_Walk1');
      const spiderBob = (phaseIdx === 0 || phaseIdx === 2) ? 2.2 : 0.0;
      const chassisPitch = phaseIdx === 0 ? 0.025 : (phaseIdx === 2 ? -0.025 : 0.0);

      const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
      const chassisBmp = buildSpriteBitmap(isHyperTesla ? 'HyperTeslaWalker_Chassis' : 'TeslaWalker_Chassis', currentUnit.race, 'western');
      const teslaBmp = buildSpriteBitmap(isHyperTesla ? 'HyperTeslaWalker_Tail' : 'TeslaWalker_Tesla', currentUnit.race, 'western');
      const clawBmp = isHyperTesla ? buildSpriteBitmap('HyperTeslaWalker_Claw', currentUnit.race, 'western') : null;

      const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
      const chassisSrc = (isFlash && chassisBmp?.flash) ? chassisBmp.flash : chassisBmp?.normal;
      const teslaSrc = (isFlash && teslaBmp?.flash) ? teslaBmp.flash : teslaBmp?.normal;
      const clawSrc = (isFlash && clawBmp?.flash) ? clawBmp.flash : clawBmp?.normal;

      if (legsSrc) ctx.drawImage(legsSrc, spriteX, spriteY + spiderBob, spriteDisplaySize, spriteDisplaySize);

      ctx.save();
      const pivotX = spriteX + spriteDisplaySize / 2;
      const pivotY = spriteY + spriteDisplaySize / 2 + spiderBob;
      ctx.translate(pivotX, pivotY);
      ctx.rotate(chassisPitch);
      ctx.translate(-pivotX, -pivotY);
      if (chassisSrc) ctx.drawImage(chassisSrc, spriteX, spriteY + spiderBob, spriteDisplaySize, spriteDisplaySize);
      ctx.restore();

      if (teslaSrc) ctx.drawImage(teslaSrc, spriteX + recoilOffsetX, spriteY + spiderBob, spriteDisplaySize, spriteDisplaySize);
      if (clawSrc) ctx.drawImage(clawSrc, spriteX + recoilOffsetX * 0.5, spriteY + spiderBob, spriteDisplaySize, spriteDisplaySize);

      if (isHyperTesla) {
        // ⚡ Hyper Tesla Walker: Discharge from forward scorpion stinger needle tip (x: 23.5/24, y: 2.5/24)
        muzzleX = spriteX + spriteDisplaySize * (23.5 / 24) + recoilOffsetX;
        muzzleY = spriteY + spriteDisplaySize * (2.5 / 24) + spiderBob;
        this._hyperTeslaMuzzleX = muzzleX;
        this._hyperTeslaMuzzleY = muzzleY;
      } else {
        // ⚡ Tesla Walker: Discharge from upper forehead toroid emitter / plasma orb (x: 0.62, y: 2.5/24)
        muzzleX = spriteX + spriteDisplaySize * 0.62 + recoilOffsetX;
        muzzleY = spriteY + spriteDisplaySize * (2.5 / 24) + spiderBob;
        this._teslaMuzzleX = muzzleX;
        this._teslaMuzzleY = muzzleY;
      }

      // Ambient electric hum particles on coil tip
      ctx.fillStyle = activeBulletColorHex || '#00f0ff';
      for (let spark = 0; spark < 3; spark++) {
        const sparkAngle = (now * 0.012 + spark * 2.1) % (Math.PI * 2);
        const sparkDist = 5 + Math.sin(now * 0.025 + spark * 3) * 4;
        ctx.fillRect(muzzleX + Math.cos(sparkAngle) * sparkDist, muzzleY + Math.sin(sparkAngle) * sparkDist, 2, 2);
      }
    } else if (isMarine || isHyperMarine) {
      // 🚶 Marine / Hyper Marine: 6-Phase Infantry Walkcycle
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6);
      const walkFrames = isHyperMarine
        ? ['HyperMarine_Legs_Walk1', 'HyperMarine_Legs_Walk2', 'HyperMarine_Legs_Walk3', 'HyperMarine_Legs_Walk4', 'HyperMarine_Legs_Walk5', 'HyperMarine_Legs_Walk6']
        : ['Marine_Legs_Walk1', 'Marine_Legs_Walk2', 'Marine_Legs_Walk3', 'Marine_Legs_Walk4', 'Marine_Legs_Walk5', 'Marine_Legs_Walk6'];
      const prefix = isHyperMarine ? 'HyperMarine' : 'Marine';
      const legsKey = walkFrames[phaseIdx] || 'Marine_Legs_Walk1';
      const torsoBob = Math.sin(walkPhase * 2) * 2.2;

      const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
      const torsoBmp = buildSpriteBitmap(`${prefix}_Torso`, currentUnit.race, 'western');
      const gunBmp = buildSpriteBitmap(`${prefix}_Gun`, currentUnit.race, 'western');

      const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
      const torsoSrc = (isFlash && torsoBmp?.flash) ? torsoBmp.flash : torsoBmp?.normal;
      const gunSrc = (isFlash && gunBmp?.flash) ? gunBmp.flash : gunBmp?.normal;

      if (legsSrc) ctx.drawImage(legsSrc, spriteX, spriteY, spriteDisplaySize, spriteDisplaySize);
      if (torsoSrc) ctx.drawImage(torsoSrc, spriteX, spriteY + torsoBob, spriteDisplaySize, spriteDisplaySize);
      if (gunSrc) ctx.drawImage(gunSrc, spriteX + recoilOffsetX, spriteY + torsoBob, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * 0.84 + recoilOffsetX;
      muzzleY = spriteY + spriteDisplaySize * 0.38 + torsoBob;
    } else if (isDevilPatrol || isHyperDevilPatrol) {
      // 🔥 Devil Patrol: 6-Phase Flame Stride
      if (isHyperDevilPatrol) {
        const hoverBob = Math.sin(walkPhase * 2) * 3;
        const legsBmp = buildSpriteBitmap('HyperDevilPatrol_Legs', currentUnit.race, 'western');
        const torsoBmp = buildSpriteBitmap('HyperDevilPatrol_Torso', currentUnit.race, 'western');
        const gunBmp = buildSpriteBitmap('HyperDevilPatrol_Gun', currentUnit.race, 'western');
        const renderX = spriteX + spriteDisplaySize / 2;
        const renderY = spriteY + spriteDisplaySize / 2;
        const b1AnchorX = renderX - (spriteDisplaySize * 0.16);
        const b1AnchorY = renderY - spriteDisplaySize * 0.04 + hoverBob;
        drawHyperDevilPatrolAmmoBelt(ctx, b1AnchorX, b1AnchorY, 1, spriteDisplaySize, walkPhase, 0, 0, 0.0, 'ultra');
        if (legsBmp?.normal) ctx.drawImage(legsBmp.normal, spriteX, spriteY + hoverBob, spriteDisplaySize, spriteDisplaySize);
        if (torsoBmp?.normal) ctx.drawImage(torsoBmp.normal, spriteX, spriteY + hoverBob, spriteDisplaySize, spriteDisplaySize);
        const b2AnchorX = renderX + (spriteDisplaySize * 0.02);
        const b2AnchorY = renderY + spriteDisplaySize * 0.08 + hoverBob;
        drawHyperDevilPatrolAmmoBelt(ctx, b2AnchorX, b2AnchorY, 1, spriteDisplaySize, walkPhase, 0, 0, 2.1, 'ultra');
        if (gunBmp?.normal) ctx.drawImage(gunBmp.normal, spriteX + recoilOffsetX, spriteY + hoverBob, spriteDisplaySize, spriteDisplaySize);
        muzzleX = spriteX + spriteDisplaySize * 0.84 + recoilOffsetX;
        muzzleY = spriteY + spriteDisplaySize * 0.30 + hoverBob;
      } else {
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6);
        const walkFrames = [
          'DevilPatrol_Legs_Walk1', 'DevilPatrol_Legs_Walk2', 'DevilPatrol_Legs_Walk3',
          'DevilPatrol_Legs_Walk4', 'DevilPatrol_Legs_Walk5', 'DevilPatrol_Legs_Walk6'
        ];
        const legsKey = walkFrames[phaseIdx] || 'DevilPatrol_Legs_Walk1';
        const torsoBob = Math.sin(walkPhase * 2) * 2;

        const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
        const torsoBmp = buildSpriteBitmap('DevilPatrol_Torso', currentUnit.race, 'western');
        const gunBmp = buildSpriteBitmap('DevilPatrol_Gun', currentUnit.race, 'western');

        const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
        const torsoSrc = (isFlash && torsoBmp?.flash) ? torsoBmp.flash : torsoBmp?.normal;
        const gunSrc = (isFlash && gunBmp?.flash) ? gunBmp.flash : gunBmp?.normal;

        if (legsSrc) ctx.drawImage(legsSrc, spriteX, spriteY, spriteDisplaySize, spriteDisplaySize);
        if (torsoSrc) ctx.drawImage(torsoSrc, spriteX, spriteY + torsoBob, spriteDisplaySize, spriteDisplaySize);
        if (gunSrc) ctx.drawImage(gunSrc, spriteX + recoilOffsetX, spriteY + torsoBob, spriteDisplaySize, spriteDisplaySize);

        muzzleX = spriteX + spriteDisplaySize * 0.84 + recoilOffsetX;
        muzzleY = spriteY + spriteDisplaySize * 0.54 + torsoBob;
      }
    } else if (isShade || isHyperShade) {
      // 🎯 Shade: 6-Phase Stealth Walk
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6);
      const prefix = isHyperShade ? 'HyperShade' : 'Shade';
      const walkFrames = [
        `${prefix}_Legs_Walk1`, `${prefix}_Legs_Walk2`, `${prefix}_Legs_Walk3`,
        `${prefix}_Legs_Walk4`, `${prefix}_Legs_Walk5`, `${prefix}_Legs_Walk6`
      ];
      const legsKey = walkFrames[phaseIdx] || `${prefix}_Legs_Walk1`;
      const torsoBob = Math.sin(walkPhase * 2) * 1.6;

      const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
      const torsoBmp = buildSpriteBitmap(`${prefix}_Torso`, currentUnit.race, 'western');
      const gunBmp = buildSpriteBitmap(`${prefix}_Gun`, currentUnit.race, 'western');

      const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
      const torsoSrc = (isFlash && torsoBmp?.flash) ? torsoBmp.flash : torsoBmp?.normal;
      const gunSrc = (isFlash && gunBmp?.flash) ? gunBmp.flash : gunBmp?.normal;

      if (legsSrc) ctx.drawImage(legsSrc, spriteX, spriteY, spriteDisplaySize, spriteDisplaySize);
      if (torsoSrc) ctx.drawImage(torsoSrc, spriteX, spriteY + torsoBob, spriteDisplaySize, spriteDisplaySize);
      if (gunSrc) ctx.drawImage(gunSrc, spriteX + recoilOffsetX, spriteY + torsoBob, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * 0.88 + recoilOffsetX;
      muzzleY = spriteY + spriteDisplaySize * 0.48 + torsoBob;
    } else if (isPanzer || isHyperPanzer) {
      // 🛡️ Panzer / Hyper Panzer: 3-Phase Rolling Treads
      const rollIdx = (Math.floor((cycle / (Math.PI * 2)) * 3) % 3) + 1;
      const prefix = isHyperPanzer ? 'HyperPanzer' : 'Panzer';
      const chassisBmp = buildSpriteBitmap(prefix + '_Chassis_Roll' + rollIdx, currentUnit.race, 'western') || buildSpriteBitmap(prefix + '_Chassis', currentUnit.race, 'western');
      const turretBmp = buildSpriteBitmap(prefix + '_Turret', currentUnit.race, 'western');
      const chassisBob = Math.sin(walkPhase * 2) * 1.2;

      const chassisSrc = (isFlash && chassisBmp?.flash) ? chassisBmp.flash : chassisBmp?.normal;
      const turretSrc = (isFlash && turretBmp?.flash) ? turretBmp.flash : turretBmp?.normal;

      if (chassisSrc) ctx.drawImage(chassisSrc, spriteX, spriteY + chassisBob, spriteDisplaySize, spriteDisplaySize);
      if (turretSrc) ctx.drawImage(turretSrc, spriteX + recoilOffsetX, spriteY + chassisBob, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * 0.95 + recoilOffsetX;
      muzzleY = spriteY + spriteDisplaySize * 0.32 + chassisBob;
    } else if (isOrchestra) {
      // 🚀 Orchestra: 3-Phase Rolling Heavy 8x8 Chassis
      const rollIdx = Math.floor((cycle / (Math.PI * 2)) * 3) + 1;
      const chassisBmp = buildSpriteBitmap('Orchestra_Chassis_Roll' + rollIdx, currentUnit.race, 'western') || buildSpriteBitmap('Orchestra_Chassis', currentUnit.race, 'western');
      const launcherBmp = buildSpriteBitmap('Orchestra_Launcher', currentUnit.race, 'western');
      const chassisBob = Math.sin(walkPhase * 2) * 1.2;

      const chassisSrc = (isFlash && chassisBmp?.flash) ? chassisBmp.flash : chassisBmp?.normal;
      const launcherSrc = (isFlash && launcherBmp?.flash) ? launcherBmp.flash : launcherBmp?.normal;

      if (chassisSrc) ctx.drawImage(chassisSrc, spriteX, spriteY + chassisBob, spriteDisplaySize, spriteDisplaySize);
      if (launcherSrc) ctx.drawImage(launcherSrc, spriteX + recoilOffsetX, spriteY + chassisBob, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * 0.70 + recoilOffsetX;
      muzzleY = spriteY + spriteDisplaySize * 0.42 + chassisBob;
    } else if (isWheelieBomber) {
      // 🏍️ Wheelie Bomber: 3-Phase Rolling Wheels
      const rollIdx = (Math.floor((cycle / (Math.PI * 2)) * 3) % 3) + 1;
      const bikeBmp = buildSpriteBitmap('WheelieBomber_Bike_Roll' + rollIdx, currentUnit.race, 'western') || buildSpriteBitmap('WheelieBomber_Bike', currentUnit.race, 'western');
      const riderBmp = buildSpriteBitmap('WheelieBomber_Rider', currentUnit.race, 'western');
      const animBobY = Math.sin(walkPhase * 2) * 2.5;

      const bikeSrc = (isFlash && bikeBmp?.flash) ? bikeBmp.flash : bikeBmp?.normal;
      const riderSrc = (isFlash && riderBmp?.flash) ? riderBmp.flash : riderBmp?.normal;

      if (bikeSrc) ctx.drawImage(bikeSrc, spriteX + recoilOffsetX, spriteY + animBobY, spriteDisplaySize, spriteDisplaySize);
      if (riderSrc) ctx.drawImage(riderSrc, spriteX + recoilOffsetX, spriteY + animBobY, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * 0.82 + recoilOffsetX;
      muzzleY = spriteY + spriteDisplaySize * 0.52 + animBobY;
    } else if (isApostateLoaf) {
      // 🐛 Apostate Loaf: 4-Phase Crawl
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4) + 1;
      const loafBmp = buildSpriteBitmap('ApostateLoaf_Walk' + phaseIdx, currentUnit.race, 'western') || buildSpriteBitmap('ApostateLoaf', currentUnit.race, 'western');
      const crawlBob = Math.abs(Math.sin(walkPhase)) * 3;
      const loafSrc = (isFlash && loafBmp?.flash) ? loafBmp.flash : loafBmp?.normal;
      if (loafSrc) ctx.drawImage(loafSrc, spriteX, spriteY - crawlBob, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * 0.72;
      muzzleY = spriteY + spriteDisplaySize * 0.54 - crawlBob;
    } else if (isForgottenLoaf) {
      // 🕷️ Forgotten Loaf: 4-Phase Arachnid Tripod Gait & Bioluminescent Fluid Tank
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4) + 1;
      const walkBob = Math.sin(walkPhase * 2) * 1.5;
      const loafBmp = buildSpriteBitmap('ForgottenLoaf_Walk' + phaseIdx, currentUnit.race, 'western') || buildSpriteBitmap('ForgottenLoaf', currentUnit.race, 'western');
      const loafSrc = (isFlash && loafBmp?.flash) ? loafBmp.flash : loafBmp?.normal;
      
      const bodyX = spriteX + recoilOffsetX;
      const bodyY = spriteY + walkBob;
      if (loafSrc) {
        ctx.drawImage(loafSrc, bodyX, bodyY, spriteDisplaySize, spriteDisplaySize);
      }
      // Glowing Cyan Fluid Tank Core Pulsing
      const tankPulse = 0.85 + 0.15 * Math.sin(t * 3.5);
      const tankCenterX = bodyX + spriteDisplaySize * 0.48;
      const tankCenterY = bodyY + spriteDisplaySize * 0.28;
      const coreR = spriteDisplaySize * 0.16 * tankPulse;
      const coreGrad = ctx.createRadialGradient(tankCenterX, tankCenterY, 1, tankCenterX, tankCenterY, coreR);
      coreGrad.addColorStop(0, 'rgba(56, 189, 248, 0.95)');
      coreGrad.addColorStop(0.5, 'rgba(14, 165, 233, 0.45)');
      coreGrad.addColorStop(1, 'rgba(2, 132, 199, 0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(tankCenterX, tankCenterY, coreR, 0, Math.PI * 2);
      ctx.fill();

      muzzleX = tankCenterX;
      muzzleY = tankCenterY;
    } else if (isRocketship) {
      // 🚀 Rocketship: Hover Motion + Dynamic Thruster Plume
      const hoverBob = Math.sin(walkPhase) * 4;
      const shipBmp = buildSpriteBitmap('Rocketship', currentUnit.race, 'western');
      const shipSrc = (isFlash && shipBmp?.flash) ? shipBmp.flash : shipBmp?.normal;
      if (shipSrc) ctx.drawImage(shipSrc, spriteX + recoilOffsetX, spriteY + hoverBob, spriteDisplaySize, spriteDisplaySize);

      const equippedTrailStyleId = inventory.getEquippedRocketTrail('Rocketship');
      const equippedTrailStyle = REEL_3_STYLES.find(s => s.id === equippedTrailStyleId);
      const trailColor = equippedTrailStyle?.hex || '#f97316';
      const exhaustX = spriteX + spriteDisplaySize * 0.085 + recoilOffsetX;
      const exhaustY = spriteY + hoverBob + spriteDisplaySize * 0.52;
      const plumeLength = 16 + Math.sin(now * 0.04) * 6;
      ctx.fillStyle = trailColor;
      ctx.beginPath();
      ctx.ellipse(exhaustX - plumeLength, exhaustY, plumeLength, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.ellipse(exhaustX - plumeLength * 0.5, exhaustY, plumeLength * 0.5, 3, 0, 0, Math.PI * 2);
      ctx.fill();

      muzzleX = spriteX + spriteDisplaySize * 0.82 + recoilOffsetX;
      muzzleY = spriteY + hoverBob + spriteDisplaySize * 0.48;
    } else if (isA9Piranha) {
      // 🚁 A9 Piranha: Hover + Spinning Rotor
      const hoverBob = Math.sin(walkPhase) * 3;
      const hullBmp = buildSpriteBitmap('A9Piranha_Hull', currentUnit.race, 'western');
      const rotorBmp = buildSpriteBitmap('A9Piranha_Rotor', currentUnit.race, 'western');
      if (hullBmp?.normal) ctx.drawImage(hullBmp.normal, spriteX + recoilOffsetX, spriteY + hoverBob, spriteDisplaySize, spriteDisplaySize);
      if (rotorBmp?.normal) {
        ctx.save();
        const rX = spriteX + spriteDisplaySize * 0.5;
        const rY = spriteY + hoverBob + spriteDisplaySize * 0.22;
        ctx.translate(rX, rY);
        ctx.scale(Math.cos(now * 0.035), 1);
        ctx.drawImage(rotorBmp.normal, -spriteDisplaySize / 2, -spriteDisplaySize * 0.22, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      muzzleX = spriteX + spriteDisplaySize * (35.5 / 36) + recoilOffsetX;
      muzzleY = spriteY + hoverBob + spriteDisplaySize * (23.5 / 36);
    } else if (isA9Grizzly) {
      // 🐻 A9 Grizzly: 8-Phase Rolling V-Tread Bronze Wheels + 360° Rotating Roof Radar + Cupola MG
      const heavyRollPhase = (now * 0.0052) % (Math.PI * 2);
      const rollIdx = (Math.floor((heavyRollPhase / (Math.PI * 2)) * 8) % 8) + 1;
      const radarPhase = (now * 0.0032) % (Math.PI * 2);
      const rotIdx = (Math.floor((radarPhase / (Math.PI * 2)) * 6) % 6) + 1;
      const chassisBmp = buildSpriteBitmap('A9Grizzly_Chassis_Roll' + rollIdx, currentUnit.race, 'western') || buildSpriteBitmap('A9Grizzly_Chassis', currentUnit.race, 'western');
      const radarBmp = buildSpriteBitmap('A9Grizzly_Radar_Rot' + rotIdx, currentUnit.race, 'western') || buildSpriteBitmap('A9Grizzly_Radar', currentUnit.race, 'western');
      const turretBmp = buildSpriteBitmap('A9Grizzly_Turret', currentUnit.race, 'western');
      const chassisBob = Math.sin(now * 0.0026) * 0.45;

      const chassisSrc = (isFlash && chassisBmp?.flash) ? chassisBmp.flash : chassisBmp?.normal;
      const radarSrc = (isFlash && radarBmp?.flash) ? radarBmp.flash : radarBmp?.normal;
      const turretSrc = (isFlash && turretBmp?.flash) ? turretBmp.flash : turretBmp?.normal;

      if (chassisSrc) ctx.drawImage(chassisSrc, spriteX, spriteY + chassisBob, spriteDisplaySize, spriteDisplaySize);
      if (radarSrc) ctx.drawImage(radarSrc, spriteX, spriteY + chassisBob, spriteDisplaySize, spriteDisplaySize);
      if (turretSrc) ctx.drawImage(turretSrc, spriteX + recoilOffsetX, spriteY + chassisBob, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * (35.5 / 36) + recoilOffsetX;
      muzzleY = spriteY + spriteDisplaySize * (10 / 36) + chassisBob;
      this._grizzlyMuzzleX = muzzleX;
      this._grizzlyMuzzleY = muzzleY;
    } else if (isA9Pigeon) {
      // 🐦 A9 Pigeon: 2.5D Iridescent VTOL Heavy Fighter with Gyro-Compensated Vector Engine, Chin Gatling & Wingman Pigeon
      const pigeonSize = Math.round(spriteDisplaySize * 1.15);
      const flightPhase = (t * 0.85) % (Math.PI * 2);
      // Cyclic flight demo: forward surge -> VTOL flare hover -> reverse kiting
      const surgeX = Math.sin(flightPhase) * (18 * stageScale) + recoilOffsetX * 0.45;
      const surgeVelX = Math.cos(flightPhase);
      const isRevOrHover = surgeVelX < 0.15 || (elapsedRecoil >= 0 && elapsedRecoil < 450);
      const targetNozzleTilt = isRevOrHover ? 1.0 : 0.0;
      this._srPigeonNozzleTilt = (this._srPigeonNozzleTilt ?? 0) + (targetNozzleTilt - (this._srPigeonNozzleTilt ?? 0)) * 0.12;
      const nozzleTilt = this._srPigeonNozzleTilt;

      const hoverBob = Math.sin(t * 3.4) * (3.5 * stageScale) + Math.cos(t * 7.1) * (1.0 * stageScale);
      const renderX = pedestalX + surgeX;
      const renderY = pedestalY - pigeonSize * 0.50 + hoverBob;

      // Whole-aircraft pitch aiming toward target drone
      const droneTargetX = (this._droneRenderX !== null ? this._droneRenderX : (w * 0.82));
      const droneTargetY = (this._droneRenderY !== null ? this._droneRenderY : (h * 0.52));
      const aimAngle = Math.atan2(droneTargetY - renderY, Math.max(40, droneTargetX - renderX));
      const clampedAim = Math.max(-0.34, Math.min(0.38, aimAngle));
      const cruiseTrim = (!isRevOrHover) ? 0.04 : -0.03;
      const isFiring = (elapsedRecoil >= 0 && elapsedRecoil < 280);
      const joltPitch = isFiring ? Math.sin(now * 0.06) * 0.018 : 0;
      const hullRot = clampedAim + cruiseTrim + joltPitch;

      // 1. Ground Downwash Rings on Pedestal when VTOL nozzle is tilted downward
      if (nozzleTilt > 0.25 && !isLowQuality) {
        ctx.save();
        for (let r = 0; r < 2; r++) {
          const ringProg = ((t * 2.2 + r * 0.5) % 1.0);
          const ringRx = (18 + ringProg * 46) * stageScale * nozzleTilt;
          const ringRy = (5 + ringProg * 13) * stageScale * nozzleTilt;
          ctx.strokeStyle = `rgba(186, 230, 253, ${(1 - ringProg) * 0.36 * nozzleTilt})`;
          ctx.lineWidth = 1.6 * stageScale;
          ctx.beginPath();
          ctx.ellipse(renderX - pigeonSize * 0.18, pedestalY + 4 * stageScale, ringRx, ringRy, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.restore();
      }

      const hullBmp = buildSpriteBitmap('A9Pigeon_Hull', currentUnit.race, 'western');
      const engKey = nozzleTilt < 0.33 ? 'A9Pigeon_Engine_Horiz' : (nozzleTilt < 0.72 ? 'A9Pigeon_Engine_Diag' : 'A9Pigeon_Engine_Vert');
      const engBmp = buildSpriteBitmap(engKey, currentUnit.race, 'western');
      const spinIdx = isFiring ? ((Math.floor(now * 0.03) % 2) + 1) : 1;
      const gatBmp = buildSpriteBitmap('A9Pigeon_Gatling_Spin' + spinIdx, currentUnit.race, 'western');
      const bombBmp = buildSpriteBitmap('A9Pigeon_Bomb', currentUnit.race, 'western');

      const hullSrc = (isFlash && hullBmp?.flash) ? hullBmp.flash : hullBmp?.normal;
      const engSrc = (isFlash && engBmp?.flash) ? engBmp.flash : engBmp?.normal;
      const gatSrc = (isFlash && gatBmp?.flash) ? gatBmp.flash : gatBmp?.normal;
      const bombSrc = (isFlash && bombBmp?.flash) ? bombBmp.flash : bombBmp?.normal;

      ctx.save();
      ctx.translate(renderX, renderY);
      ctx.rotate(hullRot);

      // 2. Separate Vector-Thrust Engine Sprite with Active Gyro Counter-Rotation against hullRot
      const pivX = (-6.5 / 36) * pigeonSize;
      const pivY = (2.5 / 36) * pigeonSize;
      ctx.save();
      ctx.translate(pivX, pivY);
      const revCant = (surgeVelX < -0.15) ? 0.14 : 0.0;
      const targetDownWorldRot = -(Math.PI * 0.5 - revCant);
      const localNozzleRot = nozzleTilt * (targetDownWorldRot - hullRot);
      ctx.rotate(localNozzleRot);

      // Dynamic Afterburner Plasma Cone
      if (!isLowQuality) {
        const thrustAngle = Math.PI + nozzleTilt * (-Math.PI * 0.5) - localNozzleRot;
        const plumeLen = (18 + Math.sin(now * 0.045) * 6) * stageScale;
        const exX = Math.cos(thrustAngle) * (8 * stageScale);
        const exY = Math.sin(thrustAngle) * (8 * stageScale);
        ctx.save();
        ctx.translate(exX, exY);
        ctx.rotate(thrustAngle);
        ctx.fillStyle = activeTrailColorHex || '#0ea5e9';
        ctx.globalAlpha = 0.78;
        ctx.beginPath();
        ctx.ellipse(plumeLen * 0.45, 0, plumeLen * 0.55, 4.5 * stageScale, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#e0f2fe';
        ctx.globalAlpha = 0.95;
        ctx.beginPath();
        ctx.ellipse(plumeLen * 0.25, 0, plumeLen * 0.28, 2.2 * stageScale, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      if (engSrc) {
        ctx.drawImage(engSrc, -pigeonSize / 2 - pivX, -pigeonSize / 2 - pivY, pigeonSize, pigeonSize);
      }
      ctx.restore();

      // 3. Underslung Heavy Ground Bomb
      if (bombSrc) {
        ctx.drawImage(bombSrc, -pigeonSize / 2, -pigeonSize / 2, pigeonSize, pigeonSize);
      }

      // 4. Main Fuselage with Iridescent Pigeon-Neck Nose & Cyan Canopy
      if (hullSrc) {
        ctx.drawImage(hullSrc, -pigeonSize / 2, -pigeonSize / 2, pigeonSize, pigeonSize);
      }

      // 5. Chin-Mounted 4-Barrel Gatling Gun + Thermal Glow
      const gatRecoilX = isFiring ? -2.2 * stageScale : 0;
      if (gatSrc) {
        ctx.drawImage(gatSrc, -pigeonSize / 2 + gatRecoilX, -pigeonSize / 2, pigeonSize, pigeonSize);
      }
      if (isFiring && !isLowQuality) {
        ctx.fillStyle = 'rgba(251, 146, 60, 0.75)';
        ctx.fillRect((8 / 36) * pigeonSize, (6.5 / 36) * pigeonSize, (8 / 36) * pigeonSize, (2.2 / 36) * pigeonSize);
      }
      ctx.restore();

      // 6. Wingman Pigeon Companion with Spring-Damper Flight Lag
      const flapIdx = (Math.floor(t * 13) % 3) + 1;
      const birdBmp = buildSpriteBitmap('A9Pigeon_Bird_Flap' + flapIdx, currentUnit.race, 'western');
      if (birdBmp?.normal) {
        const birdGoalX = renderX - pigeonSize * 0.34;
        const birdGoalY = renderY - pigeonSize * 0.38 + Math.sin(t * 6.2) * (4.5 * stageScale);
        this._srBirdX = (this._srBirdX ?? birdGoalX) + (birdGoalX - (this._srBirdX ?? birdGoalX)) * 0.14;
        this._srBirdY = (this._srBirdY ?? birdGoalY) + (birdGoalY - (this._srBirdY ?? birdGoalY)) * 0.14;
        const birdSize = Math.round(pigeonSize * 0.44);
        ctx.save();
        ctx.translate(this._srBirdX, this._srBirdY);
        ctx.rotate(hullRot * 0.55 + Math.cos(t * 6.2) * 0.08);
        ctx.drawImage(birdBmp.normal, -birdSize / 2, -birdSize / 2, birdSize, birdSize);
        ctx.restore();
      }

      // Compute exact rotated chin-cannon muzzle coordinates in stage space
      const localMuzX = (16.5 / 36) * pigeonSize + gatRecoilX;
      const localMuzY = (7.5 / 36) * pigeonSize;
      const cosH = Math.cos(hullRot);
      const sinH = Math.sin(hullRot);
      muzzleX = renderX + localMuzX * cosH - localMuzY * sinH;
      muzzleY = renderY + localMuzX * sinH + localMuzY * cosH;
      this._pigeonMuzzleX = muzzleX;
      this._pigeonMuzzleY = muzzleY;
    } else if (isDeployableCannon) {
      // 🕷️ Deployable Cannon: Spider Base & Autocannon Turret (Stationary structure)
      const baseBmp = buildSpriteBitmap('DeployableCannon_Base', currentUnit.race, 'western');
      const turretBmp = buildSpriteBitmap('DeployableCannon_Turret', currentUnit.race, 'western');
      const baseSrc = (isFlash && baseBmp?.flash) ? baseBmp.flash : baseBmp?.normal;
      const turretSrc = (isFlash && turretBmp?.flash) ? turretBmp.flash : turretBmp?.normal;

      if (baseSrc) ctx.drawImage(baseSrc, spriteX, spriteY, spriteDisplaySize, spriteDisplaySize);
      if (turretSrc) ctx.drawImage(turretSrc, spriteX + recoilOffsetX, spriteY, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * 0.95 + recoilOffsetX;
      muzzleY = spriteY + spriteDisplaySize * 0.32;
    } else if (isDefectiveLoaf) {
      // 𓏏 Sul Defective Loaf: 5-Strand Root Skirt + Living Proboscis + Starburst Core
      const idleBob = Math.sin(t * 1.5) * 2.5;
      const breathPulse = Math.sin(t * 1.8);
      const stretchY = 1.0 + breathPulse * 0.024;
      const squashX = 1.0 - breathPulse * 0.018;
      const idleTilt = Math.sin(t * 1.2) * 0.03;
      const loafX = spriteX + spriteDisplaySize / 2 + recoilOffsetX;
      const loafY = spriteY + spriteDisplaySize * 0.52 + idleBob;
      const cosT = Math.cos(idleTilt);
      const sinT = Math.sin(idleTilt);

      // 1. 5-Strand Root Skirt (Drawn behind body)
      ctx.save();
      for (let s = 0; s < 5; s++) {
        const sFreq = DEFECTIVE_LOAF_ROOT_FREQ[s];
        const sPhase = DEFECTIVE_LOAF_ROOT_PHASE[s];
        const sSpatial = DEFECTIVE_LOAF_ROOT_SPATIAL[s];
        const sAmp = DEFECTIVE_LOAF_ROOT_AMP[s];
        const sColor = DEFECTIVE_LOAF_ROOT_COLORS[s];

        const anchorLocalX = (s - 2) * (spriteDisplaySize * 0.08 * squashX);
        const anchorLocalY = spriteDisplaySize * 0.18 * stretchY;
        const anchorX = loafX + cosT * anchorLocalX - sinT * anchorLocalY;
        const anchorY = loafY + sinT * anchorLocalX + cosT * anchorLocalY;
        const strandLen = spriteDisplaySize * DEFECTIVE_LOAF_ROOT_LEN[s] * stretchY;

        let prevX = anchorX;
        let prevY = anchorY;

        for (let k = 1; k <= 5; k++) {
          const u = k / 5;
          const idleWavePhase = t * (2.2 * sFreq) - u * sSpatial + sPhase;
          const idleWaveX = Math.sin(idleWavePhase) * (u * 4.5 * sAmp);
          const idleWaveY = Math.cos(t * (1.5 * sFreq) - u * 2.5 + sPhase) * (u * 2.0 * sAmp);
          const currX = anchorX + idleWaveX;
          const currY = anchorY + (u * strandLen) + idleWaveY;

          ctx.strokeStyle = sColor;
          ctx.lineWidth = Math.max(1.2, (5 - k) * 0.6 + 1.0);
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(prevX, prevY);
          ctx.lineTo(currX, currY);
          ctx.stroke();

          if (k === 5) {
            ctx.fillStyle = sColor;
            ctx.beginPath();
            ctx.arc(currX, currY, 1.8, 0, Math.PI * 2);
            ctx.fill();
          }

          prevX = currX;
          prevY = currY;
        }
      }
      ctx.restore();

      // 2. Shell Body Sprite
      const spriteKey = (elapsedRecoil >= 0 && elapsedRecoil < 120) ? 'DefectiveLoaf_Attack' : 'DefectiveLoaf_Front';
      const loafBmp = buildSpriteBitmap(spriteKey, currentUnit.race, 'western') || buildSpriteBitmap('DefectiveLoaf', currentUnit.race, 'western');
      const loafSrc = (isFlash && loafBmp?.flash) ? loafBmp.flash : loafBmp?.normal;
      ctx.save();
      ctx.translate(loafX, loafY);
      ctx.rotate(idleTilt);
      ctx.scale(squashX, stretchY);
      if (loafSrc) {
        ctx.drawImage(loafSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
      }
      // Starburst Plasma Core Glow
      const corePulse = 0.7 + 0.3 * Math.sin(t * 4.2);
      const coreR = spriteDisplaySize * 0.14 * corePulse;
      const coreGrad = ctx.createRadialGradient(0, -spriteDisplaySize * 0.12, 1, 0, -spriteDisplaySize * 0.12, coreR);
      coreGrad.addColorStop(0, 'rgba(250, 204, 21, 0.95)');
      coreGrad.addColorStop(0.5, 'rgba(234, 88, 12, 0.5)');
      coreGrad.addColorStop(1, 'rgba(220, 38, 38, 0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(0, -spriteDisplaySize * 0.12, coreR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // 3. Living Prehensile Proboscis
      const probBaseX = loafX + sinT * (spriteDisplaySize * 0.02);
      const probBaseY = loafY + cosT * (spriteDisplaySize * 0.17 * stretchY);
      let prevProbX = probBaseX;
      let prevProbY = probBaseY;
      ctx.save();
      for (let k = 1; k <= 6; k++) {
        const u = k / 6;
        let currX, currY;
        if (elapsedRecoil >= 0 && elapsedRecoil < 220) {
          const reachDist = u * spriteDisplaySize * 0.55;
          const sWave = Math.sin(t * 14 - u * 3.5) * (u * 4.5);
          currX = probBaseX + reachDist;
          currY = probBaseY + (u * spriteDisplaySize * 0.05) + sWave;
        } else {
          const coilAng = u * (Math.PI * 1.35) + Math.sin(t * 2.0) * 0.25;
          const coilR = (1 - u * 0.45) * (spriteDisplaySize * 0.18);
          currX = probBaseX + Math.sin(coilAng) * coilR;
          currY = probBaseY + (u * spriteDisplaySize * 0.22) + (1 - Math.cos(coilAng)) * (spriteDisplaySize * 0.08);
        }
        let probColor = '#4338ca';
        if (k === 3) probColor = '#6366f1';
        else if (k === 4) probColor = '#818cf8';
        else if (k === 5) probColor = '#a855f7';
        else if (k === 6) probColor = '#f0abfc';
        ctx.strokeStyle = probColor;
        ctx.lineWidth = Math.max(1.8, (7 - k) * 0.8);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(prevProbX, prevProbY);
        ctx.lineTo(currX, currY);
        ctx.stroke();
        prevProbX = currX;
        prevProbY = currY;
      }
      ctx.restore();

      muzzleX = loafX + spriteDisplaySize * 0.35;
      muzzleY = loafY + spriteDisplaySize * 0.15;
    } else if (isRejectedLoaf) {
      // 𓏏 Sul Rejected Loaf: 5 Mycelium Filaments + Coronal Spore Halo + Chitin Skull
      const idleBob = Math.sin(t * 1.4) * 2.6 + Math.cos(t * 0.7) * 0.8;
      const breathPulse = Math.sin(t * 1.8);
      const stretchY = 1.0 + breathPulse * 0.025;
      const squashX = 1.0 - breathPulse * 0.018;
      const idleTilt = Math.sin(t * 1.1) * 0.025;
      const skullX = spriteX + spriteDisplaySize / 2 + recoilOffsetX;
      const skullY = spriteY + spriteDisplaySize * 0.48 + idleBob;
      const cosT = Math.cos(idleTilt);
      const sinT = Math.sin(idleTilt);
      const bulbLocalY = spriteDisplaySize * 0.20 * stretchY;

      // 1. Coronal Halo Glow
      const haloRadius = 26 * (0.85 + 0.15 * Math.sin(t * 2.5));
      const haloGrad = ctx.createRadialGradient(skullX, skullY, 2, skullX, skullY, haloRadius);
      haloGrad.addColorStop(0, 'rgba(216, 180, 254, 0.45)');
      haloGrad.addColorStop(0.6, 'rgba(168, 85, 247, 0.18)');
      haloGrad.addColorStop(1, 'rgba(126, 34, 206, 0)');
      ctx.fillStyle = haloGrad;
      ctx.beginPath();
      ctx.arc(skullX, skullY, haloRadius, 0, Math.PI * 2);
      ctx.fill();

      // 2. 5 Multi-Strand Mycelium Filaments
      const umbrellaFactor = breathPulse * 0.25;
      ctx.save();
      for (let s = 0; s < 5; s++) {
        const sFreq = REJECTED_LOAF_STRAND_FREQ[s];
        const sPhase = REJECTED_LOAF_STRAND_PHASE[s];
        const sSpatial = REJECTED_LOAF_STRAND_SPATIAL[s];
        const sAmp = REJECTED_LOAF_STRAND_AMP[s];

        const bulbLocalX = (s - 2) * (spriteDisplaySize * 0.075 * squashX);
        const anchorX = skullX + cosT * bulbLocalX - sinT * bulbLocalY;
        const anchorY = skullY + sinT * bulbLocalX + cosT * bulbLocalY;
        const strandLength = spriteDisplaySize * REJECTED_LOAF_STRAND_LEN[s] * stretchY;

        _srRejectedStrandsScratch[s][0].x = anchorX;
        _srRejectedStrandsScratch[s][0].y = anchorY;

        for (let k = 1; k <= 6; k++) {
          const u = k / 6;
          const idleWavePhase = t * (2.2 * sFreq) - u * sSpatial + sPhase;
          const idleWaveX = Math.sin(idleWavePhase) * (u * 5.2 * sAmp) + Math.sin(t * 1.1 + sPhase) * (u * 2.2);
          const idleWaveY = Math.cos(t * (1.6 * sFreq) - u * 3.0 + sPhase) * (u * 2.4 * sAmp);
          const lateralSpreadDir = (s - 2);
          const umbrellaSpreadX = lateralSpreadDir * umbrellaFactor * (u * spriteDisplaySize * 0.18);

          _srRejectedStrandsScratch[s][k].x = anchorX + idleWaveX + umbrellaSpreadX;
          _srRejectedStrandsScratch[s][k].y = anchorY + (u * strandLength) + idleWaveY;
        }
      }

      ctx.lineCap = 'round';
      for (let k = 1; k <= 6; k++) {
        const prevIdx = k - 1;
        ctx.strokeStyle = REJECTED_LOAF_SEG_COLORS[k - 1];
        ctx.lineWidth = REJECTED_LOAF_SEG_WIDTHS[k - 1] * 1.3;
        ctx.beginPath();
        for (let s = 0; s < 5; s++) {
          ctx.moveTo(_srRejectedStrandsScratch[s][prevIdx].x, _srRejectedStrandsScratch[s][prevIdx].y);
          ctx.lineTo(_srRejectedStrandsScratch[s][k].x, _srRejectedStrandsScratch[s][k].y);
        }
        ctx.stroke();
      }

      // Luminous Spore Tips
      ctx.fillStyle = '#f5d0fe';
      ctx.beginPath();
      for (let s = 0; s < 5; s++) {
        const tip = _srRejectedStrandsScratch[s][6];
        ctx.moveTo(tip.x + 2.0, tip.y);
        ctx.arc(tip.x, tip.y, 2.0, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.restore();

      // 3. Skull Body Sprite
      const loafBmp = buildSpriteBitmap('RejectedLoaf_Front', currentUnit.race, 'western') || buildSpriteBitmap('RejectedLoaf', currentUnit.race, 'western');
      const loafSrc = (isFlash && loafBmp?.flash) ? loafBmp.flash : loafBmp?.normal;
      ctx.save();
      ctx.translate(skullX, skullY);
      ctx.rotate(idleTilt);
      ctx.scale(squashX, stretchY);
      if (loafSrc) {
        ctx.drawImage(loafSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
      }
      ctx.restore();

      muzzleX = skullX + spriteDisplaySize * 0.35;
      muzzleY = skullY + spriteDisplaySize * 0.12;
    } else if (isDelvingPillar) {
      // 𓉐 Sul Delving Pillar: 9 Living Matrix Tentacles across 3 Depth Planes + Compound Oculars
      // Slow, smooth long-phase majestic floating bio-glide
      const pillarSize = spriteDisplaySize * 0.70;
      const hoverPhase = t * 0.75;
      const idleBob = Math.sin(hoverPhase) * 3.6 + Math.sin(hoverPhase * 0.5 + 1.1) * 1.2;
      const propCycle = (t * 1.4) % (Math.PI * 2);
      const strokeWhip = Math.sin(propCycle);
      const strokeSpread = Math.cos(propCycle);
      const breathPulse = Math.sin(t * 1.2);
      const stretchY = 1.0 + breathPulse * 0.018;
      const squashX = 1.0 - breathPulse * 0.012;
      const idleTilt = Math.sin(t * 0.5) * 0.025;
      const pillarX = spriteX + spriteDisplaySize / 2 + recoilOffsetX;
      const pillarY = spriteY + spriteDisplaySize * 0.48 + idleBob;
      const cosT = Math.cos(idleTilt);
      const sinT = Math.sin(idleTilt);

      // 1. Calculate All 9 Tentacle Paths
      for (let s = 0; s < 9; s++) {
        const sFreq = DELVING_TENTACLE_FREQ[s];
        const sPhase = DELVING_TENTACLE_PHASE[s];
        const sSpatial = DELVING_TENTACLE_SPATIAL[s];
        const sAmp = DELVING_TENTACLE_AMP[s];
        const sCurlFreq = DELVING_TENTACLE_CURL_FREQ[s];
        const sCurlAmp = DELVING_TENTACLE_CURL_AMP[s];

        const localAnchorX = DELVING_TENTACLE_ANCHOR_X[s] * pillarSize * squashX;
        const localAnchorY = DELVING_TENTACLE_ANCHOR_Y[s] * pillarSize * stretchY;
        const anchorX = pillarX + cosT * localAnchorX - sinT * localAnchorY;
        const anchorY = pillarY + sinT * localAnchorX + cosT * localAnchorY;
        const strandLen = pillarSize * DELVING_TENTACLE_LEN[s] * stretchY;

        _srDelvingTentaclesScratch[s][0].x = anchorX;
        _srDelvingTentaclesScratch[s][0].y = anchorY;

        for (let k = 1; k <= 9; k++) {
          const u = k / 9;
          const wavePhase1 = t * (2.4 * sFreq) - u * sSpatial + sPhase;
          const wavePhase2 = t * (1.2 * sFreq) - u * 3.4 + sPhase;
          const idleWaveX = Math.sin(wavePhase1) * (u * 5.0 * sAmp) + Math.cos(wavePhase2) * (u * 2.0);
          const idleWaveY = Math.cos(wavePhase1 * 0.8) * (u * 2.5 * sAmp);

          const sweepX = -Math.max(0, strokeWhip) * (u * pillarSize * 0.15);
          const sweepY = strokeSpread * (u * pillarSize * 0.08);

          let curlX = 0;
          let curlY = 0;
          if (k >= 5) {
            const curlWeight = (k - 4) / 5;
            curlX = Math.sin(t * sCurlFreq + sPhase * 1.6) * (curlWeight * sCurlAmp);
            curlY = Math.cos(t * (sCurlFreq * 0.85) + sPhase * 1.6) * (curlWeight * sCurlAmp * 0.6);
          }

          const lateralSpread = (s - 4) * (0.06 * pillarSize * u);

          _srDelvingTentaclesScratch[s][k].x = anchorX + idleWaveX + sweepX + curlX + lateralSpread;
          _srDelvingTentaclesScratch[s][k].y = anchorY + (u * strandLen) + idleWaveY + sweepY + curlY;
        }
      }

      // 2. LAYER A: Background Tentacles (0..2)
      ctx.save();
      ctx.lineCap = 'round';
      for (let k = 1; k <= 9; k++) {
        const prevIdx = k - 1;
        ctx.strokeStyle = DELVING_TENTACLE_BG_COLORS[k - 1];
        ctx.lineWidth = Math.max(1.0, DELVING_TENTACLE_SEG_WIDTHS[k - 1] * 0.9);
        ctx.beginPath();
        for (let s = 0; s < 3; s++) {
          ctx.moveTo(_srDelvingTentaclesScratch[s][prevIdx].x, _srDelvingTentaclesScratch[s][prevIdx].y);
          ctx.lineTo(_srDelvingTentaclesScratch[s][k].x, _srDelvingTentaclesScratch[s][k].y);
        }
        ctx.stroke();
      }
      ctx.restore();

      // 3. LAYER B: Body Sprite & Compound Sensory Ocular Cluster
      const spriteKey = (elapsedRecoil >= 0 && elapsedRecoil < 120) ? 'DelvingPillar_Attack' : 'DelvingPillar_Hover';
      const pillarBitmap = buildSpriteBitmap(spriteKey, currentUnit.race, 'western') || buildSpriteBitmap('DelvingPillar', currentUnit.race, 'western');
      const activeImg = (isFlash && pillarBitmap?.flash) ? pillarBitmap.flash : pillarBitmap?.normal;
      ctx.save();
      ctx.translate(pillarX, pillarY);
      ctx.rotate(idleTilt);
      ctx.scale(squashX, stretchY);
      if (activeImg) {
        ctx.drawImage(activeImg, -pillarSize / 2, -pillarSize / 2, pillarSize, pillarSize);
      }
      const snoutOffX = pillarSize * 0.22;
      const snoutOffY = -pillarSize * 0.02;
      const oR = pillarSize * 0.24;
      const eyeGrad = ctx.createRadialGradient(snoutOffX, snoutOffY, 1, snoutOffX, snoutOffY, oR);
      eyeGrad.addColorStop(0, 'rgba(254, 240, 138, 0.9)');
      eyeGrad.addColorStop(0.5, 'rgba(250, 204, 21, 0.4)');
      eyeGrad.addColorStop(1, 'rgba(234, 179, 8, 0)');
      ctx.fillStyle = eyeGrad;
      ctx.beginPath();
      ctx.arc(snoutOffX, snoutOffY, oR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // 4. LAYER C: Foreground Tentacles (3..8)
      ctx.save();
      ctx.lineCap = 'round';
      for (let k = 1; k <= 9; k++) {
        const prevIdx = k - 1;
        ctx.strokeStyle = DELVING_TENTACLE_FG_COLORS[k - 1];
        ctx.lineWidth = Math.max(1.0, DELVING_TENTACLE_SEG_WIDTHS[k - 1] * 1.0);
        ctx.beginPath();
        for (let s = 3; s < 9; s++) {
          ctx.moveTo(_srDelvingTentaclesScratch[s][prevIdx].x, _srDelvingTentaclesScratch[s][prevIdx].y);
          ctx.lineTo(_srDelvingTentaclesScratch[s][k].x, _srDelvingTentaclesScratch[s][k].y);
        }
        ctx.stroke();
      }

      // Luminous Searchlight Bulbs & Halos
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      for (let s = 0; s < 9; s++) {
        const tip = _srDelvingTentaclesScratch[s][9];
        const r = (s === 4 || s === 7) ? 2.2 : 1.6;
        ctx.moveTo(tip.x + r, tip.y);
        ctx.arc(tip.x, tip.y, r, 0, Math.PI * 2);
      }
      ctx.fill();

      for (let s = 1; s < 9; s += 2) {
        const tip = _srDelvingTentaclesScratch[s][9];
        const tipGrad = ctx.createRadialGradient(tip.x, tip.y, 1, tip.x, tip.y, 8);
        tipGrad.addColorStop(0, 'rgba(254, 240, 138, 0.8)');
        tipGrad.addColorStop(0.5, 'rgba(234, 179, 8, 0.3)');
        tipGrad.addColorStop(1, 'rgba(234, 179, 8, 0)');
        ctx.fillStyle = tipGrad;
        ctx.beginPath();
        ctx.arc(tip.x, tip.y, 8, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      muzzleX = pillarX + pillarSize * 0.45;
      muzzleY = pillarY;
    } else if (isHarvestingPillar) {
      // 𓉐 Sul Harvesting Pillar: 6 Skeletal Claw Fingers & Living Procedural Scaled Arms Rig
      // Background Claw Fingers (0..2) & Foreground Claw Fingers (3..5)
      const pillarSize = spriteDisplaySize * 1.05;
      const pillarX = spriteX + spriteDisplaySize / 2 + recoilOffsetX;
      const pillarY = spriteY + spriteDisplaySize * 0.48;

      const targetDroneX = (this._droneRenderX !== null ? this._droneRenderX : (w * 0.82));
      const targetDroneY = (this._droneRenderY !== null ? this._droneRenderY : (h * 0.50));

      let attackFactor = 0;
      if (elapsedRecoil >= 0 && elapsedRecoil < 560) {
        const p = elapsedRecoil / 560;
        if (p < 0.28) {
          // Lunge & stretch arms forward toward target drone
          attackFactor = Math.sin((p / 0.28) * (Math.PI / 2));
        } else if (p < 0.65) {
          // Clamping vice-grip on target drone
          attackFactor = 1.0;
          if (!this._harvestingHitTriggered && p > 0.32) {
            this._harvestingHitTriggered = true;
            this._droneHitTime = now;
            this._droneImpactColor = '#c084fc';
            gameProgress.recordTargetDroneHit();
          }
        } else {
          // Retract back into U-shaped floating posture
          attackFactor = Math.cos(((p - 0.65) / 0.35) * (Math.PI / 2));
        }
      }

      drawHarvestingPillarRig(ctx, {
        renderX: pillarX,
        renderY: pillarY,
        drawSize: pillarSize,
        dir: 1, // Facing right toward target drone
        team: 'western',
        renderRace: currentUnit.race || 'Sul',
        t,
        wobble: 0,
        isHitFlashing: isFlash,
        isAttacking: (elapsedRecoil >= 0 && elapsedRecoil < 560),
        attackFactor,
        targetX: targetDroneX,
        targetY: targetDroneY,
        isLocked1: false,
        isLocked2: false,
        quality: isLowQuality ? 'low' : 'ultra',
        spawnAlpha: 1.0,
        walkFactor: 0
      });

      muzzleX = pillarX + pillarSize * 0.42;
      muzzleY = pillarY - pillarSize * 0.38;
      this._harvestingMuzzleX = muzzleX;
      this._harvestingMuzzleY = muzzleY;
    } else if (isSulLoaf) {
      // 🧫 Sul Loaf Biomass: Organic Gelatinous Squish & Stretch
      const loafBmp = buildSpriteBitmap(currentUnit.unitType, currentUnit.race, 'western');
      const loafSrc = (isFlash && loafBmp?.flash) ? loafBmp.flash : loafBmp?.normal;
      const squish = Math.sin(walkPhase) * 0.07;
      ctx.save();
      const cX = spriteX + spriteDisplaySize / 2;
      const cY = spriteY + spriteDisplaySize * 0.65;
      ctx.translate(cX, cY);
      ctx.scale(1.0 + squish, 1.0 - squish);
      if (loafSrc) ctx.drawImage(loafSrc, -spriteDisplaySize / 2, -spriteDisplaySize * 0.65, spriteDisplaySize, spriteDisplaySize);
      ctx.restore();

      muzzleX = spriteX + spriteDisplaySize * 0.72;
      muzzleY = spriteY + spriteDisplaySize * 0.54;
    } else if (isLootingVulture) {
      // 🐉 SUL LOOTING VULTURE CELESTIAL DRAGON TITAN (Facing +X towards target drone)
      // Fully articulated 4-segment celestial sky leviathan modeled after 1_lootingvulture.png
      const bodyBitmap = buildSpriteBitmap('LootingVulture_Body', currentUnit.race, 'western');
      const pyramidBitmap = buildSpriteBitmap('LootingVulture_Pyramid', currentUnit.race, 'western');
      const headBitmap = buildSpriteBitmap('LootingVulture_Head', currentUnit.race, 'western');
      const tailBitmap = buildSpriteBitmap('LootingVulture_Tail', currentUnit.race, 'western');

      const bodySrc = (isFlash && bodyBitmap?.flash) ? bodyBitmap.flash : bodyBitmap?.normal;
      const pyramidSrc = (isFlash && pyramidBitmap?.flash) ? pyramidBitmap.flash : pyramidBitmap?.normal;
      const headSrc = (isFlash && headBitmap?.flash) ? headBitmap.flash : headBitmap?.normal;
      const tailSrc = (isFlash && tailBitmap?.flash) ? tailBitmap.flash : tailBitmap?.normal;

      const renderX = pedestalX;
      const renderY = pedestalY - spriteDisplaySize * 0.46;
      const drawSize = spriteDisplaySize;
      const forwardDir = 1; // Facing right toward target drone

      // 1. Celestial Leviathan Levitation Dynamics (Air unit floating majestically over pedestal)
      const hoverY = Math.sin(t * 1.3) * 5.0;
      const flightBank = Math.sin(t * 1.1) * 0.025;

      ctx.save();
      ctx.translate(renderX, renderY + hoverY);
      ctx.rotate(flightBank);
      ctx.translate(-renderX, -(renderY + hoverY));

      // ── TRAVELLING TRANSVERSE SINE WAVE KINEMATICS ──
      const omega = 2.3;
      const tailShiftX = -drawSize * 0.30 * forwardDir;
      const pyramidShiftX = drawSize * 0.18 * forwardDir;
      const headShiftX = drawSize * 0.22 * forwardDir;
      const pyramidScale = 0.78;

      const tailWavePhase = t * omega - 2.0;
      const tailSurgeX = Math.cos(tailWavePhase) * 4.5 * forwardDir;
      const tailWaveY = Math.sin(tailWavePhase) * 11.5 + Math.sin(tailWavePhase * 2.0 - 0.6) * 3.2;
      const tailPitch = (Math.cos(tailWavePhase) * 0.28 + Math.cos(tailWavePhase * 2.0) * 0.08) * forwardDir;

      const bodyWavePhase = t * omega - 0.4;
      const bodySurgeX = Math.cos(bodyWavePhase) * 2.8 * forwardDir;
      const bodyWaveY = Math.sin(bodyWavePhase) * 7.5 + Math.sin(bodyWavePhase * 2.0 - 0.4) * 2.2;
      const bodyPitch = (Math.cos(bodyWavePhase) * 0.16 + Math.cos(bodyWavePhase * 2.0) * 0.04) * forwardDir;

      const pyramidWavePhase = t * omega + 0.25;
      const pyramidSurgeX = Math.cos(pyramidWavePhase) * 1.8 * forwardDir;
      const pyramidWaveY = Math.sin(pyramidWavePhase) * 5.2 + Math.sin(pyramidWavePhase * 2.0 - 0.3) * 1.5;
      const pyramidPitch = (Math.cos(pyramidWavePhase) * 0.12 + Math.cos(pyramidWavePhase * 2.0) * 0.03) * forwardDir;

      const headLeadPhase = t * omega + 0.8;
      const headSurgeX = Math.cos(headLeadPhase) * 1.4 * forwardDir;
      const headBobY = Math.sin(headLeadPhase) * 4.6 + Math.sin(headLeadPhase * 2.0 - 0.2) * 1.2;
      let headPitch = (Math.cos(headLeadPhase) * 0.14 + Math.cos(headLeadPhase * 2.0) * 0.035) * forwardDir;

      // Dynamic aim tracking towards holo target drone
      if (this._droneRenderX !== null && this._droneRenderY !== null) {
        const ddx = (this._droneRenderX - renderX);
        const ddy = (this._droneRenderY - (renderY + hoverY));
        if (ddx > 0) {
          const aimAngle = Math.atan2(ddy, ddx);
          const clampedAim = Math.max(-0.25, Math.min(0.25, aimAngle));
          headPitch += clampedAim;
        }
      }

      // A. Trailing Caudal S-Curve Tail
      ctx.save();
      const tailPivotX = renderX + ((10 - 11.5) / 24) * drawSize * forwardDir + tailShiftX + tailSurgeX;
      const tailPivotY = (renderY + hoverY) + ((10 - 11.5) / 24) * drawSize;
      ctx.translate(tailPivotX, tailPivotY + tailWaveY);
      ctx.rotate(tailPitch);
      ctx.translate(-tailPivotX, -(tailPivotY + tailWaveY));

      if (tailSrc) {
        ctx.drawImage(
          tailSrc,
          renderX - drawSize / 2 + tailShiftX + tailSurgeX + recoilOffsetX,
          (renderY + hoverY + tailWaveY) - drawSize / 2,
          drawSize,
          drawSize
        );
      }

      // Ethereal Caudal Fin Ray Tendrils
      const finTipX = renderX + ((1 - 11.5) / 24) * drawSize * forwardDir + tailShiftX + tailSurgeX + recoilOffsetX;
      const finTipY = (renderY + hoverY + tailWaveY) + ((8 - 11.5) / 24) * drawSize;
      const finWhip = Math.sin(t * 3.4) * 5.0;
      ctx.strokeStyle = 'rgba(253, 224, 71, 0.85)';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(finTipX, finTipY);
      ctx.bezierCurveTo(
        finTipX - 12 * forwardDir, finTipY + finWhip * 0.4,
        finTipX - 22 * forwardDir, finTipY - finWhip * 0.6,
        finTipX - 34 * forwardDir, finTipY + finWhip
      );
      ctx.stroke();
      ctx.restore();

      // B. Serpentine Body Chassis
      ctx.save();
      ctx.translate(renderX + bodySurgeX + recoilOffsetX, renderY + hoverY + bodyWaveY);
      ctx.rotate(bodyPitch);
      ctx.translate(-(renderX + bodySurgeX + recoilOffsetX), -(renderY + hoverY + bodyWaveY));

      // Living Muscular Pelvis & Tail Bridge
      ctx.strokeStyle = '#1c1917';
      ctx.lineWidth = drawSize * 0.13;
      ctx.beginPath();
      const bodyPelvisX = renderX + ((5.5 - 11.5) / 24) * drawSize * forwardDir + bodySurgeX + recoilOffsetX;
      const bodyPelvisY = renderY + hoverY + bodyWaveY + ((11 - 11.5) / 24) * drawSize;
      ctx.moveTo(bodyPelvisX, bodyPelvisY);
      ctx.quadraticCurveTo(
        (bodyPelvisX + tailPivotX) / 2,
        Math.min(bodyPelvisY, tailPivotY + tailWaveY) - 3.0,
        tailPivotX,
        tailPivotY + tailWaveY
      );
      ctx.stroke();

      // Gold Sinew Thread on Pelvis Bridge
      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.moveTo(bodyPelvisX, bodyPelvisY - 0.5);
      ctx.quadraticCurveTo(
        (bodyPelvisX + tailPivotX) / 2,
        Math.min(bodyPelvisY, tailPivotY + tailWaveY) - 4.5,
        tailPivotX,
        tailPivotY + tailWaveY - 0.5
      );
      ctx.stroke();

      // Golden Vertebrae Spines along Pelvic Bridge
      for (let v = 1; v <= 3; v++) {
        const u = v / 4;
        const vx = bodyPelvisX + (tailPivotX - bodyPelvisX) * u;
        const vy = bodyPelvisY + (tailPivotY + tailWaveY - bodyPelvisY) * u - 3.0;
        ctx.fillStyle = '#fde047';
        ctx.fillRect(vx - 1.2, vy - 1.2, 2.4, 2.4);
      }

      if (bodySrc) {
        ctx.drawImage(
          bodySrc,
          renderX - drawSize / 2 + bodySurgeX + recoilOffsetX,
          (renderY + hoverY + bodyWaveY) - drawSize / 2,
          drawSize,
          drawSize
        );
      }

      // Coarse Fibrous Mane Tufts along Dorsal Crest
      for (let m = 0; m < 5; m++) {
        const mu = m / 4;
        const mgx = 6.5 + mu * 9.5;
        const mBaseX = renderX + ((mgx - 11.5) / 24) * drawSize * forwardDir + bodySurgeX + recoilOffsetX;
        const mRowY = 8.2 - Math.sin(mu * Math.PI) * 1.6;
        const mBaseY = renderY + hoverY + bodyWaveY + ((mRowY - 11.5) / 24) * drawSize;
        const mFlutter = Math.sin(t * 4.2 - m * 0.75) * 4.0;
        const mLen = 12 + (m % 2) * 6;
        const mTipX = mBaseX - (mLen * forwardDir);
        const mTipY = mBaseY - 8 + mFlutter;

        ctx.strokeStyle = '#1c1917';
        ctx.lineWidth = 2.4;
        ctx.beginPath();
        ctx.moveTo(mBaseX, mBaseY);
        ctx.quadraticCurveTo(mBaseX - (mLen * 0.45 * forwardDir), mBaseY - 6, mTipX, mTipY);
        ctx.stroke();

        ctx.strokeStyle = (m % 2 === 0 ? '#fde047' : '#ca8a04');
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(mBaseX + 0.5 * forwardDir, mBaseY);
        ctx.quadraticCurveTo(mBaseX - (mLen * 0.45 * forwardDir) + 0.5, mBaseY - 6.5, mTipX + 1.0 * forwardDir, mTipY - 0.5);
        ctx.stroke();
      }

      // Shaggy Fibrous Fur Fringes hanging from Belly Dip
      for (let b = 0; b < 4; b++) {
        const bu = b / 3;
        const bgx = 7.5 + bu * 6.5;
        const bBaseX = renderX + ((bgx - 11.5) / 24) * drawSize * forwardDir + bodySurgeX + recoilOffsetX;
        const bRowY = 14.5 + Math.sin(bu * Math.PI) * 1.5;
        const bBaseY = renderY + hoverY + bodyWaveY + ((bRowY - 11.5) / 24) * drawSize;
        const bSway = Math.sin(t * 3.6 - b * 0.9) * 3.6;
        const bLen = 11 + (b % 2) * 6;
        const bTipX = bBaseX - (bLen * 1.2 * forwardDir);
        const bTipY = bBaseY + 8 + bSway;

        ctx.strokeStyle = '#292524';
        ctx.lineWidth = 2.0;
        ctx.beginPath();
        ctx.moveTo(bBaseX, bBaseY);
        ctx.quadraticCurveTo(bBaseX - (bLen * 0.5 * forwardDir), bBaseY + 4, bTipX, bTipY);
        ctx.stroke();

        ctx.strokeStyle = '#eab308';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(bBaseX, bBaseY);
        ctx.quadraticCurveTo(bBaseX - (bLen * 0.5 * forwardDir), bBaseY + 4.5, bTipX, bTipY);
        ctx.stroke();
      }

      // Iconic Golden Sand Cascade from belly
      const dustPhase = (t * 1.8) % 1.0;
      for (let d = 0; d < 5; d++) {
        const dOffset = (dustPhase + d * 0.20) % 1.0;
        const dSpanX = renderX - ((6 + dOffset * 24) * forwardDir) + bodySurgeX + recoilOffsetX;
        const dSpanY = (renderY + hoverY + bodyWaveY) + 18 + (dOffset * 28);
        const dAlpha = (1.0 - dOffset) * 0.85;
        ctx.fillStyle = `rgba(254, 240, 138, ${dAlpha})`;
        ctx.fillRect(dSpanX - 1.0, dSpanY - 1.0, 2.0, 2.0);
      }
      ctx.restore();

      // C. Living Muscular Ventral Throat Ridge
      const bodyChestX = renderX + ((16.5 - 11.5) / 24) * drawSize * forwardDir + bodySurgeX + recoilOffsetX;
      const bodyChestY = renderY + hoverY + bodyWaveY + ((11.5 - 11.5) / 24) * drawSize;
      const headThroatX = renderX + ((14.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const headThroatY = (renderY + hoverY + bodyWaveY + headBobY) + ((12 - 11.5) / 24) * drawSize;

      ctx.strokeStyle = '#1c1917';
      ctx.lineWidth = drawSize * 0.08;
      ctx.beginPath();
      ctx.moveTo(bodyChestX, bodyChestY);
      ctx.quadraticCurveTo(
        (bodyChestX + headThroatX) / 2,
        Math.max(bodyChestY, headThroatY) + 1.5,
        headThroatX,
        headThroatY
      );
      ctx.stroke();

      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(bodyChestX, bodyChestY + 1.0);
      ctx.quadraticCurveTo(
        (bodyChestX + headThroatX) / 2,
        Math.max(bodyChestY, headThroatY) + 2.5,
        headThroatX,
        headThroatY + 1.0
      );
      ctx.stroke();

      // D. Fused 3D Megalithic Pyramid
      const pyramidPivotX = renderX + ((13.5 - 11.5) / 24) * drawSize * forwardDir + pyramidShiftX + pyramidSurgeX + recoilOffsetX;
      const pyramidPivotY = (renderY + hoverY + pyramidWaveY) + ((9 - 11.5) / 24) * drawSize;

      ctx.save();
      ctx.translate(pyramidPivotX, pyramidPivotY);
      ctx.rotate(pyramidPitch);
      ctx.scale(pyramidScale, pyramidScale);
      ctx.translate(-pyramidPivotX, -pyramidPivotY);

      if (pyramidSrc) {
        ctx.drawImage(
          pyramidSrc,
          renderX - drawSize / 2 + pyramidShiftX + pyramidSurgeX + recoilOffsetX,
          (renderY + hoverY + pyramidWaveY) - drawSize / 2,
          drawSize,
          drawSize
        );
      }
      ctx.restore();

      // E. Autonomous Articulated Dragon Skull
      const neckJointX = renderX + ((14 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const neckJointY = (renderY + hoverY + bodyWaveY) + ((10 - 11.5) / 24) * drawSize;

      ctx.save();
      ctx.translate(neckJointX, neckJointY + headBobY);
      ctx.rotate(headPitch);
      ctx.translate(-neckJointX, -(neckJointY + headBobY));

      if (headSrc) {
        ctx.drawImage(
          headSrc,
          renderX - drawSize / 2 + headShiftX + headSurgeX + recoilOffsetX,
          (renderY + hoverY + bodyWaveY + headBobY) - drawSize / 2,
          drawSize,
          drawSize
        );
      }

      // Flowing Celestial Whisker Barbels
      const chinX = renderX + ((18.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const chinY = (renderY + hoverY + bodyWaveY + headBobY) + ((13.5 - 11.5) / 24) * drawSize;

      // Barbel 1
      const b1WaveA = Math.sin(t * 3.6) * 3.0;
      const b1WaveB = Math.cos(t * 7.2) * 1.8;
      const b1TipX = chinX - (22 * forwardDir);
      const b1TipY = chinY + 12 + b1WaveA + b1WaveB;
      const b1Ctrl1X = chinX - (6 * forwardDir);
      const b1Ctrl1Y = chinY + 13 + b1WaveA * 0.5;
      const b1Ctrl2X = chinX - (15 * forwardDir);
      const b1Ctrl2Y = chinY + 7 - b1WaveA * 0.4;

      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(chinX, chinY);
      ctx.bezierCurveTo(b1Ctrl1X, b1Ctrl1Y, b1Ctrl2X, b1Ctrl2Y, b1TipX, b1TipY);
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(b1TipX, b1TipY, 1.2, 0, Math.PI * 2);
      ctx.fill();

      // Barbel 2
      const b2WaveA = Math.sin(t * 4.2 + 1.2) * 2.5;
      const b2TipX = chinX - (16 * forwardDir);
      const b2TipY = chinY + 16 + b2WaveA;
      const b2Ctrl1X = chinX - (4 * forwardDir);
      const b2Ctrl1Y = chinY + 9;
      const b2Ctrl2X = chinX - (10 * forwardDir);
      const b2Ctrl2Y = chinY + 14 + b2WaveA * 0.6;

      ctx.strokeStyle = '#eab308';
      ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.moveTo(chinX - (1 * forwardDir), chinY + 1);
      ctx.bezierCurveTo(b2Ctrl1X, b2Ctrl1Y, b2Ctrl2X, b2Ctrl2Y, b2TipX, b2TipY);
      ctx.stroke();

      // Horn Tip Specular Gleam
      const hornTipX = renderX + ((23.0 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const hornTipY = (renderY + hoverY + bodyWaveY + headBobY) + ((9.0 - 11.5) / 24) * drawSize;
      const hornGlint = 0.5 + 0.5 * Math.sin(t * 3.5 + 0.8);
      ctx.fillStyle = `rgba(255, 255, 255, ${0.4 + 0.5 * hornGlint})`;
      ctx.beginPath();
      ctx.arc(hornTipX, hornTipY, 1.2 * (0.8 + 0.4 * hornGlint), 0, Math.PI * 2);
      ctx.fill();

      // Glowing Amber Compound Eyes Glints
      const eyeShimmer = 0.6 + 0.4 * Math.sin(t * 4.2 + 1.2);
      const eye1X = renderX + ((16.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const eye1Y = (renderY + hoverY + bodyWaveY + headBobY) + ((6.8 - 11.5) / 24) * drawSize;
      const eye2X = renderX + ((18.2 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const eye2Y = (renderY + hoverY + bodyWaveY + headBobY) + ((7.8 - 11.5) / 24) * drawSize;
      ctx.fillStyle = `rgba(255, 255, 255, ${0.75 * eyeShimmer})`;
      ctx.beginPath();
      ctx.arc(eye1X, eye1Y, 0.9, 0, Math.PI * 2);
      ctx.arc(eye2X, eye2Y, 0.9, 0, Math.PI * 2);
      ctx.fill();

      // Golden sand cascade pouring downwards from maw
      const mawX = renderX + ((19.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const mawY = (renderY + hoverY + bodyWaveY + headBobY) + ((12.0 - 11.5) / 24) * drawSize;
      const sandPal = ['#fef08a', '#fde047', '#facc15', '#ca8a04', '#ffffff'];
      for (let sIdx = 0; sIdx < 5; sIdx++) {
        const sPhase = (t * 2.6 + sIdx * 0.2) % 1.0;
        const sAlpha = Math.sin(sPhase * Math.PI) * 0.85;
        const dropDist = sPhase * 20.0;
        const driftBack = (sPhase * sPhase * 12.0 + Math.sin(sPhase * 6.0 + sIdx) * 2.0) * forwardDir;
        const sx = mawX - driftBack;
        const sy = mawY + dropDist;
        const sRadius = sIdx === 4 ? 0.9 : (sIdx % 2 === 0 ? 1.2 : 1.0);
        ctx.fillStyle = sandPal[sIdx % sandPal.length];
        ctx.globalAlpha = sAlpha;
        ctx.beginPath();
        ctx.arc(sx, sy, sRadius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1.0;

      ctx.restore();

      // F. Radiant Solar Apex Star on Pyramid Capstone
      const scaledApexDistY = ((0.5 - 9) / 24) * drawSize * pyramidScale;
      const cosP = Math.cos(pyramidPitch);
      const sinP = Math.sin(pyramidPitch);
      const apexPx = pyramidPivotX - scaledApexDistY * sinP;
      const apexPy = pyramidPivotY + scaledApexDistY * cosP;

      this._lootingVultureApexX = apexPx;
      this._lootingVultureApexY = apexPy;

      const isAttackingFlare = (elapsedRecoil >= 0 && elapsedRecoil < 140);
      const flarePulse = (isAttackingFlare ? 1.8 : (0.75 + 0.25 * Math.sin(t * 4.5)));
      const flareR = drawSize * 0.12 * flarePulse * pyramidScale;

      const flareGrad = ctx.createRadialGradient(apexPx, apexPy, 1, apexPx, apexPy, flareR);
      flareGrad.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
      flareGrad.addColorStop(0.3, 'rgba(254, 240, 138, 0.80)');
      flareGrad.addColorStop(0.65, 'rgba(234, 179, 8, 0.35)');
      flareGrad.addColorStop(1, 'rgba(217, 119, 6, 0)');
      ctx.fillStyle = flareGrad;
      ctx.beginPath();
      ctx.arc(apexPx, apexPy, flareR, 0, Math.PI * 2);
      ctx.fill();

      // 4-Point Anamorphic Star Cross
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(apexPx - flareR * 1.2, apexPy);
      ctx.lineTo(apexPx + flareR * 1.2, apexPy);
      ctx.moveTo(apexPx, apexPy - flareR * 1.2);
      ctx.lineTo(apexPx, apexPy + flareR * 1.2);
      ctx.stroke();

      // Secondary diagonal glints
      ctx.strokeStyle = 'rgba(254, 240, 138, 0.7)';
      ctx.lineWidth = 1.0;
      const diagR = flareR * 0.65;
      ctx.beginPath();
      ctx.moveTo(apexPx - diagR, apexPy - diagR);
      ctx.lineTo(apexPx + diagR, apexPy + diagR);
      ctx.moveTo(apexPx - diagR, apexPy + diagR);
      ctx.lineTo(apexPx + diagR, apexPy - diagR);
      ctx.stroke();

      // Ambient Shimmering Gold Dust vortices
      const shimmerPhase = (t * 2.5) % 1.0;
      for (let s = 0; s < 4; s++) {
        const sOffset = (shimmerPhase + s * 0.25) % 1.0;
        const vortexSpin = Math.sin(sOffset * Math.PI * 3.0) * 4.0;
        const sx = renderX - ((8 + sOffset * 26) * forwardDir) + pyramidShiftX + pyramidSurgeX + recoilOffsetX;
        const sy = (renderY + hoverY + pyramidWaveY) + 8 + (sOffset * 20) + vortexSpin;
        const sAlpha = (1.0 - sOffset) * 0.75;
        ctx.fillStyle = `rgba(254, 240, 138, ${sAlpha})`;
        ctx.fillRect(sx - 0.9, sy - 0.9, 1.8, 1.8);
      }

      ctx.restore(); // restores flightBank / translation

      muzzleX = apexPx;
      muzzleY = apexPy;
    } else if (isEnforcerVulture) {
      // 𓅂 Sul Enforcer Vulture: Articulated Head & Colossal Recurved Sickle Tusks
      const bodyBitmap = buildSpriteBitmap('EnforcerVulture_Body', currentUnit.race, 'western');
      const headBitmap = buildSpriteBitmap('EnforcerVulture_Head', currentUnit.race, 'western');
      const bodySrc = (isFlash && bodyBitmap?.flash) ? bodyBitmap.flash : bodyBitmap?.normal;
      const headSrc = (isFlash && headBitmap?.flash) ? headBitmap.flash : headBitmap?.normal;

      const hoverY = Math.sin(t * 1.6) * 3.5;
      const forwardDir = 1;
      const renderX = pedestalX;
      const renderY = pedestalY - spriteDisplaySize * 0.46;
      const drawSize = spriteDisplaySize;

      ctx.save();
      ctx.translate(renderX, renderY + hoverY);

      if (bodySrc) {
        ctx.drawImage(bodySrc, -drawSize / 2 + recoilOffsetX, -drawSize / 2, drawSize, drawSize);
      }

      // Dorsal Skeletal Housing & 8 Bio-Rocket Spheres (Carapace mounting rails & pods)
      ctx.strokeStyle = '#78350f';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      for (let j = 0; j < 4; j++) {
        const pFar = VULTURE_POD_SPINE_OFFSETS[j * 2];
        const pNear = VULTURE_POD_SPINE_OFFSETS[j * 2 + 1];
        const px = recoilOffsetX + ((pFar.gx - 23.5) / 48) * drawSize * forwardDir;
        const py1 = ((pFar.gy - 23.5) / 48) * drawSize;
        const py2 = ((pNear.gy - 23.5) / 48) * drawSize;
        ctx.moveTo(px, py1);
        ctx.lineTo(px, py2);
      }
      ctx.stroke();

      const baseBallR = drawSize * 0.052;
      for (let orderIdx = 0; orderIdx < 8; orderIdx++) {
        const i = VULTURE_RENDER_POD_ORDER[orderIdx];
        const pPos = VULTURE_POD_SPINE_OFFSETS[i];
        const isFar = pPos.isFar;
        const podX = recoilOffsetX + ((pPos.gx - 23.5) / 48) * drawSize * forwardDir;
        const podY = ((pPos.gy - 23.5) / 48) * drawSize;
        const colIdx = Math.floor(i / 2);

        const curBaseR = isFar ? baseBallR * 0.88 : baseBallR * 1.0;
        const ballPulse = 1.0 + 0.03 * Math.sin(t * 2.0 - colIdx * 0.65);
        const curR = curBaseR * ballPulse;
        const seatedY = podY + 0.3;

        // Bioluminescent corona halo
        const haloPulse = Math.sin(t * 2.0 - colIdx * 0.65);
        const haloAlpha = isFar ? (0.16 + 0.06 * haloPulse) : (0.26 + 0.10 * haloPulse);
        ctx.fillStyle = isFar ? `rgba(225, 29, 72, ${haloAlpha})` : `rgba(255, 0, 50, ${haloAlpha})`;
        ctx.beginPath();
        ctx.arc(podX, seatedY, curR * (isFar ? 1.35 : 1.5), 0, Math.PI * 2);
        ctx.fill();

        // Socket well
        ctx.fillStyle = isFar ? '#2e1805' : '#451a03';
        ctx.beginPath();
        ctx.arc(podX, podY, curBaseR + (isFar ? 1.2 : 1.6), 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = isFar ? '#78350f' : '#b45309';
        ctx.lineWidth = isFar ? 0.7 : 1.0;
        ctx.stroke();

        ctx.fillStyle = '#140a02';
        ctx.beginPath();
        ctx.arc(podX, podY, curBaseR + 0.3, 0, Math.PI * 2);
        ctx.fill();

        // 3D Spherical Bio-Ball
        const radGrad = ctx.createRadialGradient(podX - curR * 0.3, seatedY - curR * 0.35, curR * 0.1, podX, seatedY, curR);
        radGrad.addColorStop(0.0, '#ffffff');
        radGrad.addColorStop(0.25, isFar ? '#fda4af' : '#ff6b8b');
        radGrad.addColorStop(0.65, isFar ? '#be123c' : '#ff0033');
        radGrad.addColorStop(1.0, isFar ? '#4c0519' : '#450a0a');
        ctx.fillStyle = radGrad;
        ctx.beginPath();
        ctx.arc(podX, seatedY, curR, 0, Math.PI * 2);
        ctx.fill();

        // Specular glint
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.beginPath();
        ctx.arc(podX - curR * 0.28, seatedY - curR * 0.32, Math.max(0.7, curR * 0.26), 0, Math.PI * 2);
        ctx.fill();

        // Cavity rim occlusion
        ctx.strokeStyle = isFar ? 'rgba(31, 17, 3, 0.75)' : 'rgba(46, 24, 5, 0.65)';
        ctx.lineWidth = 0.85;
        ctx.beginPath();
        ctx.arc(podX, podY, curBaseR + 0.05, 1.1 * Math.PI, 1.9 * Math.PI);
        ctx.stroke();
      }

      // Articulated Head
      const headBobY = Math.sin(t * 2.2) * 2.0;
      let headPitch = Math.sin(t * 1.8) * 0.04;
      if (this._droneRenderX !== null && this._droneRenderY !== null) {
        const ddx = (this._droneRenderX - renderX);
        const ddy = (this._droneRenderY - (renderY + hoverY));
        if (ddx > 0) {
          headPitch += Math.max(-0.25, Math.min(0.25, Math.atan2(ddy, ddx)));
        }
      }

      const headPivotX = drawSize * 0.12 + recoilOffsetX;
      const headPivotY = drawSize * 0.05 + headBobY;
      ctx.save();
      ctx.translate(headPivotX, headPivotY);
      ctx.rotate(headPitch);
      ctx.translate(-headPivotX, -headPivotY);

      if (headSrc) {
        ctx.drawImage(headSrc, -drawSize / 2 + recoilOffsetX, -drawSize / 2 + headBobY, drawSize, drawSize);
      }

      // Colossal Recurved Sickle Tusks / Mandibles
      const jawBaseX = headPivotX + (12 * forwardDir);
      const jawBaseY = headPivotY + 4;
      const tuskFlex = Math.sin(t * 2.0) * 1.8;

      // Primary Outer Sickle Mandible
      const m1KneeX = jawBaseX + (16 * forwardDir);
      const m1KneeY = jawBaseY + 24;
      const m1TipX = jawBaseX - ((20 + tuskFlex) * forwardDir);
      const m1TipY = jawBaseY + 36;

      ctx.strokeStyle = '#261002';
      ctx.lineWidth = 4.5;
      ctx.beginPath();
      ctx.moveTo(jawBaseX, jawBaseY);
      ctx.quadraticCurveTo(m1KneeX, m1KneeY, m1TipX, m1TipY);
      ctx.stroke();

      ctx.strokeStyle = '#d97706';
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(jawBaseX, jawBaseY);
      ctx.quadraticCurveTo(m1KneeX, m1KneeY, m1TipX, m1TipY);
      ctx.stroke();

      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(jawBaseX + 0.8 * forwardDir, jawBaseY);
      ctx.quadraticCurveTo(m1KneeX + 1.2 * forwardDir, m1KneeY + 0.6, m1TipX, m1TipY);
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(m1TipX, m1TipY, 2.0, 0, Math.PI * 2);
      ctx.fill();

      // Secondary Inner Recurved Mandible
      const m2KneeX = jawBaseX + (10 * forwardDir);
      const m2KneeY = jawBaseY + 19;
      const m2TipX = jawBaseX - ((14 + tuskFlex * 0.7) * forwardDir);
      const m2TipY = jawBaseY + 29;

      ctx.strokeStyle = '#261002';
      ctx.lineWidth = 3.2;
      ctx.beginPath();
      ctx.moveTo(jawBaseX - 3 * forwardDir, jawBaseY + 2);
      ctx.quadraticCurveTo(m2KneeX, m2KneeY, m2TipX, m2TipY);
      ctx.stroke();

      ctx.strokeStyle = '#b45309';
      ctx.lineWidth = 2.0;
      ctx.beginPath();
      ctx.moveTo(jawBaseX - 3 * forwardDir, jawBaseY + 2);
      ctx.quadraticCurveTo(m2KneeX, m2KneeY, m2TipX, m2TipY);
      ctx.stroke();

      ctx.strokeStyle = '#fde68a';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(jawBaseX - 2 * forwardDir, jawBaseY + 2);
      ctx.quadraticCurveTo(m2KneeX + 0.6 * forwardDir, m2KneeY, m2TipX, m2TipY);
      ctx.stroke();

      ctx.fillStyle = '#fde68a';
      ctx.beginPath();
      ctx.arc(m2TipX, m2TipY, 1.5, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore(); // restores head transform
      ctx.restore(); // restores body transform

      const launchPod = VULTURE_POD_SPINE_OFFSETS[7]; // near front launch pod
      muzzleX = renderX + recoilOffsetX + ((launchPod.gx - 23.5) / 48) * drawSize * forwardDir;
      muzzleY = renderY + hoverY + ((launchPod.gy - 23.5) / 48) * drawSize;
      this._enforcerMuzzleX = muzzleX;
      this._enforcerMuzzleY = muzzleY;
    } else if (isProcessingPillar) {
      // 𓉐 SUL PROCESSING PILLAR LIVING BIO-ARCHETYPE RIG:
      // Profile view, floating levitation, gleaming compound ocular corona & god rays,
      // 5 active tentacle arms with luminous tip appendages, launch discharge, and 4 spindly ground feelers
      const pillarSize = spriteDisplaySize * 1.008; // 4% kleiner (1.05 * 0.96 = 1.008)
      const hoverWave = Math.sin(t * 1.6) * 3.6 + Math.sin(t * 0.8) * 1.2;
      const breathPulse = Math.sin(t * 1.0);
      const stretchY = 1.0 + breathPulse * 0.022;
      const squashX = 1.0 - breathPulse * 0.016;

      const isFiring = (elapsedRecoil >= 0 && elapsedRecoil < 240);
      const pillarX = spriteX + spriteDisplaySize / 2 + recoilOffsetX;
      const pillarY = spriteY + spriteDisplaySize * 0.48 + hoverWave;

      // 1. Soft bio-plasma contact shadow on holographic pedestal
      ctx.save();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
      ctx.beginPath();
      ctx.ellipse(pedestalX + recoilOffsetX * 0.5, pedestalY, 68 * squashX, 22 * stretchY, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // 2. Render Authentic 36x36 HD Dual-Layer Rig:
      // - Dynamic Compound Ocular Matrix & Forward God Rays
      // - 5 Massive Active Tentacle Arms with Luminous Appendages
      const isCharging = (elapsedRecoil >= 120 && elapsedRecoil < 240);
      const isAttack = (elapsedRecoil >= 0 && elapsedRecoil < 120);
      const chargeProg = isCharging ? ((240 - elapsedRecoil) / 120) : (isAttack ? 1.0 : 0);

      const isFiringCombat = (elapsedRecoil >= 0 && elapsedRecoil < 240);
      if (this._srProcessingBlend === undefined) {
        this._srProcessingBlend = isFiringCombat ? 1.0 : 0.0;
      }
      const targetSrBlend = isFiringCombat ? 1.0 : 0.0;
      const srBlendSpeed = targetSrBlend > this._srProcessingBlend ? 0.20 : 0.09;
      this._srProcessingBlend += (targetSrBlend - this._srProcessingBlend) * srBlendSpeed;
      if (Math.abs(this._srProcessingBlend - targetSrBlend) < 0.005) {
        this._srProcessingBlend = targetSrBlend;
      }
      const srPoseBlend = Math.max(0, Math.min(1, this._srProcessingBlend));

      drawProcessingPillarRig(ctx, {
        x: pillarX,
        y: pillarY,
        size: pillarSize,
        dir: 1,
        team: currentUnit.race === 'Sul' ? 'western' : 'western',
        isHitFlashing: isFlash,
        isCharging: isCharging,
        isAttack: isAttack,
        isAir: false,
        chargeProgress: chargeProg,
        blendFactor: srPoseBlend,
        time: t,
        wobble: 0,
        quality: 'high',
        bulletColorHex: activeBulletColorHex || '#38bdf8'
      });

      // 3. Plasma Launch Discharge Ring at Muzzle
      const pixelScale = pillarSize / 36.0;
      const chargeFocusX = pillarX + (26 * pixelScale);
      const chargeFocusY = pillarY - (6 * pixelScale);

      if (elapsedRecoil >= 0 && elapsedRecoil < 140) {
        const dischargeProg = elapsedRecoil / 140;
        ctx.save();
        ctx.strokeStyle = activeBulletColorHex || '#67e8f9';
        ctx.lineWidth = 2.4;
        ctx.globalAlpha = (1.0 - dischargeProg) * 0.85;
        ctx.beginPath();
        ctx.arc(chargeFocusX, chargeFocusY, 12 + dischargeProg * 18, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // 4. 4 Long Thin Ground Feelers with Fluid Wave Motion
      const groundLevel = pedestalY + 8;
      ctx.save();
      ctx.strokeStyle = isFlash ? '#ffffff' : '#3d2518';
      ctx.lineWidth = isFlash ? 1.6 : 1.2;

      for (let f = 0; f < 4; f++) {
        const root = PROCESSING_FEELER_ROOTS[f];
        const rX = pillarX + (pillarSize * root.dx);
        const rY = pillarY + (pillarSize * root.dy);
        const feelerHeight = Math.max(10, groundLevel - rY);

        _srProcessingFeelersScratch[f][0].x = rX;
        _srProcessingFeelersScratch[f][0].y = rY;

        ctx.beginPath();
        ctx.moveTo(rX, rY);

        let curX = rX;
        let curY = rY;
        for (let s = 1; s <= 6; s++) {
          const segProg = s / 6;
          const baseY = rY + feelerHeight * segProg;
          const calmWave = Math.sin(t * 2.2 + f * 1.1 + s * 0.8) * 3.5 * segProg;
          curX = rX + calmWave;
          curY = baseY;
          _srProcessingFeelersScratch[f][s].x = curX;
          _srProcessingFeelersScratch[f][s].y = curY;
          ctx.lineTo(curX, curY);
        }
        ctx.stroke();

        ctx.fillStyle = isFlash ? '#ffffff' : '#67e8f9';
        ctx.globalAlpha = isFlash ? 1.0 : 0.55;
        ctx.fillRect(curX - 1, curY - 1, 2, 2);
        ctx.globalAlpha = 1.0;
      }
      ctx.restore();

      muzzleX = chargeFocusX;
      muzzleY = chargeFocusY;
      this._processingMuzzleX = muzzleX;
      this._processingMuzzleY = muzzleY;
    } else if (isSentinalLock) {
      // 🦇 SUL SENTINAL LOCK: Three-Winged Alien Bat "Spitfire", 3D Axial Barrel Roll
      const bodyBitmap = buildSpriteBitmap('SentinalLock_Body', currentUnit.race, 'western');
      const wingBitmap = buildSpriteBitmap('SentinalLock_WingPanel', currentUnit.race, 'western');
      const bodySrc = (isFlash && bodyBitmap?.flash) ? bodyBitmap.flash : bodyBitmap?.normal;
      const wingSrc = (isFlash && wingBitmap?.flash) ? wingBitmap.flash : wingBitmap?.normal;

      const drawSize = spriteDisplaySize;
      const hoverY = Math.sin(t * 1.8) * 5.0;
      const flightBank = Math.sin(t * 1.2) * 0.035;
      const renderX = pedestalX + recoilOffsetX;
      const renderY = pedestalY - spriteDisplaySize * 0.44 + hoverY;

      // 1. Soft downwash air shadow on holographic pedestal
      ctx.save();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
      ctx.beginPath();
      ctx.ellipse(pedestalX, pedestalY, 52, 16, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.save();
      ctx.translate(renderX, renderY);
      ctx.rotate(flightBank);

      // 2. 3-Wing Barrel Roll Kinematics around flight axis
      const rollBase = t * 1.6;
      const attachX = 0;
      const attachY = -drawSize * 0.042;
      const wingOffsetX = -drawSize * 0.5;
      const wingOffsetY = -drawSize * 0.9167;

      const drawWing = (phase) => {
        const s = Math.cos(phase);
        if (Math.abs(s) < 0.03) return; // edge-on
        ctx.save();
        ctx.translate(attachX, attachY);
        ctx.scale(1, s);
        if (wingSrc) {
          ctx.drawImage(wingSrc, wingOffsetX, wingOffsetY, drawSize, drawSize);
        }
        ctx.restore();
      };

      const phase0 = rollBase;
      const phase1 = rollBase + (Math.PI * 2 / 3);
      const phase2 = rollBase + (Math.PI * 4 / 3);

      // Wings swung away from camera go behind fuselage
      if (Math.sin(phase0) <= 0) drawWing(phase0);
      if (Math.sin(phase1) <= 0) drawWing(phase1);
      if (Math.sin(phase2) <= 0) drawWing(phase2);

      // 3. Body Fuselage (Nose stays facing forward)
      if (bodySrc) {
        ctx.drawImage(bodySrc, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
      }

      // 4. Glimmender lila Schnauzen-Tip (Pulsing additive glow)
      const tipGlowPulse = 0.65 + 0.35 * Math.sin(t * 3.4);
      const tipGlowR = drawSize * 0.12;
      const tipX = drawSize * 0.31;
      const tipY = -drawSize * 0.06;
      const tipGlowTex = getSentinalLockHeadGlowCanvas();
      if (tipGlowTex) {
        ctx.save();
        ctx.globalAlpha = 0.85 * tipGlowPulse;
        ctx.drawImage(tipGlowTex, tipX - tipGlowR, tipY - tipGlowR, tipGlowR * 2, tipGlowR * 2);
        ctx.restore();
      }

      // 5. Vestigial tail tentacle stubs (3 tiny swaying appendages)
      ctx.save();
      ctx.lineCap = 'round';
      const tailAttachX = -drawSize * 0.37;
      const tailAttachY = drawSize * 0.24;
      for (let c = 0; c < 3; c++) {
        const stubOffX = (c - 1) * (drawSize * 0.045);
        const stubPhase = t * 5.5 + rollBase * 0.4 + c * 2.1;
        const stubLen = drawSize * 0.06;
        const stubSwing = Math.sin(stubPhase) * (drawSize * 0.09);
        const b1X = tailAttachX + stubOffX + stubSwing * 0.25;
        const b1Y = tailAttachY;
        const b2X = b1X - stubSwing;
        const b2Y = b1Y + stubLen;

        ctx.strokeStyle = (c % 2 === 0) ? '#7c2d12' : '#9a3412';
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(b1X, b1Y);
        ctx.lineTo(b2X, b2Y);
        ctx.stroke();
      }
      ctx.restore();

      // 6. Venomous spit burst at snout during attack
      if (elapsedRecoil >= 0 && elapsedRecoil < 90) {
        ctx.fillStyle = '#4d7c0f';
        ctx.fillRect(tipX + 2, tipY + 4, 4, 4);
        ctx.fillStyle = activeBulletColorHex || '#eaff00';
        ctx.fillRect(tipX + 4, tipY + 5, 3, 2);
      }

      // Wings swung toward camera go on top of fuselage
      if (Math.sin(phase0) > 0) drawWing(phase0);
      if (Math.sin(phase1) > 0) drawWing(phase1);
      if (Math.sin(phase2) > 0) drawWing(phase2);

      ctx.restore();

      muzzleX = renderX + drawSize * 0.36;
      muzzleY = renderY - drawSize * 0.04;
      this._sentinalMuzzleX = muzzleX;
      this._sentinalMuzzleY = muzzleY;
    } else if (isPulp) {
      // 🤡 Sul Pulp: Low-Gravity Clown Moon-Hop with Articulated Mask, Mortar Kick & Jester Bells
      const hopCycle = ((walkPhase * 0.42) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const u = hopCycle / (Math.PI * 2);

      let legsKey = 'Pulp_Legs_Walk1';
      let hopY = 0;
      let hopTilt = 0;
      let legTilt = 0;
      let scaleX = 1.0;
      let scaleY = 1.0;
      let legScaleX = 1.0;
      let legScaleY = 1.0;
      let headPitch = 0;
      let headRoll = 0;
      let headBobY = 0;

      if (u < 0.78) {
        // Lunar airborne float
        const uAir = u / 0.78;
        const hang = Math.pow(Math.sin(uAir * Math.PI), 0.72);
        hopY = -hang * (spriteDisplaySize * (8.0 / 24.0));
        if (uAir < 0.16) {
          legsKey = 'Pulp_Legs_Walk1';
          legScaleY = 1.15; legScaleX = 0.90;
          legTilt = -0.12; hopTilt = -0.10;
          headPitch = -0.32; headRoll = -0.10; headBobY = spriteDisplaySize * (1.0 / 24.0);
        } else if (uAir < 0.35) {
          legsKey = 'Pulp_Legs_Walk2';
          const asc = (uAir - 0.16) / 0.19;
          legScaleY = 1.12; legScaleX = 0.94;
          legTilt = -0.06; hopTilt = -0.05;
          headPitch = -0.32 + asc * 0.44; headRoll = -0.10 + asc * 0.18; headBobY = (1.0 - asc * 1.6) * (spriteDisplaySize / 24.0);
        } else if (uAir < 0.58) {
          legsKey = 'Pulp_Legs_Walk3';
          const apex = (uAir - 0.35) / 0.23;
          legTilt = 0.24 * Math.sin(apex * Math.PI);
          legScaleY = 1.05; hopTilt = 0.08 * Math.sin(apex * Math.PI);
          headPitch = 0.12 + Math.sin(apex * Math.PI) * 0.24;
          headRoll = Math.sin(apex * Math.PI * 2) * 0.20;
          headBobY = -1.6 * Math.sin(apex * Math.PI) * (spriteDisplaySize / 24.0);
        } else if (uAir < 0.80) {
          legsKey = 'Pulp_Legs_Walk4';
          const sciss = (uAir - 0.58) / 0.22;
          legTilt = 0.12 * (1 - sciss); hopTilt = 0.04;
          headPitch = 0.12 - sciss * 0.22; headRoll = Math.cos(sciss * Math.PI) * 0.14;
          headBobY = -0.4 * (1 - sciss) * (spriteDisplaySize / 24.0);
        } else {
          legsKey = 'Pulp_Legs_Walk5';
          const desc = (uAir - 0.80) / 0.20;
          legTilt = 0.04 * (1 - desc); legScaleY = 1.14; legScaleX = 0.90;
          hopTilt = 0.08 * desc; headPitch = -0.10 - desc * 0.10; headRoll = -0.06 * desc; headBobY = 0.2 * desc * (spriteDisplaySize / 24.0);
        }
      } else {
        // Touchdown squash
        const uLand = (u - 0.78) / 0.22;
        legsKey = 'Pulp_Legs_Walk6';
        const squash = Math.sin(uLand * Math.PI);
        hopY = squash * (spriteDisplaySize * (1.8 / 24.0));
        scaleY = 1.0 - squash * 0.22;
        scaleX = 1.0 + squash * 0.18;
        legScaleY = 1.0 - squash * 0.32;
        legScaleX = 1.0 + squash * 0.28;
        headPitch = 0.42 * squash - 0.15 * (1 - squash);
        headBobY = squash * 2.2 * (spriteDisplaySize / 24.0);
        headRoll = Math.sin(uLand * Math.PI * 2) * 0.16;
      }

      const shootRecoil = (elapsedRecoil >= 0 && elapsedRecoil < 180) ? dampedSpringRecoil(Math.min(1, elapsedRecoil / 180)) : 0;
      const mortarKickAngle = -shootRecoil * 0.28;
      const mortarRecoilX = -shootRecoil * (spriteDisplaySize * (4.0 / 24.0));

      const legsBitmap = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
      const bodyBitmap = buildSpriteBitmap('Pulp_Body', currentUnit.race, 'western');
      const headBitmap = buildSpriteBitmap('Pulp_Head', currentUnit.race, 'western');
      const weaponBitmap = buildSpriteBitmap('Pulp_Weapon', currentUnit.race, 'western');

      const legsSrc = (isFlash && legsBitmap?.flash) ? legsBitmap.flash : legsBitmap?.normal;
      const bodySrc = (isFlash && bodyBitmap?.flash) ? bodyBitmap.flash : bodyBitmap?.normal;
      const headSrc = (isFlash && headBitmap?.flash) ? headBitmap.flash : headBitmap?.normal;
      const weaponSrc = (isFlash && weaponBitmap?.flash) ? weaponBitmap.flash : weaponBitmap?.normal;

      const renderX = spriteX + spriteDisplaySize / 2;
      const renderY = spriteY + spriteDisplaySize / 2;

      // 1. Rear Shoulder Mortar (behind body)
      if (weaponSrc) {
        ctx.save();
        const weaponPivotX = renderX - spriteDisplaySize * (2 / 24);
        const weaponPivotY = renderY - spriteDisplaySize * (3 / 24) + hopY;
        ctx.translate(weaponPivotX, weaponPivotY);
        ctx.rotate(hopTilt * 0.4 + mortarKickAngle);
        ctx.scale(scaleX, scaleY);
        ctx.translate(-weaponPivotX, -weaponPivotY);
        ctx.drawImage(weaponSrc, spriteX + mortarRecoilX, spriteY + hopY, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      // 2. Articulated Legs
      if (legsSrc) {
        ctx.save();
        const hipPivotY = renderY + hopY + spriteDisplaySize * (3 / 24);
        ctx.translate(renderX, hipPivotY);
        ctx.rotate(legTilt);
        ctx.scale(scaleX * legScaleX, scaleY * legScaleY);
        ctx.drawImage(legsSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2 - spriteDisplaySize * (3 / 24), spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      // 3. Torso & Scepter
      if (bodySrc) {
        ctx.save();
        ctx.translate(renderX, renderY + hopY);
        ctx.rotate(hopTilt);
        ctx.scale(scaleX, scaleY);
        ctx.drawImage(bodySrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      // 4. Articulated Clown Mask (Foreground)
      if (headSrc) {
        ctx.save();
        const neckPivotX = renderX - spriteDisplaySize * (1.5 / 24);
        const neckPivotY = renderY - spriteDisplaySize * (4 / 24) + hopY + headBobY;
        ctx.translate(neckPivotX, neckPivotY);
        ctx.rotate(hopTilt + headPitch + headRoll);
        ctx.scale(scaleX, scaleY);
        ctx.drawImage(headSrc, -spriteDisplaySize / 2 + spriteDisplaySize * (1.5 / 24), -spriteDisplaySize / 2 + spriteDisplaySize * (4 / 24), spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      muzzleX = spriteX + spriteDisplaySize * 0.52 + mortarRecoilX;
      muzzleY = spriteY + spriteDisplaySize * 0.22 + hopY;
      this._pulpMuzzleX = muzzleX;
      this._pulpMuzzleY = muzzleY;
    } else if (isStickman) {
      // 🦾 Stickman: Colossus Digitigrade Walker with Hydraulic Compression & Heavy Lance
      const cycle = ((walkPhase * 0.55 % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 8);
      const legsKey = STICKMAN_WALK_FRAMES[phaseIdx] || 'Stickman_Legs_Walk1';
      const walkerBob = (phaseIdx === 1 || phaseIdx === 5) ? (2.2 * stageScale) : ((phaseIdx === 3 || phaseIdx === 7) ? (-1.4 * stageScale) : 0.0);
      const chassisPitch = (phaseIdx === 0 || phaseIdx === 1) ? 0.03 : ((phaseIdx === 4 || phaseIdx === 5) ? -0.03 : 0.0);

      const torsoStridePitch = Math.sin(cycle) * 0.045;
      const torsoStrideY = Math.cos(cycle * 2) * 1.5 * stageScale;
      const torsoStrideX = Math.sin(cycle) * 1.0 * stageScale;
      const scanHarmonic = Math.sin(t * 1.65) * Math.sin(t * 0.77);
      const torsoScanPitch = scanHarmonic * 0.032;
      const torsoScaleX = 1.0 - Math.abs(scanHarmonic * 0.12) * 0.35;
      const torsoWalkPitch = torsoStridePitch + torsoScanPitch;

      const shootRecoil = (elapsedRecoil >= 0 && elapsedRecoil < 220) ? dampedSpringRecoil(Math.min(1, elapsedRecoil / 220)) : 0;
      const lanceRecoilX = -shootRecoil * 5.0 * stageScale;
      const lanceRecoilPitch = -shootRecoil * 0.15;

      const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
      const chassisBmp = buildSpriteBitmap('Stickman_Chassis', currentUnit.race, 'western');
      const torsoBmp = buildSpriteBitmap('Stickman_Torso', currentUnit.race, 'western');
      const cannonBmp = buildSpriteBitmap('Stickman_Cannon', currentUnit.race, 'western');

      const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
      const chassisSrc = (isFlash && chassisBmp?.flash) ? chassisBmp.flash : chassisBmp?.normal;
      const torsoSrc = (isFlash && torsoBmp?.flash) ? torsoBmp.flash : torsoBmp?.normal;
      const cannonSrc = (isFlash && cannonBmp?.flash) ? cannonBmp.flash : cannonBmp?.normal;

      const renderX = spriteX + spriteDisplaySize / 2;
      const renderY = spriteY + spriteDisplaySize / 2;

      // 1. Digitigrade Legs
      if (legsSrc) {
        ctx.drawImage(legsSrc, spriteX, spriteY, spriteDisplaySize, spriteDisplaySize);
      }

      // 2. Chassis with Hydraulic Bob
      if (chassisSrc) {
        ctx.save();
        ctx.translate(renderX, renderY + walkerBob);
        ctx.rotate(chassisPitch);
        ctx.drawImage(chassisSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      // 3. Upper Torso, Shoulder Carapace & Visor Head
      if (torsoSrc) {
        ctx.save();
        ctx.translate(renderX + torsoStrideX, renderY + walkerBob + torsoStrideY);
        ctx.rotate(chassisPitch + torsoWalkPitch);

        if (torsoScaleX !== 1.0) {
          const spinePivotX = spriteDisplaySize * (-4 / 48);
          ctx.translate(spinePivotX, 0);
          ctx.scale(torsoScaleX, 1.0);
          ctx.translate(-spinePivotX, 0);
        }

        ctx.drawImage(torsoSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);

        // Glowing High-Tech Visor Optic Flare
        const eyeX = spriteDisplaySize * (3.5 / 48);
        const eyeY = -spriteDisplaySize * (15.5 / 48);
        const pulse = 0.75 + 0.25 * Math.sin(t * 5.0);
        const flareR = 2.5 * pulse * (spriteDisplaySize / 48);

        ctx.save();
        ctx.globalAlpha = 0.85;
        ctx.fillStyle = '#00e5ff';
        ctx.beginPath();
        ctx.arc(eyeX, eyeY, flareR, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        ctx.restore();
      }

      // 4. Underslung Heavy Cannon
      if (cannonSrc) {
        ctx.save();
        const cannonYDrop = spriteDisplaySize * (2.2 / 48);
        const cannonPivotX = -spriteDisplaySize * (8 / 48);
        const cannonPivotY = -spriteDisplaySize * (3 / 48) + walkerBob + cannonYDrop;
        ctx.translate(renderX + cannonPivotX, renderY + cannonPivotY);
        ctx.rotate(chassisPitch + lanceRecoilPitch);
        ctx.translate(-renderX - cannonPivotX, -renderY - cannonPivotY);
        ctx.drawImage(cannonSrc, spriteX + lanceRecoilX, spriteY + walkerBob + cannonYDrop, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      muzzleX = renderX + spriteDisplaySize * (22 / 48) + lanceRecoilX;
      muzzleY = renderY - spriteDisplaySize * (4 / 48) + walkerBob + spriteDisplaySize * (2.2 / 48);
      this._stickmanMuzzleX = muzzleX;
      this._stickmanMuzzleY = muzzleY;
    } else if (isHotHand) {
      // 🔥 Hot Hand: Agile Cybernetic Sprinter with Trailing Hair & Molten Gauntlet
      const isGold = (currentUnit.edition === 'gold' || currentUnit.cardData?.spriteRecolor === 'gold');
      const prefix = isGold ? 'HotHandGold_' : 'HotHand_';

      // Energetic athletic sprinter cadence (~3.3 steps/s)
      const sprintCadence = (now * 0.0105) % (Math.PI * 2);
      const strideCycle = ((sprintCadence % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
      const phaseIdx = Math.floor((strideCycle / (Math.PI * 2)) * 6) % 6;
      const legsKey = (isGold ? HOTHAND_GOLD_SPRINT_FRAMES : HOTHAND_SPRINT_FRAMES)[phaseIdx] || (prefix + 'Legs_Sprint1');

      // Organic harmonic bounce: smooth compression on ground contact, lift in airborne flight
      const bounce = Math.sin(strideCycle * 2);
      const legBob = bounce * 1.5;
      const torsoBob = bounce * 1.8;
      const torsoLean = 0.22 + Math.cos(strideCycle * 2) * 0.03;

      const armSwing = Math.sin(strideCycle);
      const armAngle = armSwing * 0.52;
      const rearArmAngle = -armSwing * 0.52;

      const isPunching = (elapsedRecoil >= 0 && elapsedRecoil < 160);
      const punchProgress = isPunching ? Math.min(1, elapsedRecoil / 160) : 0;
      const punchX = isPunching ? Math.sin(punchProgress * Math.PI) * 6.0 : 0;

      const hairKey = (phaseIdx % 2 === 0) ? (prefix + 'Hair_Sprint1') : (prefix + 'Hair_Sprint2');

      const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
      const torsoBmp = buildSpriteBitmap(prefix + 'Torso', currentUnit.race, 'western');
      const rearArmBmp = buildSpriteBitmap(prefix + 'Arm_Rear', currentUnit.race, 'western');
      const armBmp = buildSpriteBitmap(isPunching ? (prefix + 'Arm_Jab') : (prefix + 'Arm_Guard'), currentUnit.race, 'western');
      const hairBmp = buildSpriteBitmap(hairKey, currentUnit.race, 'western');

      const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
      const torsoSrc = (isFlash && torsoBmp?.flash) ? torsoBmp.flash : torsoBmp?.normal;
      const rearArmSrc = (isFlash && rearArmBmp?.flash) ? rearArmBmp.flash : rearArmBmp?.normal;
      const armSrc = (isFlash && armBmp?.flash) ? armBmp.flash : armBmp?.normal;
      const hairSrc = (isFlash && hairBmp?.flash) ? hairBmp.flash : hairBmp?.normal;

      const renderX = spriteX + spriteDisplaySize / 2;
      const renderY = spriteY + spriteDisplaySize / 2;

      // 1. Counter-pumping Rear Arm
      if (rearArmSrc) {
        ctx.save();
        ctx.translate(renderX - spriteDisplaySize * 0.08, renderY + torsoBob);
        ctx.rotate(rearArmAngle);
        ctx.drawImage(rearArmSrc, -spriteDisplaySize / 2 + spriteDisplaySize * 0.08, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      // 2. Sprinter Legs
      if (legsSrc) {
        ctx.drawImage(legsSrc, spriteX, spriteY + legBob, spriteDisplaySize, spriteDisplaySize);
      }

      // 3. Torso with Athletic Forward Lean & Trailing Ponytail (Child Rig)
      ctx.save();
      ctx.translate(renderX, renderY + torsoBob);
      ctx.rotate(torsoLean);

      if (torsoSrc) {
        ctx.drawImage(torsoSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
      }

      // 4. Trailing High-Speed Ponytail (Anchored to helmet hair-tie at col 8.5, row 4)
      if (hairSrc) {
        ctx.save();
        // Hair-tie pivot on the back of the helmet:
        // Center of 24x24 sprite is at (12, 12).
        // Hair-tie sits at x = 8.5, y = 4.0.
        // Relative to center: dx = (8.5 - 12)/24 = -3.5/24, dy = (4.0 - 12)/24 = -8.0/24.
        const tieX = -spriteDisplaySize * (3.5 / 24);
        const tieY = -spriteDisplaySize * (8.0 / 24);

        // Aerodynamic trailing wave & slipstream flutter around the pinned hair-tie:
        const hairWave = Math.sin(strideCycle * 2 - 0.7);
        const hairFlutter = Math.sin(now * 0.025) * 0.04;
        const trailAngle = hairWave * 0.07 + hairFlutter;

        ctx.translate(tieX, tieY);
        ctx.rotate(trailAngle);
        ctx.translate(-tieX, -tieY);
        ctx.drawImage(hairSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      ctx.restore();

      // 5. Front Molten Gauntlet Arm
      if (armSrc) {
        ctx.save();
        ctx.translate(renderX + punchX, renderY + torsoBob);
        ctx.rotate(isPunching ? 0 : armAngle);
        ctx.drawImage(armSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      // Molten heat embers on gauntlet
      if (!isLowQuality) {
        ctx.save();
        ctx.fillStyle = isGold ? '#fbbf24' : '#f97316';
        for (let em = 0; em < 2; em++) {
          const emPhase = (now * 0.015 + em * 2.2) % 1.0;
          const emX = spriteX + spriteDisplaySize * 0.78 + punchX + Math.sin(now * 0.02 + em) * 3;
          const emY = spriteY + spriteDisplaySize * 0.44 + torsoBob - emPhase * 6;
          ctx.globalAlpha = Math.sin(emPhase * Math.PI) * 0.8;
          ctx.fillRect(emX, emY, 1.8, 1.8);
        }
        ctx.restore();
      }

      muzzleX = spriteX + spriteDisplaySize * 0.86 + punchX;
      muzzleY = spriteY + spriteDisplaySize * 0.48 + torsoBob;
    } else if (isPinkBeret) {
      // 🎖️ Pink Beret: Supports V2 (36×36 Multi-Layer Rig), V2_AutoRig (Legacy), UHD (48×48 Legacy Relaunch), and Classic (24×24 Legacy HD)
      const hasGold = inventory.hasSpecialCard(currentUnit.name, 'gold');
      const isGold = (inventory.isSpecialCardActive ? inventory.isSpecialCardActive(currentUnit.name, 'gold') : hasGold)
        || (currentUnit.edition === 'gold' || currentUnit.cardData?.spriteRecolor === 'gold');
      const isClassic = !!(currentUnit.variant === 'classic' || currentUnit.forceHD);
      const isAutoRig = !!(currentUnit.variant === 'v2_autorig');
      const isV2 = !isClassic && !isAutoRig && !!(currentUnit.variant === 'v2' || isPinkBeretV2Enabled());
      const prefix = isClassic
        ? (isGold ? 'PinkBeretGold_Classic_' : 'PinkBeret_Classic_')
        : isAutoRig
          ? 'PinkBeret_V2_AutoRig_'
          : isV2
            ? (isGold ? 'PinkBeretGold_V2_' : 'PinkBeret_V2_')
            : (isGold ? 'PinkBeretGold_UHD_' : 'PinkBeret_UHD_');

      if (!isClassic && !isV2) {
        // 🎖️ Pink Beret (48×48 UHD Native Relaunch or V2_AutoRig Legacy) in Showroom
        const pbDisplaySize = Math.round(spriteDisplaySize * 1.20);
        const diff = pbDisplaySize - spriteDisplaySize;
        const pbSpriteX = spriteX - diff / 2;
        const pbSpriteY = spriteY - diff;
        const renderX = pbSpriteX + pbDisplaySize / 2;
        const renderY = pbSpriteY + pbDisplaySize / 2;

        const isRocket = (this._activeUnitCosmetic === 'rocket_trail');
        const fireDuration = isRocket ? 300 : 180;
        const isFiring = (elapsedRecoil >= 0 && elapsedRecoil < fireDuration);
        if (isFiring) {
          const recoilT = 1.0 - (elapsedRecoil / fireDuration);
          const kickX = (isRocket ? -5.0 : -3.5) * recoilT;
          const kickY = -1.5 * recoilT;
          const gunKey = isRocket ? (prefix + 'Launcher_Fire') : (prefix + 'Gunfire');
          const gunBmp = buildSpriteBitmap(gunKey, currentUnit.race, 'western');
          const gunSrc = (isFlash && gunBmp?.flash) ? gunBmp.flash : gunBmp?.normal;
          if (gunSrc) {
            ctx.drawImage(gunSrc, renderX + kickX - pbDisplaySize / 2, renderY + kickY - pbDisplaySize / 2, pbDisplaySize, pbDisplaySize);
          }
          if (isRocket) {
            muzzleX = pbSpriteX + pbDisplaySize * 0.82 + kickX;
            muzzleY = pbSpriteY + pbDisplaySize * 0.32;
          } else {
            muzzleX = pbSpriteX + pbDisplaySize * 0.82 + kickX;
            muzzleY = pbSpriteY + pbDisplaySize * 0.34;
          }
          this._pbMuzzleX = muzzleX;
          this._pbMuzzleY = muzzleY;
        } else {
          // 32-Frame Armed Walk Cycle with dynamic handgun rotation & MJ shoulder pop (16 fps catwalk, 2.0s period)
          const walkPhase32 = ((now * 0.0005) % 1.0 + 1.0) % 1.0;
          const phaseIdx = Math.floor(walkPhase32 * 32);
          const frameNum = (phaseIdx % 32) + 1;
          const walkKey = prefix + 'Walk_Armed' + frameNum;
          const walkBmp = buildSpriteBitmap(walkKey, currentUnit.race, 'western');
          const walkSrc = (isFlash && walkBmp?.flash) ? walkBmp.flash : walkBmp?.normal;
          if (walkSrc) {
            ctx.drawImage(walkSrc, renderX - pbDisplaySize / 2, renderY - pbDisplaySize / 2, pbDisplaySize, pbDisplaySize);
          }
          muzzleX = pbSpriteX + pbDisplaySize * 0.82;
          muzzleY = pbSpriteY + pbDisplaySize * 0.34;
          this._pbMuzzleX = muzzleX;
          this._pbMuzzleY = muzzleY;
        }
      } else {
        // 🎖️ Articulated Multi-Layer Rig (V2 36×36 Tier M & Classic 24px HD)
        const pbDisplaySize = Math.round(spriteDisplaySize * (isV2 ? 1.20 : 1.12));
        const diff = pbDisplaySize - spriteDisplaySize;
        const pbSpriteX = spriteX - diff / 2;
        const pbSpriteY = spriteY - diff;

        const walkRate = 0.40;
        const strideCycle = (((walkPhase * walkRate) % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((strideCycle / (Math.PI * 2)) * 6) % 6;
        const walkFrames = isV2
          ? (isGold ? PINKBERET_GOLD_V2_WALK_FRAMES : PINKBERET_V2_WALK_FRAMES)
          : (isGold ? PINKBERET_GOLD_CLASSIC_WALK_FRAMES : PINKBERET_CLASSIC_WALK_FRAMES);
        const isRocket = (this._activeUnitCosmetic === 'rocket_trail');
        const fireDuration = isRocket ? 300 : 180;
        const isFiring = (elapsedRecoil >= 0 && elapsedRecoil < fireDuration);
        const recoilT = isFiring ? (1.0 - (elapsedRecoil / fireDuration)) : 0;

        const legsKey = (isRocket && isFiring) ? (prefix + 'Legs_Aim') : (walkFrames[phaseIdx] || (prefix + 'Legs_Walk1'));

        // Sassy hip sway & buoyant step bob
        const legBob = (isRocket && isFiring) ? 0 : Math.sin(strideCycle * 2) * 1.2;
        const torsoBob = (isRocket && isFiring) ? 0 : -Math.abs(Math.sin(strideCycle)) * 1.6;
        const torsoSway = (isRocket && isFiring) ? 0 : Math.sin(strideCycle) * 0.05;
        const rearArmAngle = (isRocket && isFiring) ? 0 : -Math.sin(strideCycle) * 0.24;
        const frontArmAngle = (isRocket && isFiring) ? 0 : Math.sin(strideCycle) * 0.20;

        const recoilPitch = isFiring ? (isRocket ? -0.06 : -0.35) * recoilT : 0;
        const recoilX = isFiring ? (isRocket ? -5.0 : -4.0) * recoilT : 0;

        const rearBmp = (!isRocket || !isFiring) ? buildSpriteBitmap(prefix + 'Arm_Rear', currentUnit.race, 'western') : null;
        const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
        const torsoBmp = buildSpriteBitmap(prefix + 'Torso', currentUnit.race, 'western');
        const wepKey = (isRocket && isFiring) ? (prefix + 'Launcher_Fire') : (isRocket ? (prefix + 'Launcher_Aim45') : (prefix + 'Pistol'));
        const wepBmp = buildSpriteBitmap(wepKey, currentUnit.race, 'western');

        const rearSrc = (isFlash && rearBmp?.flash) ? rearBmp.flash : rearBmp?.normal;
        const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
        const torsoSrc = (isFlash && torsoBmp?.flash) ? torsoBmp.flash : torsoBmp?.normal;
        const wepSrc = (isFlash && wepBmp?.flash) ? wepBmp.flash : wepBmp?.normal;

        const renderX = pbSpriteX + pbDisplaySize / 2;
        const renderY = pbSpriteY + pbDisplaySize / 2;

        // 0. Rear Swinging Arm (Behind Torso & Legs)
        if (rearSrc) {
          ctx.save();
          ctx.translate(renderX, renderY + torsoBob);
          const rearPivotX = isV2 ? (-6 / 36) * pbDisplaySize : (-7 / 24) * pbDisplaySize;
          const rearPivotY = isV2 ? (-8 / 36) * pbDisplaySize : (-4 / 24) * pbDisplaySize;
          ctx.translate(rearPivotX, rearPivotY);
          ctx.rotate(rearArmAngle);
          ctx.translate(-rearPivotX, -rearPivotY);
          ctx.drawImage(rearSrc, -pbDisplaySize / 2, -pbDisplaySize / 2, pbDisplaySize, pbDisplaySize);
          ctx.restore();
        }

        // 1. Legs with Sculpted Parade Trousers & Spurs
        if (legsSrc) {
          ctx.drawImage(legsSrc, pbSpriteX, pbSpriteY + legBob, pbDisplaySize, pbDisplaySize);
        }

        // 2. Torso with Open Jacket & Muscular Chest
        if (torsoSrc) {
          ctx.save();
          ctx.translate(renderX + recoilX * 0.4, renderY + torsoBob);
          ctx.rotate(torsoSway);
          ctx.drawImage(torsoSrc, -pbDisplaySize / 2, -pbDisplaySize / 2, pbDisplaySize, pbDisplaySize);
          ctx.restore();
        }

        // 3. Poised Hand Pistol or RPG-7 with Swinging & Recoil Kick
        if (wepSrc) {
          ctx.save();
          ctx.translate(renderX + recoilX, renderY + torsoBob);
          const frontPivotX = isV2 ? (-4 / 36) * pbDisplaySize : (-5 / 24) * pbDisplaySize;
          const frontPivotY = isV2 ? (-8 / 36) * pbDisplaySize : (-4 / 24) * pbDisplaySize;
          ctx.translate(frontPivotX, frontPivotY);
          ctx.rotate((isRocket ? 0 : frontArmAngle) + recoilPitch);
          ctx.translate(-frontPivotX, -frontPivotY);
          ctx.drawImage(wepSrc, -pbDisplaySize / 2, -pbDisplaySize / 2, pbDisplaySize, pbDisplaySize);
          ctx.restore();
        }

        if (isRocket) {
          muzzleX = pbSpriteX + pbDisplaySize * 0.82 + recoilX;
          muzzleY = pbSpriteY + pbDisplaySize * 0.32 + torsoBob;
        } else {
          muzzleX = pbSpriteX + pbDisplaySize * (isV2 ? (28 / 36) : 0.80) + recoilX;
          muzzleY = pbSpriteY + pbDisplaySize * (isV2 ? (10 / 36) : 0.34) + torsoBob;
        }
        this._pbMuzzleX = muzzleX;
        this._pbMuzzleY = muzzleY;
      }
    } else if (isClaimingPillar) {
      // 🏛️ Sul Claiming Pillar: Organic Siphon Monolith with Ground Corona & Bio-Pulse
      const breathe = Math.sin(t * 2.2);
      const floatBob = Math.sin(t * 1.8) * 3.5;
      const squashX = 1.0 + breathe * 0.035;
      const stretchY = 1.0 - breathe * 0.035;

      // Ground Corona
      if (!isLowQuality) {
        const floorGlow = getSulClaimingPillarFloorGlowCanvas();
        if (floorGlow) {
          ctx.save();
          ctx.globalAlpha = 0.55 + 0.25 * Math.sin(t * 2.5);
          ctx.drawImage(floorGlow, spriteX + spriteDisplaySize * 0.1, spriteY + spriteDisplaySize * 0.72, spriteDisplaySize * 0.8, spriteDisplaySize * 0.28);
          ctx.restore();
        }
      }

      const pillarBmp = buildSpriteBitmap('ClaimingPillar', currentUnit.race, 'western') || buildSpriteBitmap(unitType, currentUnit.race, 'western');
      const pillarSrc = (isFlash && pillarBmp?.flash) ? pillarBmp.flash : pillarBmp?.normal;

      if (pillarSrc) {
        ctx.save();
        const pivotX = spriteX + spriteDisplaySize / 2;
        const pivotY = spriteY + spriteDisplaySize * 0.65;
        ctx.translate(pivotX, pivotY + floatBob);
        ctx.scale(squashX, stretchY);
        ctx.drawImage(pillarSrc, -spriteDisplaySize / 2, -spriteDisplaySize * 0.65, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      muzzleX = spriteX + spriteDisplaySize * 0.52;
      muzzleY = spriteY + spriteDisplaySize * 0.24 + floatBob;
    } else if (isHacker) {
      // 🌐 Human Hacker: 8-Armed Cyber Deity (Frontal Symmetrical Showroom Pose)
      const isGold = (currentUnit.edition === 'gold' || currentUnit.cardData?.spriteRecolor === 'gold');
      const spriteKey = isGold ? 'HackerGold_Frontal' : 'Hacker_Frontal';
      const isAttacking = (elapsedRecoil >= 0 && elapsedRecoil < 180);
      const floatBob = Math.sin(t * 2.8) * (3.5 * stageScale) - (4.0 * stageScale);

      const pivotX = spriteX + spriteDisplaySize / 2;
      const pivotY = spriteY + spriteDisplaySize * 0.52;

      // 1. Spinning Golden Cyber-Mandala Halo behind Deity
      ctx.save();
      ctx.translate(pivotX, pivotY + floatBob);
      const mandalaAngle = t * 1.5;
      ctx.rotate(mandalaAngle);
      ctx.strokeStyle = '#facc15';
      ctx.lineWidth = 1.5 * stageScale;
      ctx.beginPath();
      ctx.arc(0, 0, spriteDisplaySize * 0.48, 0, Math.PI * 2);
      ctx.stroke();

      for (let s = 0; s < 8; s++) {
        const ang = (s * Math.PI) / 4;
        const r1 = spriteDisplaySize * 0.38;
        const r2 = spriteDisplaySize * 0.52;
        ctx.beginPath();
        ctx.moveTo(Math.cos(ang) * r1, Math.sin(ang) * r1);
        ctx.lineTo(Math.cos(ang) * r2, Math.sin(ang) * r2);
        ctx.stroke();
      }
      ctx.restore();

      // 2. Interactive Attack / Recoil Cyan EMP Pulse
      if (isAttacking) {
        ctx.save();
        ctx.translate(pivotX, pivotY + floatBob);
        const pulseRatio = elapsedRecoil / 180;
        const pulseR = spriteDisplaySize * (0.2 + pulseRatio * 0.6);
        ctx.strokeStyle = `rgba(0, 240, 255, ${1.0 - pulseRatio})`;
        ctx.lineWidth = 2.5 * stageScale;
        ctx.beginPath();
        ctx.arc(0, 0, pulseR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // 3. Draw 8-Armed Frontal Sprite
      const hkBmp = buildSpriteBitmap(spriteKey, currentUnit.race, 'western');
      const hkSrc = (isFlash && hkBmp?.flash) ? hkBmp.flash : hkBmp?.normal;
      if (hkSrc) {
        ctx.save();
        ctx.drawImage(hkSrc, spriteX, spriteY + floatBob, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      muzzleX = pivotX;
      muzzleY = pivotY + floatBob - spriteDisplaySize * 0.15;
    } else if (isPreserverLock) {
      // 🕷️ Sul Preserver Lock: 4-Phase Towering Hexapod with Swaying Power Cables & Corona
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4) % 4;
      const legsKey = PRESERVER_LOCK_WALK_FRAMES[phaseIdx] || 'PreserverLock_Legs_Walk1';
      const bodyKey = PRESERVER_LOCK_BODY_FRAMES[phaseIdx] || 'PreserverLock_Body_Walk1';
      const walkBob = (phaseIdx === 0 || phaseIdx === 2) ? (2.5 * stageScale) : 0;
      const bodyRoll = (phaseIdx === 0) ? 0.045 : ((phaseIdx === 2) ? -0.045 : 0.0);
      const isAttacking = (elapsedRecoil >= 0 && elapsedRecoil < 240);

      const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
      const bodyBmp = buildSpriteBitmap(bodyKey, currentUnit.race, 'western');

      const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
      const bodySrc = (isFlash && bodyBmp?.flash) ? bodyBmp.flash : bodyBmp?.normal;

      if (legsSrc) ctx.drawImage(legsSrc, spriteX, spriteY, spriteDisplaySize, spriteDisplaySize);
      if (bodySrc) {
        ctx.save();
        const pivotX = spriteX + spriteDisplaySize / 2;
        const pivotY = spriteY + spriteDisplaySize * 0.65;
        ctx.translate(pivotX, pivotY + walkBob);
        ctx.rotate(bodyRoll);
        ctx.drawImage(bodySrc, -spriteDisplaySize / 2, -spriteDisplaySize * 0.65, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      // Head projector corona / lens flare at twin lenses at mast peak
      const headX = spriteX + spriteDisplaySize * 0.51;
      const headY = spriteY + spriteDisplaySize * (1.5 / 24) + walkBob;
      const flarePulse = isAttacking
        ? (0.95 + 0.15 * Math.sin(t * 20))
        : (0.70 + 0.15 * Math.sin(t * 4));
      const flareR = spriteDisplaySize * (isAttacking ? 0.20 : 0.12) * flarePulse;

      if (!isLowQuality) {
        const coronaCanvas = getPreserverLockCoronaCanvas(isAttacking, activeBulletColorHex);
        if (coronaCanvas) {
          ctx.save();
          ctx.globalAlpha = isAttacking ? 0.95 : 0.70;
          ctx.drawImage(coronaCanvas, headX - flareR, headY - flareR, flareR * 2, flareR * 2);
          ctx.restore();
        }

        if (isAttacking) {
          ctx.save();
          ctx.strokeStyle = activeBulletColorHex || 'rgba(224, 242, 254, 0.85)';
          ctx.lineWidth = 1.4;
          const rayLen = flareR * 1.5;
          ctx.beginPath();
          ctx.moveTo(headX - rayLen, headY);
          ctx.lineTo(headX + rayLen, headY);
          ctx.moveTo(headX, headY - rayLen);
          ctx.lineTo(headX, headY + rayLen);
          ctx.stroke();
          ctx.restore();
        }
      }

      muzzleX = headX;
      muzzleY = headY;
      this._preserverMuzzleX = muzzleX;
      this._preserverMuzzleY = muzzleY;
    } else if (isBastionLock) {
      // 🛡️ Sul Bastion Lock: 36px Articulated Heavy Shield Barricade Rig
      // Back Shield -> 4-Phase Crawl Legs -> Carapace Body with Breathers & Visor -> Front Shield
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4) % 4;
      const isAttacking = (elapsedRecoil >= 0 && elapsedRecoil < 200);
      const legsKey = isAttacking ? 'BastionLock_Legs_Stand' : (BASTION_LOCK_WALK_FRAMES[phaseIdx] || 'BastionLock_Legs_Walk1');
      const shieldBackKey = isAttacking ? 'BastionLock_Shield_Back_Deployed' : 'BastionLock_Shield_Back';
      const shieldFrontKey = isAttacking ? 'BastionLock_Shield_Front_Deployed' : 'BastionLock_Shield_Front';

      const backShieldBmp = buildSpriteBitmap(shieldBackKey, currentUnit.race, 'western') || buildSpriteBitmap('BastionLock_Shield_Back', currentUnit.race, 'western');
      const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
      const bodyBmp = buildSpriteBitmap('BastionLock_Body', currentUnit.race, 'western');
      const frontShieldBmp = buildSpriteBitmap(shieldFrontKey, currentUnit.race, 'western') || buildSpriteBitmap('BastionLock_Shield_Front', currentUnit.race, 'western');

      const backSrc = (isFlash && backShieldBmp?.flash) ? backShieldBmp.flash : backShieldBmp?.normal;
      const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
      const bodySrc = (isFlash && bodyBmp?.flash) ? bodyBmp.flash : bodyBmp?.normal;
      const frontSrc = (isFlash && frontShieldBmp?.flash) ? frontShieldBmp.flash : frontShieldBmp?.normal;

      const bodyRecoilX = isAttacking ? recoilOffsetX * 0.7 : 0;
      const walkBob = isAttacking ? 0 : ((phaseIdx === 0 || phaseIdx === 2) ? (1.5 * stageScale) : 0);

      if (backSrc) ctx.drawImage(backSrc, spriteX, spriteY + walkBob, spriteDisplaySize, spriteDisplaySize);
      if (legsSrc) ctx.drawImage(legsSrc, spriteX, spriteY, spriteDisplaySize, spriteDisplaySize);
      if (bodySrc) ctx.drawImage(bodySrc, spriteX + bodyRecoilX, spriteY + walkBob, spriteDisplaySize, spriteDisplaySize);
      if (frontSrc) ctx.drawImage(frontSrc, spriteX, spriteY + walkBob, spriteDisplaySize, spriteDisplaySize);

      // Cyan visor respiratory pulse (Quality gated)
      if (!isLowQuality) {
        const pulse = 0.6 + 0.4 * Math.sin(t * 4.0);
        const visorX = spriteX + spriteDisplaySize * (20 / 36);
        const visorY = spriteY + spriteDisplaySize * (13.5 / 36) + walkBob;
        ctx.save();
        ctx.fillStyle = `rgba(34, 211, 238, ${0.45 * pulse})`;
        ctx.beginPath();
        ctx.arc(visorX, visorY, 4 * stageScale * pulse, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Muzzle location: centered in the eye/visor slit cavity (20.5/36 X, 13.5/36 Y)
      muzzleX = spriteX + spriteDisplaySize * (20.5 / 36);
      muzzleY = spriteY + spriteDisplaySize * (13.5 / 36) + walkBob;
      this._bastionMuzzleX = muzzleX;
      this._bastionMuzzleY = muzzleY;

      if (isAttacking && !isLowQuality) {
        ctx.save();
        ctx.fillStyle = 'rgba(249, 115, 22, 0.7)';
        ctx.beginPath();
        ctx.arc(muzzleX, muzzleY, 7 * stageScale, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(254, 240, 138, 0.9)';
        ctx.beginPath();
        ctx.arc(muzzleX, muzzleY, 3.5 * stageScale, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    } else if (isMonitorLock) {
      // 👁️ Sul Monitor Lock: Subterranean Biological Sensor Eye in Crater with Crown Spines
      const hoverBob = Math.sin(t * 1.8) * 3.5;
      const isAttacking = (elapsedRecoil >= 0 && elapsedRecoil < 200);

      const craterBmp = buildSpriteBitmap('MonitorLock_Crater', currentUnit.race, 'western');
      const bodyBmp = buildSpriteBitmap('MonitorLock_Body', currentUnit.race, 'western');
      const headKey = isAttacking ? 'MonitorLock_Head_Bite_Face' : 'MonitorLock_Head_Face';
      const headBmp = buildSpriteBitmap(headKey, currentUnit.race, 'western') || buildSpriteBitmap('MonitorLock_Head', currentUnit.race, 'western');
      const craterFrontBmp = buildSpriteBitmap('MonitorLock_Crater_Front', currentUnit.race, 'western');

      const craterSrc = (isFlash && craterBmp?.flash) ? craterBmp.flash : craterBmp?.normal;
      const bodySrc = (isFlash && bodyBmp?.flash) ? bodyBmp.flash : bodyBmp?.normal;
      const headSrc = (isFlash && headBmp?.flash) ? headBmp.flash : headBmp?.normal;
      const craterFrontSrc = (isFlash && craterFrontBmp?.flash) ? craterFrontBmp.flash : craterFrontBmp?.normal;

      const renderX = spriteX + spriteDisplaySize / 2;
      const renderY = spriteY + spriteDisplaySize / 2;

      // 1. Crater Back
      if (craterSrc) ctx.drawImage(craterSrc, spriteX, spriteY, spriteDisplaySize, spriteDisplaySize);

      // 2. Eye Body
      if (bodySrc) ctx.drawImage(bodySrc, spriteX, spriteY + hoverBob, spriteDisplaySize, spriteDisplaySize);

      // 3. Articulated Head & Crown Spines
      ctx.save();
      ctx.translate(renderX, spriteY + hoverBob);
      drawMonitorLockCrownSpines(ctx, spriteDisplaySize, 0, true, isFlash, t, 1, isAttacking, isAttacking ? elapsedRecoil / 200 : 0, isLowQuality ? 'low' : 'ultra');
      ctx.restore();

      if (headSrc) ctx.drawImage(headSrc, spriteX, spriteY + hoverBob, spriteDisplaySize, spriteDisplaySize);

      // 4. Crater Lip (Foreground)
      if (craterFrontSrc) ctx.drawImage(craterFrontSrc, spriteX, spriteY, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * 0.72;
      muzzleY = spriteY + spriteDisplaySize * 0.38 + hoverBob;
    } else if (isParasiteVulture) {
      // 🦟 Sul Parasite Vulture: Bio-Mechanical Sky Leech with Flapping Wings & Rider
      const hoverY = Math.sin(t * 2.2) * 4.5;
      const wingFlap = Math.sin(t * 6.0) * 0.28;

      const bodyBmp = buildSpriteBitmap('ParasiteVulture_Body', currentUnit.race, 'western');
      const wingBmp = buildSpriteBitmap('ParasiteVulture_Wing', currentUnit.race, 'western');
      const headBmp = buildSpriteBitmap('ParasiteVulture_Head', currentUnit.race, 'western');
      const parasiteBmp = buildSpriteBitmap('ParasiteVulture_Parasite', currentUnit.race, 'western');

      const bodySrc = (isFlash && bodyBmp?.flash) ? bodyBmp.flash : bodyBmp?.normal;
      const wingSrc = (isFlash && wingBmp?.flash) ? wingBmp.flash : wingBmp?.normal;
      const headSrc = (isFlash && headBmp?.flash) ? headBmp.flash : headBmp?.normal;
      const parasiteSrc = (isFlash && parasiteBmp?.flash) ? parasiteBmp.flash : parasiteBmp?.normal;

      const renderX = spriteX + spriteDisplaySize / 2;
      const renderY = spriteY + spriteDisplaySize / 2 + hoverY;

      // 1. Fuselage
      if (bodySrc) ctx.drawImage(bodySrc, spriteX, spriteY + hoverY, spriteDisplaySize, spriteDisplaySize);

      // 2. Wings with Dynamic Flap Rotation
      if (wingSrc) {
        ctx.save();
        ctx.translate(renderX, renderY);
        ctx.rotate(wingFlap);
        ctx.drawImage(wingSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      // 3. Head / Mandibles
      if (headSrc) ctx.drawImage(headSrc, spriteX, spriteY + hoverY, spriteDisplaySize, spriteDisplaySize);

      // 4. Parasite Leech Rider
      if (parasiteSrc) ctx.drawImage(parasiteSrc, spriteX, spriteY + hoverY, spriteDisplaySize, spriteDisplaySize);

      muzzleX = spriteX + spriteDisplaySize * 0.88;
      muzzleY = spriteY + spriteDisplaySize * 0.46 + hoverY;
    } else if (isAntiShade) {
      // 🗡️ Sul Anti Shade: Spectral Melee Infiltrator with Tattered Cloth Vortex & Twin Blades
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6) % 6;
      const legsKey = ANTI_SHADE_WALK_FRAMES[phaseIdx] || 'AntiShade_Base_Walk1';
      const glideBob = Math.sin(walkPhase * 2) * 1.5;

      const legsBmp = buildSpriteBitmap(legsKey, currentUnit.race, 'western');
      const torsoBmp = buildSpriteBitmap('AntiShade_Torso', currentUnit.race, 'western');
      const bladeBmp = buildSpriteBitmap('AntiShade_Blade', currentUnit.race, 'western');

      const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
      const torsoSrc = (isFlash && torsoBmp?.flash) ? torsoBmp.flash : torsoBmp?.normal;
      const bladeSrc = (isFlash && bladeBmp?.flash) ? bladeBmp.flash : bladeBmp?.normal;

      const renderX = spriteX + spriteDisplaySize / 2;
      const renderY = spriteY + spriteDisplaySize / 2 + glideBob;

      // 1. Spectral Cloak Tatters (Dynamic Cloth Poly Vortex)
      if (!isLowQuality) {
        ctx.save();
        ctx.fillStyle = '#1e1b4b';
        ctx.globalAlpha = 0.65;
        drawAntiShadeTatterPoly(ctx, renderX - 8, renderY - 12, 10, renderX - 16, renderY + 4, 14, renderX - 22, renderY + 18, 2.0, 1.5, 0.2);
        drawAntiShadeTatterPoly(ctx, renderX + 2, renderY - 10, 8, renderX - 6, renderY + 6, 12, renderX - 12, renderY + 20, 1.8, 1.2, 0.0);
        ctx.fill();
        ctx.restore();
      }

      // 2. Base / Hover Strides
      if (legsSrc) ctx.drawImage(legsSrc, spriteX, spriteY + glideBob, spriteDisplaySize, spriteDisplaySize);

      // 3. Torso
      if (torsoSrc) ctx.drawImage(torsoSrc, spriteX, spriteY + glideBob, spriteDisplaySize, spriteDisplaySize);

      // 4. Twin Energy Blades
      if (bladeSrc) {
        ctx.save();
        const slashSwing = (elapsedRecoil >= 0 && elapsedRecoil < 160) ? Math.sin((elapsedRecoil / 160) * Math.PI) * 0.45 : Math.sin(t * 3) * 0.08;
        ctx.translate(renderX + spriteDisplaySize * 0.12, renderY + spriteDisplaySize * 0.04);
        ctx.rotate(slashSwing);
        ctx.drawImage(bladeSrc, -spriteDisplaySize / 2, -spriteDisplaySize / 2, spriteDisplaySize, spriteDisplaySize);
        ctx.restore();
      }

      muzzleX = spriteX + spriteDisplaySize * 0.88;
      muzzleY = spriteY + spriteDisplaySize * 0.46 + glideBob;
    } else {
      // Default Unit Walking Bounce
      const animBobY = Math.sin(walkPhase * 2) * 2.5;
      const spriteEntry = buildSpriteBitmap(currentUnit.unitType, currentUnit.race, 'western');
      if (spriteEntry && spriteEntry.normal) {
        const bmpToDraw = (isFlash && spriteEntry.flash) ? spriteEntry.flash : spriteEntry.normal;
        ctx.drawImage(bmpToDraw, spriteX + recoilOffsetX, spriteY + animBobY, spriteDisplaySize, spriteDisplaySize);
      }
      muzzleX = spriteX + spriteDisplaySize * 0.82 + recoilOffsetX;
      muzzleY = spriteY + spriteDisplaySize * 0.52 + animBobY;
    }

    // Muzzle Flash Flare (during first 60ms of firing, shooting units only)
    if (elapsedRecoil >= 0 && elapsedRecoil < 60 && this.unitCanShoot(currentUnit)) {
      const activeBullet = this._projectiles[this._projectiles.length - 1];
      const muzzleColor = activeBullet?.colorHex || '#ffdd44';

      if (isTesla) {
        ctx.strokeStyle = muzzleColor;
        ctx.lineWidth = 2.5;
        if (!isLowQuality) {
          ctx.shadowColor = muzzleColor;
          ctx.shadowBlur = 18;
        }
        for (let s = 0; s < 4; s++) {
          const a = (s * Math.PI) / 2 + (now * 0.04);
          ctx.beginPath();
          ctx.moveTo(muzzleX, muzzleY);
          ctx.lineTo(muzzleX + Math.cos(a) * 18, muzzleY + Math.sin(a) * 18);
          ctx.stroke();
        }
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(muzzleX, muzzleY, 5, 0, Math.PI * 2);
        ctx.fill();
      } else if (isLootingVulture) {
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(muzzleX, muzzleY, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = muzzleColor;
        ctx.lineWidth = 2.5;
        if (!isLowQuality) {
          ctx.shadowColor = muzzleColor;
          ctx.shadowBlur = 24;
        }
        for (let s = 0; s < 4; s++) {
          const a = (s * Math.PI) / 2 + (now * 0.05);
          ctx.beginPath();
          ctx.moveTo(muzzleX, muzzleY);
          ctx.lineTo(muzzleX + Math.cos(a) * 24, muzzleY + Math.sin(a) * 24);
          ctx.stroke();
        }
      } else if (isProcessingPillar) {
        ctx.fillStyle = muzzleColor;
        ctx.beginPath();
        ctx.arc(muzzleX, muzzleY, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(muzzleX, muzzleY, 7, 0, Math.PI * 2);
        ctx.fill();
      } else if (isSentinalLock) {
        ctx.fillStyle = muzzleColor;
        ctx.beginPath();
        ctx.arc(muzzleX, muzzleY, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#4d7c0f';
        ctx.fillRect(muzzleX - 2, muzzleY - 2, 4, 4);
      } else {
        ctx.fillStyle = muzzleColor;
        ctx.beginPath();
        ctx.arc(muzzleX, muzzleY, 14, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(muzzleX, muzzleY, 6, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();

    // ==========================================
    // LAYER 4: HOLO-ZIELDROHNE / TARGET DRONE (Right side)
    // ==========================================
    ctx.save();
    const droneRadius = 44;
    const droneDefaultX = Math.min(w - droneRadius - 16, Math.max(w * 0.82, w - 160));
    const droneDefaultY = isCompactStage ? Math.round(h * 0.48) : (h * 0.52);
    if (this._droneRenderX === null) {
      this._droneRenderX = droneDefaultX;
      this._droneRenderY = droneDefaultY;
    }
    // Ease the drone toward the player's aim point (mouse position / last tap)
    // instead of snapping, so manual aiming still reads as a physical target.
    const rawGoalX = this._aimTargetX !== null ? this._aimTargetX : droneDefaultX;
    const rawGoalY = this._aimTargetY !== null ? this._aimTargetY : droneDefaultY;
    const aimGoalX = Math.max(droneRadius + 14, Math.min(w - droneRadius - 14, rawGoalX));
    const aimGoalY = Math.max(droneRadius + 14, Math.min(h - droneRadius - 14, rawGoalY));
    this._droneRenderX += (aimGoalX - this._droneRenderX) * 0.18;
    this._droneRenderY += (aimGoalY - this._droneRenderY) * 0.18;

    let droneBaseX = Math.max(droneRadius + 14, Math.min(w - droneRadius - 14, this._droneRenderX));
    let droneBaseY = Math.max(droneRadius + 14, Math.min(h - droneRadius - 14, this._droneRenderY));
    const droneHover = Math.sin(now * 0.0035) * 8;
    let droneY = Math.max(droneRadius + 10, Math.min(h - droneRadius - 10, droneBaseY + droneHover));

    const hitElapsed = now - this._droneHitTime;
    let isDroneHit = (hitElapsed >= 0 && hitElapsed < 260);
    if (isDroneHit) {
      const hitProgress = hitElapsed / 260;
      const shake = Math.sin(hitProgress * 28) * 8 * (1 - hitProgress);
      droneBaseX += shake;
    }

    const droneX = Math.max(droneRadius + 10, Math.min(w - droneRadius - 10, droneBaseX));
    const droneColor = isDroneHit ? (this._droneImpactColor || '#ef4444') : '#38bdf8';
    const droneScale = stageScale;

    // Outer Target Crosshairs / Shield Ring
    ctx.strokeStyle = droneColor;
    ctx.lineWidth = isDroneHit ? (3.5 * droneScale) : (1.8 * droneScale);
    if (!isLowQuality) {
      ctx.shadowColor = droneColor;
      ctx.shadowBlur = (isDroneHit ? 22 : 10) * droneScale;
    }
    ctx.beginPath();
    ctx.arc(droneX, droneY, 38 * droneScale, 0, Math.PI * 2);
    ctx.stroke();

    // Crosshair ticks
    const tickAngles = [0, Math.PI / 2, Math.PI, Math.PI * 1.5];
    tickAngles.forEach(ang => {
      const x1 = droneX + Math.cos(ang) * (32 * droneScale);
      const y1 = droneY + Math.sin(ang) * (32 * droneScale);
      const x2 = droneX + Math.cos(ang) * (44 * droneScale);
      const y2 = droneY + Math.sin(ang) * (44 * droneScale);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });

    // Drone Hexagonal Body
    ctx.fillStyle = isDroneHit ? 'rgba(239, 68, 68, 0.4)' : '#0f172a';
    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = Math.max(1, 2 * droneScale);
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (i * Math.PI) / 3;
      const hx = droneX + Math.cos(a) * (22 * droneScale);
      const hy = droneY + Math.sin(a) * (22 * droneScale);
      if (i === 0) ctx.moveTo(hx, hy);
      else ctx.lineTo(hx, hy);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Central Target Diode / Eye
    ctx.fillStyle = isDroneHit ? '#ffffff' : '#ef4444';
    ctx.beginPath();
    ctx.arc(droneX, droneY, (isDroneHit ? 8 : 5) * droneScale, 0, Math.PI * 2);
    ctx.fill();

    // Spark Particles on Drone Hit
    if (isDroneHit) {
      const hitProgress = hitElapsed / 260;
      ctx.fillStyle = this._droneImpactColor || '#ff003c';
      for (let p = 0; p < 8; p++) {
        const ang = (p * Math.PI) / 4 + hitProgress * 2;
        const dist = 30 + hitProgress * 45;
        const px = droneX + Math.cos(ang) * dist;
        const py = droneY + Math.sin(ang) * dist;
        ctx.fillRect(px, py, 3, 3);
      }
    }
    ctx.restore();

    // ==========================================
    // LAYER 5: FLYING PROJECTILES & TRACERS
    // ==========================================
    const targetHitX = droneX;
    const targetHitY = droneY;

    for (let i = this._projectiles.length - 1; i >= 0; i--) {
      const p = this._projectiles[i];
      const elapsed = now - p.startTime;
      const progress = Math.min(1.0, elapsed / p.duration);

      let pMuzzleX = spriteX + spriteDisplaySize * 0.84;
      let pMuzzleY = spriteY + spriteDisplaySize * 0.38;
      if (p.isTesla) {
        if (p.isHyperTesla) {
          pMuzzleX = this._hyperTeslaMuzzleX || (spriteX + spriteDisplaySize * (23.5 / 24));
          pMuzzleY = this._hyperTeslaMuzzleY || (spriteY + spriteDisplaySize * (2.5 / 24));
        } else {
          pMuzzleX = this._teslaMuzzleX || (spriteX + spriteDisplaySize * 0.62);
          pMuzzleY = this._teslaMuzzleY || (spriteY + spriteDisplaySize * (2.5 / 24));
        }
      } else if (p.isShade) {
        pMuzzleX = spriteX + spriteDisplaySize * 0.88;
        pMuzzleY = spriteY + spriteDisplaySize * 0.48;
      } else if (p.isFlame) {
        pMuzzleX = spriteX + spriteDisplaySize * 0.84;
        pMuzzleY = spriteY + spriteDisplaySize * 0.54;
      } else if (p.isRocketship) {
        pMuzzleX = spriteX + spriteDisplaySize * 0.80;
        pMuzzleY = spriteY + spriteDisplaySize * 0.48;
      } else if (p.isOrchestra) {
        pMuzzleX = spriteX + spriteDisplaySize * 0.70;
        pMuzzleY = spriteY + spriteDisplaySize * 0.42;
      } else if (p.isApostate) {
        pMuzzleX = spriteX + spriteDisplaySize * 0.52;
        pMuzzleY = spriteY + spriteDisplaySize * 0.28;
      } else if (p.isPulp) {
        pMuzzleX = this._pulpMuzzleX || (spriteX + spriteDisplaySize * 0.52);
        pMuzzleY = this._pulpMuzzleY || (spriteY + spriteDisplaySize * 0.22);
      } else if (p.isPiranha) {
        const piranhaBob = Math.sin(walkPhase) * 3;
        pMuzzleX = spriteX + spriteDisplaySize * (35.5 / 36);
        pMuzzleY = spriteY + piranhaBob + spriteDisplaySize * (23.5 / 36);
      } else if (p.isDelvingPillar) {
        pMuzzleX = spriteX + spriteDisplaySize * 0.55;
        pMuzzleY = spriteY + spriteDisplaySize * 0.44;
      } else if (p.isHyperDevilPatrol) {
        pMuzzleX = spriteX + spriteDisplaySize * 0.84;
        pMuzzleY = spriteY + spriteDisplaySize * 0.30;
      } else if (p.isGrenade) {
        pMuzzleX = spriteX + spriteDisplaySize * 0.95;
        pMuzzleY = spriteY + spriteDisplaySize * 0.32;
      } else if (p.isDefectiveLoaf) {
        pMuzzleX = spriteX + spriteDisplaySize * 0.72;
        pMuzzleY = spriteY + spriteDisplaySize * 0.54;
      } else if (p.isLootingVulture) {
        pMuzzleX = this._lootingVultureApexX || (spriteX + spriteDisplaySize * 0.76);
        pMuzzleY = this._lootingVultureApexY || (spriteY + spriteDisplaySize * 0.05);
      } else if (p.isEnforcerVulture) {
        pMuzzleX = this._enforcerMuzzleX || (spriteX + spriteDisplaySize * 0.60);
        pMuzzleY = this._enforcerMuzzleY || (spriteY + spriteDisplaySize * 0.35);
      } else if (p.isProcessingPillar) {
        pMuzzleX = this._processingMuzzleX || (spriteX + spriteDisplaySize * 0.78);
        pMuzzleY = this._processingMuzzleY || (spriteY + spriteDisplaySize * 0.46);
      } else if (p.isPreserverLock) {
        pMuzzleX = this._preserverMuzzleX || (spriteX + spriteDisplaySize * 0.51);
        pMuzzleY = this._preserverMuzzleY || (spriteY + spriteDisplaySize * (1.5 / 24));
      } else if (p.isBastionLock) {
        pMuzzleX = this._bastionMuzzleX || (spriteX + spriteDisplaySize * (20.5 / 36));
        pMuzzleY = this._bastionMuzzleY || (spriteY + spriteDisplaySize * (13.5 / 36));
      } else if (p.isSentinalLock) {
        pMuzzleX = this._sentinalMuzzleX || (pedestalX + spriteDisplaySize * 0.36);
        pMuzzleY = this._sentinalMuzzleY || (pedestalY - spriteDisplaySize * 0.44 - spriteDisplaySize * 0.04);
      } else if (p.isPinkBeret) {
        if (p.isPinkBeretRocket) {
          pMuzzleX = this._pbMuzzleX || (spriteX + spriteDisplaySize * 0.82);
          pMuzzleY = this._pbMuzzleY || (spriteY + spriteDisplaySize * 0.32);
        } else {
          pMuzzleX = this._pbMuzzleX || (spriteX + spriteDisplaySize * 0.80);
          pMuzzleY = this._pbMuzzleY || (spriteY + spriteDisplaySize * 0.22);
        }
      } else if (p.isA9Grizzly) {
        pMuzzleX = this._grizzlyMuzzleX || (spriteX + spriteDisplaySize * (35.5 / 36));
        pMuzzleY = this._grizzlyMuzzleY || (spriteY + spriteDisplaySize * (10 / 36));
      } else if (p.isA9Pigeon) {
        pMuzzleX = this._pigeonMuzzleX || (spriteX + spriteDisplaySize * (34.5 / 36));
        pMuzzleY = this._pigeonMuzzleY || (spriteY + spriteDisplaySize * (25.5 / 36));
      } else if (p.isStickman) {
        pMuzzleX = this._stickmanMuzzleX || (spriteX + spriteDisplaySize * (46 / 48));
        pMuzzleY = this._stickmanMuzzleY || (spriteY + spriteDisplaySize * (22.2 / 48));
      } else if (p.isHarvestingPillar) {
        pMuzzleX = this._harvestingMuzzleX || (spriteX + spriteDisplaySize * 0.94);
        pMuzzleY = this._harvestingMuzzleY || (spriteY + spriteDisplaySize * 0.08);
      }

      ctx.save();
      if (p.isTesla) {
        // ⚡ Tesla Dielectric Chain Lightning Arc
        const arcProgress = Math.min(1.0, progress * 1.5);
        const reachX = pMuzzleX + arcProgress * (targetHitX - pMuzzleX);
        const reachY = pMuzzleY + arcProgress * (targetHitY - pMuzzleY);
        const boltColor = p.colorHex || '#00f0ff';

        for (let b = 0; b < 2; b++) {
          const segments = 6;
          ctx.strokeStyle = (b === 0) ? '#ffffff' : boltColor;
          if (!isLowQuality) {
            ctx.shadowColor = boltColor;
            ctx.shadowBlur = 18;
          }
          ctx.lineWidth = (b === 0) ? 2.5 : 4.5;
          ctx.beginPath();
          ctx.moveTo(pMuzzleX, pMuzzleY);

          for (let seg = 1; seg <= segments; seg++) {
            const t = seg / segments;
            const jitterAmp = Math.sin(t * Math.PI) * (b === 0 ? 14 : 26);
            const seed = Math.sin(now * 0.06 + seg * 11 + b * 29);
            const nx = pMuzzleX + (reachX - pMuzzleX) * t + seed * jitterAmp;
            const ny = pMuzzleY + (reachY - pMuzzleY) * t + Math.cos(now * 0.05 + seg * 17) * jitterAmp * 0.5;
            ctx.lineTo(nx, ny);
          }
          ctx.stroke();
        }

        if (arcProgress >= 0.8) {
          ctx.fillStyle = boltColor;
          if (!isLowQuality) {
            ctx.shadowColor = boltColor;
            ctx.shadowBlur = 14;
          }
          for (let sp = 0; sp < 4; sp++) {
            const ang = (sp * Math.PI) / 2 + now * 0.02;
            ctx.fillRect(reachX + Math.cos(ang) * 10, reachY + Math.sin(ang) * 10, 3, 3);
          }
        }
      } else if (p.isLootingVulture) {
        // ☀️ Sul Looting Vulture: Apex Pyramid Solar Energy Beam
        const beamProgress = Math.min(1.0, progress * 1.8);
        const reachX = pMuzzleX + beamProgress * (targetHitX - pMuzzleX);
        const reachY = pMuzzleY + beamProgress * (targetHitY - pMuzzleY);
        const beamColor = p.colorHex || '#fde047';

        // Outer solar golden plasma beam
        ctx.strokeStyle = beamColor;
        if (!isLowQuality) {
          ctx.shadowColor = beamColor;
          ctx.shadowBlur = 22;
        }
        ctx.lineWidth = 5.0;
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(reachX, reachY);
        ctx.stroke();

        // Inner brilliant white core beam
        ctx.lineWidth = 1.8;
        ctx.strokeStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(reachX, reachY);
        ctx.stroke();

        // Solar flare burst at apex origin
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(pMuzzleX, pMuzzleY, 5, 0, Math.PI * 2);
        ctx.fill();

        // Target impact sparks
        if (beamProgress >= 0.85) {
          ctx.fillStyle = beamColor;
          if (!isLowQuality) {
            ctx.shadowColor = beamColor;
            ctx.shadowBlur = 14;
          }
          for (let sp = 0; sp < 4; sp++) {
            const ang = (sp * Math.PI) / 2 + now * 0.02;
            ctx.fillRect(reachX + Math.cos(ang) * 10, reachY + Math.sin(ang) * 10, 3, 3);
          }
        }
      } else if (p.isShade) {
        // 🎯 Shade: Hypersonic Rail Needle Beam
        const endX = pMuzzleX + progress * (targetHitX - pMuzzleX);
        const endY = pMuzzleY + progress * (targetHitY - pMuzzleY);
        ctx.strokeStyle = p.colorHex;
        if (!isLowQuality) {
          ctx.shadowColor = p.colorHex;
          ctx.shadowBlur = 20;
        }
        ctx.lineWidth = 4.5;
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        ctx.lineWidth = 1.2;
        ctx.strokeStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      } else if (p.isDefectiveLoaf) {
        // 🧬 Sul Undulating Bio-Tether
        const endX = pMuzzleX + progress * (targetHitX - pMuzzleX);
        const endY = targetHitY;
        ctx.strokeStyle = 'rgba(217, 70, 239, 0.5)';
        ctx.lineWidth = 6;
        ctx.beginPath();
        for (let x = pMuzzleX; x <= endX; x += 4) {
          const wave = Math.sin((x * 0.08) - (progress * 16)) * 8 * (1 - Math.abs((x - pMuzzleX) / (targetHitX - pMuzzleX) - 0.5));
          if (x === pMuzzleX) ctx.moveTo(x, pMuzzleY + wave);
          else ctx.lineTo(x, pMuzzleY + wave);
        }
        ctx.stroke();

        ctx.strokeStyle = '#f472b6';
        ctx.lineWidth = 2.5;
        ctx.stroke();
      } else if (p.isApostate) {
        // 🍄 Apostate Loaf: High-Arc Spore Mortar Lob (with tumbling spore cluster, motes & ground shadow)
        const arcHeight = 65;
        const prog = progress;
        const currentX = pMuzzleX + prog * (targetHitX - pMuzzleX);
        const elevation = 4 * arcHeight * prog * (1 - prog);
        const drawY = pMuzzleY + prog * (targetHitY - pMuzzleY) - elevation;

        // Ground shadow beneath spore
        const heightFactor = Math.min(1.0, elevation / arcHeight);
        const shadowAlpha = 0.35 * (1.0 - heightFactor * 0.65);
        const baseW = Math.max(3, 7 - heightFactor * 3.5);
        ctx.fillStyle = '#051b03';
        ctx.globalAlpha = shadowAlpha;
        ctx.beginPath();
        ctx.ellipse(currentX, pMuzzleY + prog * (targetHitY - pMuzzleY), baseW, baseW * 0.4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;

        // Orbiting bioluminescent fungal spore motes
        const sporeColor = p.colorHex || '#84cc16';
        for (let m = 0; m < 6; m++) {
          const mAngle = (now * 0.008 + m * 1.05) % (Math.PI * 2);
          const mDist = 7.0 + Math.sin(now * 0.015 + m * 2) * 3;
          const mx = currentX + Math.cos(mAngle) * mDist;
          const my = drawY + Math.sin(mAngle) * (mDist * 0.75);
          ctx.fillStyle = (m % 3 === 0) ? '#bef264' : ((m % 3 === 1) ? sporeColor : '#a855f7');
          ctx.globalAlpha = 0.7 + Math.sin(now * 0.02 + m) * 0.3;
          ctx.fillRect(mx - 1.5, my - 1.5, 3, 3);
        }
        ctx.globalAlpha = 1.0;

        // Multi-nodal organic spore cluster with rotation
        ctx.save();
        ctx.translate(currentX, drawY);
        ctx.rotate(now * 0.008);
        ctx.fillStyle = sporeColor;
        if (!isLowQuality) {
          ctx.shadowColor = sporeColor;
          ctx.shadowBlur = 12;
        }
        ctx.beginPath();
        ctx.arc(0, 0, 5, 0, Math.PI * 2);
        ctx.arc(3, -2, 3.5, 0, Math.PI * 2);
        ctx.arc(-2, 3, 3.2, 0, Math.PI * 2);
        ctx.arc(-3, -2, 2.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#bef264';
        ctx.fillRect(-1, -2, 2, 2);
        ctx.restore();
      } else if (p.isPulp) {
        // 🤡 Sul Pulp: High-Arc Bio-Mortar Lob (Parabolic lob like Apostate, with a completely round glowing sphere)
        const arcHeight = 65;
        const prog = progress;
        const currentX = pMuzzleX + prog * (targetHitX - pMuzzleX);
        const elevation = 4 * arcHeight * prog * (1 - prog);
        const drawY = pMuzzleY + prog * (targetHitY - pMuzzleY) - elevation;

        // Ground shadow beneath sphere
        const heightFactor = Math.min(1.0, elevation / arcHeight);
        const shadowAlpha = 0.35 * (1.0 - heightFactor * 0.65);
        const baseW = Math.max(3, 7 - heightFactor * 3.5);
        ctx.fillStyle = '#051b03';
        ctx.globalAlpha = shadowAlpha;
        ctx.beginPath();
        ctx.ellipse(currentX, pMuzzleY + prog * (targetHitY - pMuzzleY), baseW, baseW * 0.4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;

        // Orbiting bioluminescent motes
        const sphereColor = p.colorHex || '#d946ef';
        for (let m = 0; m < 6; m++) {
          const mAngle = (now * 0.008 + m * 1.05) % (Math.PI * 2);
          const mDist = 7.0 + Math.sin(now * 0.015 + m * 2) * 3;
          const mx = currentX + Math.cos(mAngle) * mDist;
          const my = drawY + Math.sin(mAngle) * (mDist * 0.75);
          ctx.fillStyle = (m % 3 === 0) ? '#f472b6' : ((m % 3 === 1) ? sphereColor : '#00f0ff');
          ctx.globalAlpha = 0.7 + Math.sin(now * 0.02 + m) * 0.3;
          ctx.fillRect(mx - 1.5, my - 1.5, 3, 3);
        }
        ctx.globalAlpha = 1.0;

        // Perfectly round sphere ("ganz runde kugel") with glowing core & specular gleam
        ctx.save();
        ctx.translate(currentX, drawY);
        ctx.fillStyle = sphereColor;
        if (!isLowQuality) {
          ctx.shadowColor = sphereColor;
          ctx.shadowBlur = 14;
        }
        ctx.beginPath();
        ctx.arc(0, 0, 5.5, 0, Math.PI * 2);
        ctx.fill();

        // Round specular gleam (concentric sphere highlight)
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        ctx.arc(-1.5, -1.5, 2.0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      } else if (p.isPiranha) {
        // 🚁 A9 Piranha: Dual Rapid-Fire Wing Pylon Tracers
        const pylonOffsets = [-10, 10];
        for (let i = 0; i < 2; i++) {
          const py = pMuzzleY + pylonOffsets[i];
          const currX = pMuzzleX + progress * (targetHitX - pMuzzleX);
          const currY = py + progress * (targetHitY - py);

          if (progress < 0.3) {
            ctx.fillStyle = p.colorHex;
            if (!isLowQuality) {
              ctx.shadowColor = p.colorHex;
              ctx.shadowBlur = 10;
            }
            ctx.beginPath();
            ctx.arc(pMuzzleX, py, 5 * (1 - progress / 0.3), 0, Math.PI * 2);
            ctx.fill();
          }

          ctx.strokeStyle = p.colorHex;
          if (!isLowQuality) {
            ctx.shadowColor = p.colorHex;
            ctx.shadowBlur = 12;
          }
          ctx.lineWidth = 4;
          ctx.globalAlpha = 0.45;
          ctx.beginPath();
          ctx.moveTo(pMuzzleX, py);
          ctx.lineTo(currX, currY);
          ctx.stroke();

          ctx.lineWidth = 1.6;
          ctx.globalAlpha = 1.0;
          ctx.strokeStyle = '#ffffff';
          ctx.beginPath();
          ctx.moveTo(pMuzzleX, py);
          ctx.lineTo(currX, currY);
          ctx.stroke();
        }
      } else if (p.isRocketship) {
        // 🚀 Rocketship: Dual Curved Micro-Missiles
        for (let m = 0; m < 2; m++) {
          const curveSign = (m === 0) ? -1 : 1;
          const initialY = pMuzzleY + curveSign * 8;
          const baseX = pMuzzleX + progress * (targetHitX - pMuzzleX);
          const baseY = initialY + progress * (targetHitY - initialY);
          const arcOffset = Math.sin(progress * Math.PI) * 26 * curveSign;
          const curX = baseX;
          const curY = baseY + arcOffset;

          const nextProg = Math.min(1.0, progress + 0.05);
          const nextBaseX = pMuzzleX + nextProg * (targetHitX - pMuzzleX);
          const nextBaseY = initialY + nextProg * (targetHitY - initialY);
          const nextArc = Math.sin(nextProg * Math.PI) * 26 * curveSign;
          const angle = Math.atan2((nextBaseY + nextArc) - curY, nextBaseX - curX);

          // Smoke trail puffs along curve
          for (let s = 1; s <= 4; s++) {
            const trailProg = Math.max(0, progress - s * 0.04);
            const tx = pMuzzleX + trailProg * (targetHitX - pMuzzleX);
            const ty = initialY + trailProg * (targetHitY - initialY) + Math.sin(trailProg * Math.PI) * 26 * curveSign;
            ctx.fillStyle = p.trailHex || '#64748b';
            ctx.globalAlpha = (1.0 - s * 0.22) * 0.55;
            ctx.beginPath();
            ctx.arc(tx, ty, 3 + s * 0.8, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1.0;

          // Micro-missile sprite with warhead & booster
          ctx.save();
          ctx.translate(curX, curY);
          ctx.rotate(angle);

          // Casing
          ctx.fillStyle = '#cbd5e1';
          ctx.fillRect(-5, -2, 8, 4);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(-5, -2, 8, 1.2);

          // Stabilizer fins
          ctx.fillStyle = '#334155';
          ctx.fillRect(-5, -3.5, 2.5, 7);

          // Warhead (+X)
          ctx.fillStyle = p.colorHex || '#ef4444';
          if (!isLowQuality) {
            ctx.shadowColor = p.colorHex || '#ef4444';
            ctx.shadowBlur = 8;
          }
          ctx.fillRect(3, -1.5, 3, 3);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(4.5, -0.75, 1.5, 1.5);

          // Thruster flame (-X)
          ctx.fillStyle = p.trailHex || '#f97316';
          if (!isLowQuality) {
            ctx.shadowColor = p.trailHex || '#f97316';
            ctx.shadowBlur = 10;
          }
          ctx.fillRect(-7.5, -1, 2.5, 2);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(-6.5, -0.5, 1.5, 1);

          ctx.restore();
        }
      } else if (p.isOrchestra) {
        // 🚀 Orchestra: Heavy Vertical Ballistic Artillery Rocket
        const arcHeight = 75;
        const baseX = pMuzzleX + progress * (targetHitX - pMuzzleX);
        const arcOffset = -Math.sin(progress * Math.PI) * arcHeight;
        const baseY = pMuzzleY + progress * (targetHitY - pMuzzleY) + arcOffset;

        const nextProg = Math.min(1.0, progress + 0.04);
        const nextX = pMuzzleX + nextProg * (targetHitX - pMuzzleX);
        const nextY = pMuzzleY + nextProg * (targetHitY - pMuzzleY) - Math.sin(nextProg * Math.PI) * arcHeight;
        const angle = Math.atan2(nextY - baseY, nextX - baseX);

        // Dense smoke contrail
        for (let s = 1; s <= 6; s++) {
          const trailProg = Math.max(0, progress - s * 0.035);
          const tx = pMuzzleX + trailProg * (targetHitX - pMuzzleX);
          const ty = pMuzzleY + trailProg * (targetHitY - pMuzzleY) - Math.sin(trailProg * Math.PI) * arcHeight;
          ctx.fillStyle = p.trailHex || '#f1f5f9';
          ctx.globalAlpha = (1.0 - s * 0.16) * 0.65;
          ctx.beginPath();
          ctx.arc(tx, ty, 3.5 + s * 1.0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;

        // Heavy Rocket Fuselage
        ctx.save();
        ctx.translate(baseX, baseY);
        ctx.rotate(angle);

        ctx.fillStyle = '#64748b';
        ctx.fillRect(-7, -2.5, 13, 5);
        ctx.fillStyle = '#f1f5f9';
        ctx.fillRect(-7, -2.5, 13, 1.5);
        ctx.fillStyle = '#334155';
        ctx.fillRect(-7, 1.2, 13, 1.3);

        // Fins
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(-7, -4.5, 3.5, 9);

        // Warhead
        ctx.fillStyle = p.colorHex || '#ef4444';
        if (!isLowQuality) {
          ctx.shadowColor = p.colorHex || '#ef4444';
          ctx.shadowBlur = 10;
        }
        ctx.fillRect(6, -2, 3.5, 4);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(9.5, -1, 1.5, 2);

        // Booster plume
        ctx.fillStyle = p.trailHex || '#f97316';
        if (!isLowQuality) {
          ctx.shadowColor = p.trailHex || '#f97316';
          ctx.shadowBlur = 14;
        }
        ctx.fillRect(-11, -1.5, 4, 3);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-9, -0.75, 2, 1.5);

        ctx.restore();
      } else if (p.isDelvingPillar) {
        // 🔮 Delving Pillar: Crystalline Bio-Plasma Missile
        const currX = pMuzzleX + progress * (targetHitX - pMuzzleX);
        const currY = pMuzzleY + progress * (targetHitY - pMuzzleY) + Math.sin(progress * Math.PI * 2) * 5;
        const pillarColor = p.colorHex || '#c084fc';

        // Energy trail motes
        for (let s = 1; s <= 4; s++) {
          const tProg = Math.max(0, progress - s * 0.04);
          const tx = pMuzzleX + tProg * (targetHitX - pMuzzleX);
          const ty = pMuzzleY + tProg * (targetHitY - pMuzzleY) + Math.sin(tProg * Math.PI * 2) * 5;
          ctx.fillStyle = pillarColor;
          ctx.globalAlpha = (1.0 - s * 0.2) * 0.55;
          ctx.beginPath();
          ctx.arc(tx, ty, 3 + (4 - s), 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;

        ctx.save();
        ctx.translate(currX, currY);
        ctx.fillStyle = pillarColor;
        if (!isLowQuality) {
          ctx.shadowColor = pillarColor;
          ctx.shadowBlur = 16;
        }
        ctx.beginPath();
        ctx.moveTo(8, 0);
        ctx.lineTo(0, -4);
        ctx.lineTo(-7, 0);
        ctx.lineTo(0, 4);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(0, 0, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      } else if (p.isPinkBeretRocket) {
        // 🚀 Pink Beret: 45° Seeking RPG-7 Rocket with Warhead, Decal & Smoke Plume
        const arcHeight = 35;
        const prog = progress;
        const curX = pMuzzleX + prog * (targetHitX - pMuzzleX);
        const arcOffset = -Math.sin(prog * Math.PI) * arcHeight;
        const curY = pMuzzleY + prog * (targetHitY - pMuzzleY) + arcOffset;

        const nextProg = Math.min(1.0, prog + 0.04);
        const nextX = pMuzzleX + nextProg * (targetHitX - pMuzzleX);
        const nextY = pMuzzleY + nextProg * (targetHitY - pMuzzleY) - Math.sin(nextProg * Math.PI) * arcHeight;
        const angle = Math.atan2(nextY - curY, nextX - curX);

        // Dense contrail puffs along rocket trajectory
        for (let s = 1; s <= 6; s++) {
          const trailProg = Math.max(0, prog - s * 0.035);
          const tx = pMuzzleX + trailProg * (targetHitX - pMuzzleX);
          const ty = pMuzzleY + trailProg * (targetHitY - pMuzzleY) - Math.sin(trailProg * Math.PI) * arcHeight;
          ctx.fillStyle = (s % 2 === 0) ? (p.trailHex || '#ff2a85') : '#ffffff';
          ctx.globalAlpha = (1.0 - s * 0.16) * 0.65;
          ctx.beginPath();
          ctx.arc(tx, ty, 3.2 + s * 0.9, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;

        // RPG-7 Rocket Fuselage & Warhead
        ctx.save();
        ctx.translate(curX, curY);
        ctx.rotate(angle);

        // Rocket Body
        ctx.fillStyle = '#334155';
        ctx.fillRect(-8, -2.5, 14, 5);

        // Nosecone
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(6, -2.5);
        ctx.lineTo(11, 0);
        ctx.lineTo(6, 2.5);
        ctx.closePath();
        ctx.fill();

        // Decal Stripe in equipped color
        ctx.fillStyle = p.trailHex || '#ff2a85';
        ctx.fillRect(-2, -2.5, 3, 5);

        // Booster Plume
        const flameLen = 4 + Math.sin(now * 0.05) * 3;
        ctx.fillStyle = '#fde047';
        if (!isLowQuality) {
          ctx.shadowColor = '#fde047';
          ctx.shadowBlur = 10;
        }
        ctx.beginPath();
        ctx.moveTo(-8, -2);
        ctx.lineTo(-8 - flameLen, 0);
        ctx.lineTo(-8, 2);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-9, -0.75, 2, 1.5);

        ctx.restore();
      } else if (p.isFlame) {
        // 🔥 Devil Patrol: Flame Cone
        const flameLength = progress * (targetHitX - pMuzzleX);
        const puffCount = Math.floor(flameLength / 18);
        for (let pf = 0; pf <= puffCount; pf++) {
          const px = pMuzzleX + pf * 18;
          const puffProg = pf / Math.max(1, puffCount);
          const radius = 6 + puffProg * 18;
          const offsetY = Math.sin(pf * 1.8 + progress * 8) * puffProg * 8;
          ctx.fillStyle = p.colorHex;
          ctx.globalAlpha = 0.35;
          ctx.beginPath();
          ctx.arc(px, pMuzzleY + offsetY, radius * 1.3, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1.0;
        }
      } else if (p.isGrenade) {
        // 💣 Panzer & Deployable Cannon: 120mm Ballistic Artillery Shell
        const sx = pMuzzleX + progress * (targetHitX - pMuzzleX);
        const arcY = pMuzzleY + progress * (targetHitY - pMuzzleY) - Math.sin(progress * Math.PI) * 26;

        const nextProg = Math.min(1.0, progress + 0.03);
        const nextX = pMuzzleX + nextProg * (targetHitX - pMuzzleX);
        const nextY = pMuzzleY + nextProg * (targetHitY - pMuzzleY) - Math.sin(nextProg * Math.PI) * 26;
        const angle = Math.atan2(nextY - arcY, nextX - sx);

        // Smoke motes
        for (let s = 1; s <= 3; s++) {
          const tProg = Math.max(0, progress - s * 0.04);
          const tx = pMuzzleX + tProg * (targetHitX - pMuzzleX);
          const ty = pMuzzleY + tProg * (targetHitY - pMuzzleY) - Math.sin(tProg * Math.PI) * 26;
          ctx.fillStyle = p.isHyperDevilPatrol ? '#f97316' : '#64748b';
          ctx.globalAlpha = 0.45 - s * 0.12;
          ctx.fillRect(tx - 2, ty - 2, 4, 4);
        }
        ctx.globalAlpha = 1.0;

        ctx.save();
        ctx.translate(sx, arcY);
        ctx.rotate(angle);
        ctx.fillStyle = p.colorHex || (p.isHyperDevilPatrol ? '#f97316' : '#fbbf24');
        if (!isLowQuality) {
          ctx.shadowColor = p.colorHex || '#f59e0b';
          ctx.shadowBlur = 8;
        }
        ctx.fillRect(-5, -2, 7, 4);
        ctx.beginPath();
        ctx.moveTo(2, -2);
        ctx.lineTo(6, 0);
        ctx.lineTo(2, 2);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      } else if (p.isEnforcerVulture) {
        // 🩸 Sul Enforcer Vulture: Bio-Rocket Ball (Vertical 90° Ascent + Target Homing Dive)
        const phase1Fraction = 0.35;
        const ejectionHeight = 70;
        let curX, curY, angle;

        if (progress < phase1Fraction) {
          // Phase 1: Pure 90-degree vertical rocket ascent straight UP into the sky
          const p1 = progress / phase1Fraction;
          const climbEase = 1.0 - Math.pow(1.0 - p1, 2.0);
          curX = pMuzzleX;
          curY = pMuzzleY - ejectionHeight * climbEase;
          angle = -Math.PI / 2;
        } else {
          // Phase 2: Apex target lock-on & accelerated homing dive
          const p2 = (progress - phase1Fraction) / (1.0 - phase1Fraction);
          const apexX = pMuzzleX;
          const apexY = pMuzzleY - ejectionHeight;
          const t = p2;
          const u = 1 - t;
          const ctrlX = apexX + (targetHitX - apexX) * 0.35;
          const ctrlY = apexY - 24;

          curX = u * u * apexX + 2 * u * t * ctrlX + t * t * targetHitX;
          curY = u * u * apexY + 2 * u * t * ctrlY + t * t * targetHitY;

          const nextT = Math.min(1.0, t + 0.04);
          const nextU = 1 - nextT;
          const nx = nextU * nextU * apexX + 2 * nextU * nextT * ctrlX + nextT * nextT * targetHitX;
          const ny = nextU * nextU * apexY + 2 * nextU * nextT * ctrlY + nextT * nextT * targetHitY;
          angle = Math.atan2(ny - curY, nx - curX);
        }

        // Bioluminescent smoke / spore contrail puffs along trajectory
        for (let s = 1; s <= 4; s++) {
          const trailProg = Math.max(0, progress - s * 0.035);
          let tx, ty;
          if (trailProg < phase1Fraction) {
            const p1 = trailProg / phase1Fraction;
            tx = pMuzzleX;
            ty = pMuzzleY - ejectionHeight * (1.0 - Math.pow(1.0 - p1, 2.0));
          } else {
            const p2 = (trailProg - phase1Fraction) / (1.0 - phase1Fraction);
            const t = p2, u = 1 - t;
            const ctrlX = pMuzzleX + (targetHitX - pMuzzleX) * 0.35;
            const ctrlY = (pMuzzleY - ejectionHeight) - 24;
            tx = u * u * pMuzzleX + 2 * u * t * ctrlX + t * t * targetHitX;
            ty = u * u * (pMuzzleY - ejectionHeight) + 2 * u * t * ctrlY + t * t * targetHitY;
          }
          ctx.fillStyle = p.trailHex || '#ff0033';
          ctx.globalAlpha = (1.0 - s * 0.22) * 0.55;
          ctx.beginPath();
          ctx.arc(tx, ty, Math.max(1, 3.2 - s * 0.5), 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;

        // 3D Spherical Bio-Ball Sprite with Thruster Plume
        ctx.save();
        ctx.translate(curX, curY);
        ctx.rotate(angle);

        // Bioluminescent corona halo
        ctx.fillStyle = 'rgba(255, 0, 50, 0.42)';
        ctx.beginPath();
        ctx.arc(0, 0, 8.0, 0, Math.PI * 2);
        ctx.fill();

        // 3D Spherical Bio-Ball (radial specular gradient)
        const ballColor = p.colorHex || '#ff0033';
        const radGrad = ctx.createRadialGradient(-1.2, -1.2, 0.5, 0, 0, 5.0);
        radGrad.addColorStop(0.0, '#ffffff');
        radGrad.addColorStop(0.25, '#ff4d6d');
        radGrad.addColorStop(0.70, ballColor);
        radGrad.addColorStop(1.0, '#4c0519');

        ctx.fillStyle = radGrad;
        ctx.beginPath();
        ctx.arc(0, 0, 5.0, 0, Math.PI * 2);
        ctx.fill();

        // Specular glint
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.beginPath();
        ctx.arc(-1.4, -1.6, 1.4, 0, Math.PI * 2);
        ctx.fill();

        // Dynamic bio-thruster nozzle & flame (-X)
        const flamePulse = 0.85 + 0.3 * Math.sin(now * 0.04);
        ctx.fillStyle = '#ff4d6d';
        ctx.fillRect(-7.5 * flamePulse, -1.6, 3.5 * flamePulse, 3.2);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-5.5 * flamePulse, -0.9, 2.2 * flamePulse, 1.8);

        ctx.restore();
      } else if (p.isProcessingPillar) {
        // 🔮 Sul Processing Pillar: Slow Homing Plasma Sphere ("Große Kugel")
        const currX = pMuzzleX + progress * (targetHitX - pMuzzleX);
        const currY = pMuzzleY + progress * (targetHitY - pMuzzleY) + Math.sin(progress * Math.PI) * -8;
        const sphereColor = p.colorHex || '#38bdf8';
        const baseR = 12.0;
        const pulse = 1.0 + Math.sin(now * 0.015) * 0.08;
        const sphereR = baseR * pulse;

        ctx.save();
        // Layer 1: Outer soft radiant corona
        ctx.fillStyle = '#c084fc';
        ctx.globalAlpha = 0.35;
        ctx.beginPath();
        ctx.arc(currX, currY, sphereR * 1.6, 0, Math.PI * 2);
        ctx.fill();

        // Layer 2: Swirling bio-plasma mantle
        ctx.fillStyle = sphereColor;
        ctx.globalAlpha = 0.88;
        ctx.beginPath();
        ctx.arc(currX, currY, sphereR, 0, Math.PI * 2);
        ctx.fill();

        // Layer 3: Inner radiant core
        ctx.fillStyle = '#bae6fd';
        ctx.globalAlpha = 0.92;
        ctx.beginPath();
        ctx.arc(currX, currY, sphereR * 0.58, 0, Math.PI * 2);
        ctx.fill();

        // Layer 4: Brilliant white center
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = 0.98;
        ctx.beginPath();
        ctx.arc(currX, currY, sphereR * 0.35, 0, Math.PI * 2);
        ctx.fill();

        // Layer 5: Specular bubble glint
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        ctx.arc(currX - sphereR * 0.28, currY - sphereR * 0.28, sphereR * 0.18, 0, Math.PI * 2);
        ctx.fill();

        // Layer 6: Soft iridescent bubble rim
        ctx.strokeStyle = '#e0f2fe';
        ctx.lineWidth = 1.3;
        ctx.globalAlpha = 0.75;
        ctx.beginPath();
        ctx.arc(currX, currY, sphereR, 0, Math.PI * 2);
        ctx.stroke();

        // Trailing plasma motes along path
        for (let s = 1; s <= 4; s++) {
          const trailProg = Math.max(0, progress - s * 0.04);
          const tx = pMuzzleX + trailProg * (targetHitX - pMuzzleX);
          const ty = pMuzzleY + trailProg * (targetHitY - pMuzzleY) + Math.sin(trailProg * Math.PI) * -8;
          ctx.fillStyle = (s % 2 === 0) ? '#c084fc' : sphereColor;
          ctx.globalAlpha = (1.0 - s * 0.22) * 0.6;
          ctx.beginPath();
          ctx.arc(tx, ty, Math.max(1.5, 4.5 - s * 0.8), 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      } else if (p.isSentinalLock) {
        // 🦇 Sul Sentinal Lock: Lobbed Toxic Bio-Glob Spit
        const arcHeight = 32;
        const prog = progress;
        const currentX = pMuzzleX + prog * (targetHitX - pMuzzleX);
        const elevation = 4 * arcHeight * prog * (1 - prog);
        const drawY = pMuzzleY + prog * (targetHitY - pMuzzleY) - elevation;
        const globColor = p.colorHex || '#eaff00';

        ctx.save();
        // Toxic droplet motes
        for (let m = 0; m < 4; m++) {
          const mProg = Math.max(0, prog - (m + 1) * 0.03);
          const mx = pMuzzleX + mProg * (targetHitX - pMuzzleX);
          const my = pMuzzleY + mProg * (targetHitY - pMuzzleY) - (4 * arcHeight * mProg * (1 - mProg));
          ctx.fillStyle = (m % 2 === 0) ? '#a3e635' : globColor;
          ctx.globalAlpha = (1.0 - m * 0.22) * 0.65;
          ctx.beginPath();
          ctx.arc(mx, my, Math.max(1, 3.2 - m * 0.6), 0, Math.PI * 2);
          ctx.fill();
        }

        // Rotating organic bio-glob cluster
        ctx.translate(currentX, drawY);
        ctx.rotate(now * 0.012);
        ctx.fillStyle = globColor;
        if (!isLowQuality) {
          ctx.shadowColor = globColor;
          ctx.shadowBlur = 12;
        }
        ctx.beginPath();
        ctx.arc(0, 0, 4.5, 0, Math.PI * 2);
        ctx.arc(2.5, -1.5, 3.0, 0, Math.PI * 2);
        ctx.arc(-2.0, 2.0, 2.8, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-0.8, -1.2, 1.6, 1.6);
        ctx.restore();
      } else if (p.isPreserverLock) {
        // ⚡ Sul Preserver Lock: High-Energy Dual Lenses Plasma Beam
        const beamProgress = Math.min(1.0, progress * 1.6);
        const reachX = pMuzzleX + beamProgress * (targetHitX - pMuzzleX);
        const reachY = pMuzzleY + beamProgress * (targetHitY - pMuzzleY);
        const beamColor = p.colorHex || '#7dd3fc';
        const pulse = 0.8 + 0.2 * Math.sin(now * 0.03);

        ctx.save();
        // Layer 1: Radiant outer plasma bloom
        ctx.strokeStyle = beamColor;
        ctx.lineWidth = 6.0 * pulse;
        ctx.globalAlpha = 0.35 * (1.0 - progress * 0.2);
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(reachX, reachY);
        ctx.stroke();

        // Layer 2: Main energetic laser core
        ctx.strokeStyle = beamColor;
        ctx.lineWidth = 3.0 * pulse;
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(reachX, reachY);
        ctx.stroke();

        // Layer 3: Brilliant white inner ray
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.2 * pulse;
        ctx.globalAlpha = 0.95;
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(reachX, reachY);
        ctx.stroke();

        // Target impact spark corona
        if (beamProgress > 0.6) {
          ctx.fillStyle = beamColor;
          ctx.beginPath();
          ctx.arc(reachX, reachY, 5 * pulse, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(reachX, reachY, 2.5 * pulse, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      } else if (p.isBastionLock) {
        // 🛡️ Bastion Lock: Focused Blue Beam Pulses Through Firing Slit
        const beamProgress = Math.min(1.0, progress * 1.8);
        const reachX = pMuzzleX + beamProgress * (targetHitX - pMuzzleX);
        const reachY = pMuzzleY + beamProgress * (targetHitY - pMuzzleY);
        const beamColor = p.colorHex || '#38bdf8';

        ctx.save();
        // Layer 1: Soft outer cyan bloom
        ctx.strokeStyle = beamColor;
        ctx.lineWidth = 4.8;
        ctx.globalAlpha = 0.45;
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(reachX, reachY);
        ctx.stroke();

        // Layer 2: Focused energetic core
        ctx.strokeStyle = beamColor;
        ctx.lineWidth = 2.4;
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(reachX, reachY);
        ctx.stroke();

        // Layer 3: Brilliant white inner ray
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.0;
        ctx.globalAlpha = 0.95;
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(reachX, reachY);
        ctx.stroke();

        // Target impact sparks
        if (beamProgress > 0.6) {
          ctx.fillStyle = beamColor;
          ctx.beginPath();
          ctx.arc(reachX, reachY, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(reachX, reachY, 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      } else {
        // 🎯 Marine & Hyper Marine: Ballistic Tracer Beam
        const currX = pMuzzleX + progress * (targetHitX - pMuzzleX);
        const currY = pMuzzleY + progress * (targetHitY - pMuzzleY);

        ctx.strokeStyle = p.colorHex;
        if (!isLowQuality) {
          ctx.shadowColor = p.colorHex;
          ctx.shadowBlur = 12;
        }
        ctx.lineWidth = 5;
        ctx.globalAlpha = 0.4;
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(currX, currY);
        ctx.stroke();

        ctx.lineWidth = 2.2;
        ctx.globalAlpha = 1.0;
        ctx.strokeStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(pMuzzleX, pMuzzleY);
        ctx.lineTo(currX, currY);
        ctx.stroke();
      }
      ctx.restore();

      if (progress >= 1.0) {
        this._droneHitTime = now;
        this._droneImpactColor = p.colorHex;
        gameProgress.recordTargetDroneHit();
        this._projectiles.splice(i, 1);
      }
    }
  }
}
